/**
 * Bike dynamics.
 *
 * Deliberately not a rigid-body-engine vehicle. The model is a leaning bicycle with
 * two suspended contact points, a combined-slip tire, and an explicit rider that
 * commands lean rather than being a passenger. Everything here was arrived at by
 * measurement; the comments record the failures that each choice fixes, because the
 * naive alternative looks reasonable and is wrong.
 *
 * The frame of reference for every moment in here is the CoM. That one decision fixes
 * all the signs: the contact patch sits comHeight below the CoM and, once the bike is
 * leaned, comHeight*sin(lean) to the inside of it. So the normal load acting through
 * that lateral offset is the term that makes a leaned bike fall over, and the tire's
 * lateral force acting through the vertical offset is the only thing that stands it
 * back up. Both are per-wheel and both scale with that wheel's own load — writing the
 * gravity term as m*g in each wheel's solve double-counts it and hands you a lean with
 * no equilibrium, which is exactly what it did.
 *
 * The other structural decision worth stating up front is that a hit the suspension
 * cannot absorb is a **state**, not a force problem. Three rounds were spent shaping the
 * spring curve so bottoming out would be "felt and rare", and the third one measured that
 * it cannot be both: across twenty-four sweep points every change that bought time off
 * the bump stop paid for it in peak g and vice versa. The curve is on its Pareto front.
 * What made that trade one-dimensional was that bottoming had no consequence — the
 * harness caught the bike riding off a 23 m cliff, burying the rear axle 723 mm past full
 * travel, and riding away at 51 km/h. `updateImpact` is the second axis: severity is
 * measured as the stroke demanded past what the frame has, a case costs speed, balance
 * and the rider's authority for a beat, and full severity — the chassis arriving where
 * the hillside is — is a crash the angle-only test could never see.
 *
 * And the reason that Pareto front existed at all — the finding this file was sent back
 * for a third time to get — is that **the rider was not a load path**. Peak g on a landing
 * is bounded below by v^2/(2*d*g), d is the distance the mass is stopped over, and with
 * all 92 kg rigid on the frame the only d in the model was the frame's own 165 mm. That
 * bound is 10 g at 5.7 m/s and no spring shape moves it, so every sweep point could only
 * trade *where* in the stroke the force sat. A real bike is not that: about two thirds of
 * the mass is a rider standing on the pedals with a quarter-metre of their own heavily
 * damped travel, and what the springs carry is what that element *transmits*, not the
 * rider's inertia. The vertical dynamics are now two masses (see `legForce` and the split
 * in `step`) and the horizontal is still one, because a rider is stiffly coupled fore/aft
 * and laterally and only softly coupled up and down.
 *
 * Round 4's finding is smaller to write down and was the largest single force in the file:
 * **the damper was being driven by a velocity the wheel never had.** `travel` is measured
 * against the wheel ENVELOPE — the soft-max of five probes dropped by the wheel's own
 * curvature, which is the whole point of having an envelope — while `travelVel` was read
 * off the contact NORMAL, i.e. the analytic gradient of the RAW heightfield. Position and
 * velocity came from two different surfaces, and they differ by construction exactly where
 * the envelope earns its keep. The gradient goes as 1/ny, so a steep facet under one probe
 * multiplies it, and it is proportional to forward speed, so it grew with the thing it was
 * meant to damp. Measured across the scenario set at 240 Hz, `travelVel` sat pinned at the
 * +-8 m/s guard for 20-26 consecutive steps while the wheel's real stroke rate over the
 * same steps ran between -4 and +2 m/s — the compression circuit was being asked for
 * 2.37 kN on steps where the wheel was *extending*. p90 of the excess was 5.4 m/s.
 * That phantom kilonewton pushes the wheel deeper, which is self-reinforcing, and it is
 * most of why five different scenarios read 8.5-10 g with the front wheel 100-330 mm past
 * full travel while rolling at 15-20 km/h with no air at all. `vGround` is now the measured
 * rate of the envelope, which makes `travelVel` the exact derivative of the `travel` the
 * spring reads. The flat drop bench is bit-identical at every touchdown speed from 2 to
 * 20 m/s, because on flat ground the two surfaces agree — which is the check that says
 * this is a consistency fix and not a retune.
 *
 * Round 5's finding is that two of the file's own guards had drifted out of agreement with
 * the model they guard, and that the air is not a place where nothing happens.
 *
 * **The chassis floor was below where the wheel model is defined.** `travel` is kinematic —
 * `comHeight + sagRef - drop` — so the deepest position the solver can describe is the one
 * where the whole stack, frame stroke plus bump stop, is used up: about 0.53 m under this
 * geometry. The hard floor at the bottom of `step` sat at 0.3*comHeight = 0.32 m, 190 mm
 * below that, and down there the projection of the chassis-to-ground gap onto the pitched
 * body axis comes out NEGATIVE. Both wheels then report no contact, every suspension and
 * tire force is zero, `airborne` reads true, and the only thing holding the bike up is the
 * clamp. Measured on `holdline` at 240 Hz: 0.28 s of exactly that at 3.1 km/h on a 62%
 * face, `vy` pinned at +0.01 m/s with the bike buried in the dirt, ending in **1376 mm past
 * full travel and 30.9 g** in one step the moment the attitude swung far enough for contact
 * to resume — and the bike lying at 86 degrees against a 43 degree command. The heightfield
 * is stationary through all of it (80 fixed world points re-sampled every step of the run,
 * zero drift), so this was the model losing contact with its own geometry. Raising the floor
 * to `bike.chassisFloorFraction` takes that scenario to 366 mm, 12.2 g, 42 degrees of lean
 * and no crash.
 *
 * **The rider's save was capped at one g while the thing toppling them was not.** The torque
 * that lays a leaned bike down is the normal force through `comHeight*sin(lean)` and it
 * scales with the whole load; on a landing that is many times static, while `loadFrac` is
 * `clamp01(load/(m*g))` and stops at 1. See `rider.saveLoadCap`. The save is also no longer
 * charged the case-shock authority loss (`impact.caseSaveAuthorityLoss`), because a case
 * costs finesse and not the reflex that stops you going down.
 *
 * **Air control levelled the bike to the world horizon.** Every other attitude term in this
 * file is measured against `groundPitch` — the file argues at length that on a 30 degree
 * chute an ordinary attitude *is* 30 degrees nose down — and `applyAirControl` was the one
 * place that was not. On a descent a neutral stick was therefore a request to land nose-up
 * by the whole gradient, hit rear-first and pitch straight back off the ground. `groundPitch`
 * is now measured through the flight too, from a ballistic preview of the landing point, and
 * `control.airGroundTrack` is how much of it a neutral stick matches. `corner` is airborne
 * 8.8% of its run without it and 1.8% with it.
 *
 * A note on the instrument, because it cost this round real time: the mountain is edited by
 * other agents while this file is being measured, so numbers taken hours apart are not an
 * A/B. Every figure above is from a single session with the change behind a tuning constant
 * whose OLD value reproduces the previous behaviour, measured in the same browser. The sim
 * is bit-deterministic run to run (verified: two `fullrun` passes agree to every digit), so
 * within a session the differences are real — but `fullrun` completion is chaotic and moved
 * 2.4-3.0% across all five arms, which is not signal.
 *
 * Round 6 has one finding and it is a re-diagnosis: **the bike submerging into the terrain
 * was never the suspension and was never the clamp — it is the pitch attitude.**
 *
 * The gap this round inherited was "the bike still submerges into the terrain on the GATE
 * scenario, and raising `chassisFloorFraction` 0.3 -> 0.55 made the burial shallower, not
 * absent." A 240 Hz census of every one of `fullrun`'s 90900 sim steps says where it lives.
 * The deepest excursion of the entire run — 404 mm past full travel, which is exactly how
 * far the DRAWN wheel is inside the hill, because `travel` is clamped at travelMax and the
 * renderer poses the wheel from the clamped value — happens at **11.9 km/h**. Not a wall
 * strike, not a landing: a walking-pace fall-over, with the chassis at **-52 degrees of
 * pitch**. At that attitude rigid geometry alone puts the front axle mount
 * `frontOffset*sin(pitch)` = 546 mm below the CoM's own plane and the kinematic `drop`
 * divides the remainder by cos(pitch), so the solver asks the front wheel for 574 mm of a
 * 170 mm stroke. There is no spring curve that answers that; the only fix is not to arrive
 * at the attitude.
 *
 * And the reason it arrived there is the *other half of round 5's finding, never applied*.
 * Round 5 fixed the roll save being capped at one g while the thing toppling the bike was
 * not (`rider.saveLoadCap`). Pitch is the identical equation on the other arm: past
 * atan(frontOffset/comHeight) the front contact patch is BEHIND the CoM, so the front
 * wheel's own normal force pitches the bike further over, and that moment is `rLong * load`
 * — the whole load. Measured at that instant: 2.8 kN*m of nose-down moment against a save
 * worth 750 N*m. Giving the pitch save the same `saveLoadCap` treatment takes the worst
 * attitude of the run from -53 to -43 degrees.
 *
 * Two structural things went with it.
 *
 * **The chassis floor is a contact now, not a clamp.** It teleported the CoM and deleted
 * the velocity that took it there, both outside the force sum, so `gForce` could not see
 * it, `updateImpact` could not score it, and `airborne` read true while the guard alone
 * held the bike up. It is an ordinary spring-damper along the ground normal, added to the
 * same accumulator the wheels use, dissipative in one direction only, and `airborne` is now
 * false by construction whenever it touches. It was swept, and softness is not kindness: at
 * 45 kN/m the bike settled into it and dwelt there for 2.15% of steps against the clamp's
 * 0.22%, and every millimetre it sank was a millimetre more `overTravel`. A frame is stiff.
 *
 * **`rider.leanCommandRate` was a quarter-second filter in front of the countersteer loop.**
 * Tracing the first ejection of the gate scenario — the one that sets `completionPct`,
 * since that is distance before the first terminal failure and nothing else — the autopilot
 * asks for 85% of full lean at t=4.7 s and `leanCommand` does not arrive until t=6.0 s. The
 * bike's own lean tracks the command to within a degree the whole way (p50 error 1.0 deg
 * over the run), so none of that is the chassis. In those 1.3 s the bike goes from 0.03 m
 * to 3.99 m off the centreline, and 2.5 m is an ejection. Raising it cannot make the bike
 * twitchy — `updateSteering` closes on lean error through a rate loop capped at
 * `maxRollRate`, so a faster setpoint only saturates that cap sooner. Measured on `corner`
 * over 1517 steady-cornering samples, the bike delivers 96.4% of the path curvature its
 * lean asks for against 81.7% before.
 *
 * A note on that last number, because the first version of the probe that produced it was
 * wrong and would have routed a fix at a non-defect. Graded against the textbook bicycle
 * relation g*tan(lean)/v the model reads 25% understeer — but this model deliberately does
 * not lean the combined CoM by the bike's own lean angle (`bike.leanDropFraction`: a rider
 * counter-leans). Its own roll balance is `sin(lean)*Fy = cosL*FLat`, so its equilibrium
 * CoM lean is atan(sin(lean)/cosL), which is *less* than `lean`, and the textbook formula
 * charges it cos(lean)/cosL of understeer that is there by construction — 6% at 29 degrees,
 * 23% at 52. Grade a model against its own equations before calling the difference a bug.
 *
 * Round 7 (wave 5) is the round that finally measured the thing every previous round was
 * arguing about, and the answer was in neither the spring nor the clamp nor the attitude:
 * **the sim and the renderer disagreed about where the bike was, and no instrument in the
 * project could see the difference.**
 *
 * Round 6 left the gap as "a drawn wheel is >=200 mm below the surface for 5.98% of
 * `fullrun` frames, max 888 mm, at 26-30 km/h and 0.74-2.06 g — ordinary riding", with the
 * note that `overTravel` reads zero through most of it. That note was the whole clue and
 * three rounds read past it. `overTravel` is the stroke demanded past `travelMax` measured
 * against **the piece of ground the solver sampled**. If the solver is sampling the wrong
 * piece of ground, it is not a weak metric, it is a blind one: it is derived from the same
 * error it is being asked to detect.
 *
 * Where the two files disagree. `bikeModel.ts` poses the bike about the **contact line** —
 * root at `(s.x, s.y - comHeight, s.z)`, chassis rotated about that root. `solveWheel`
 * resolves the patch about the **CoM** — `offset` ahead and `comHeight*cosL` below, and
 * rotating that arm swings the patch by `comHeight*sin(pitch)` fore/aft and
 * `comHeight*sin(lean)` outboard. Both are internally consistent. They are not each other.
 * Measured at 240 Hz over the whole gate scenario, the point the wheel solve sampled and
 * the point the renderer drew the wheel at were up to **1.40 m apart**, and on `corner`
 * 1.25 m — three quarters of a metre of hillside on a mountain made of 40-70% faces. The
 * suspension was being solved against ground the player could not see, and the wheel was
 * drawn into the ground they could.
 *
 * Three separate consequences fell out of that one mismatch, and they add:
 *   - the height reference. `comHeight` sat OUTSIDE the projection onto the pitched/leaned
 *     body axis in `drop` and INSIDE it in the renderer's pose. That alone is
 *     `comHeight*(1 - cosL*cosP)` of ride height: 100 mm at 25 degrees of pitch, 175 mm at
 *     25 and 30 of lean, 314 mm at 41. Under the old form a bike whose CoM was exactly
 *     `comHeight` above the ground read its fork as TOPPED OUT the moment it leaned — so
 *     the spring stopped holding it up and it sank until the belly contact caught it.
 *   - `leanDropFraction`'s half-drop was in the kinematic travel as well as in the moment
 *     arms. It belongs only in the arms: the renderer leans the bike rigidly about the
 *     contact line, where a leaned wheel's contact point does not move down at all.
 *   - two smaller mirrors nobody had written down: the renderer's chassis origin is the
 *     bottom bracket, 109 mm behind the CoM (46 mm of height at 25 degrees of pitch), and
 *     a compressed fork carries the front axle 61 mm BACK along its own axis (46 mm of
 *     height at 49 degrees). Both were found by differencing this file's model of the pose
 *     against the posed `frontAxle` bone, which is the only way they could have been.
 *
 * The fix is `bike.drawnPoseBlend`, and it is a consistency fix, not a retune: the wheel
 * samples the ground under the wheel **as drawn**, and `travel` is the stroke that puts
 * the drawn wheel exactly on it. The moment arms are untouched — `rLong`/`rUp`/`rLat` stay
 * the rigid-body arms about the CoM, because that is a statement about the dynamics and it
 * is correct — so this changes which ground the bike stands on and nothing about how it
 * leans, brakes or turns.
 *
 * A/B at blend 0 vs 1 inside ONE browser session (which is the only honest way to measure
 * anything here — nine agents edit the mountain while this file is being timed, and the
 * trail network was republished twice during this round alone):
 *
 *      fullrun    worst drawn-wheel sink  336 mm  ->   30 mm
 *                 frames >50 mm buried    2.94 %  ->  0.00 %
 *                 frames >100 mm buried   1.73 %  ->  0.00 %
 *                 sim-vs-drawn pose gap   0.99 m  ->  0 by construction
 *                 peak g                   9.18   ->   6.93
 *                 airborne                 0.62 % ->   0.20 %
 *                 COMPLETION              16.7 %  ->  99.8 %  (892.6 m of 894.2)
 *                 ejections/km             3.5    ->   1.1
 *      corner     worst sink              384 mm  ->   19 mm
 *                 frames >100 mm buried   16.7 %  ->  0.00 %
 *                 mean sink over the run   45.5 mm ->  0.0 mm
 *                 peak g                   9.9    ->   5.9
 *
 * A note on that completion figure, because an earlier A/B this same round said the
 * opposite and it is the more instructive half. On the network as it stood at midday —
 * `trails.crestCurvature` 12.6x over its own budget, `ownershipStep` 1.805 m — the same
 * change took completion 7.7% -> 1.4%, and the reason is worth keeping: the burial was
 * **load-bearing**. A bike ploughing through the hillside is a bike whose crests are
 * filtered and whose speed is bled (mean 12.7 km/h buried against 16.0 km/h on the
 * surface), so un-burying it delivered the trail's own defects to the rider intact. Both
 * arms were first thrown off the line at the same station (14.3 m vs 14.7 m), which was
 * the tell that the trail ejected the rider either way. When trails landed its fix the
 * sign flipped and the same change is now worth 83 points of completion. Two lessons, and
 * the second one is the expensive one: do not read one A/B on a moving world as a verdict,
 * and do not "fix" a number by putting the bike back inside the hill.
 *
 * The instrument that says all of this is `ridefeel.wheelSinkDepth` on the self-audit bus,
 * and it is deliberately not derived from anything the solver believes: it re-implements
 * the renderer's pose equations and asks `terrain.height`. It is cross-checked every round
 * by `captures/ride-feel-tools/sink.mjs`, which reads the posed `frontAxle`/`rearAxle` bone
 * matrices out of the scene graph and knows nothing about this file. Agreement on the gate
 * scenario is 27 mm at blend 0 (837 vs 811) and the two arms bracket each other; on
 * `corner` it is 1 mm at blend 0 and 3 mm at blend 1. It was checked in the direction that
 * matters most: at blend 0 it reads the full historical defect, so it can fail.
 *
 * What that is worth, measured, against the same benches the previous rounds used: a 1.27 m
 * drop to flat goes 9.6 g and both wheels on the bump stop to 7.1 g and 155 mm of 170,
 * never reaching it; a 1.83 m drop kisses the stop for 33 ms and rides away; the mountain
 * plunge goes from 0.92%/1.46% of grounded frames at full travel to 0.00%/0.21% while
 * covering 32% more ground in the same 40 s, and drop-bench restitution halves from 0.31
 * to 0.17. Two smaller structural absences went with it: the tire had no compliance at all
 * (see `solveWheel`), and the pump was a force injected at the contact patches with nothing
 * pushing back, which the split immediately exposed and which is now the rider's own legs
 * moving their own datum.
 *
 * Round 8 changed no dynamics. It re-measured round 7 on a mountain that was being rebuilt
 * underneath it, and the only thing it added to this file is a coverage number for its own
 * instrument. Both halves are worth writing down.
 *
 * **The fix holds, on ground round 7 never saw.** `terrain.ts` was edited twice and the
 * trail network republished four times inside the ninety minutes this round took; the
 * shared spawn moved 600 m and 120 m down. Re-run in that world, blend 0 against blend 1
 * inside one session: worst drawn-wheel sink 342 mm -> 7 mm on the probe and 451 mm -> 43 mm
 * on the engine invariant, frames over the 50 mm budget 3.41% -> 0.00%. On the delivered
 * build the gate scenario reads 46.1 mm worst, **0 of 73348 samples over budget**, and
 * completes 99.6% of 1764.6 m clean — zero ejections, zero stalls, zero respawns, 12/12
 * features clean at 41.5 km/h average. That is the carried gap closed and it is closed on
 * a different mountain from the one it was closed on.
 *
 * **Completion is not a property of this file alone, and this round has the numbers to
 * prove it.** With `physics.ts` and `tuning.ts` byte-identical throughout, the same
 * `fullrun` scenario measured 19.0% (a 1247.8 m network), 99.5% (1764.6 m), 8.3% (1502.8 m,
 * zero-latency rider) and 99.6% (1764.6 m) over about forty minutes. The intermediate
 * networks ejected the rider off their own berms — the zero-latency, 10 m/s^2 rider did
 * *worse* than the handicapped one on the network it shared, four of its six ejections
 * being "thrown into the air and came down off the line" on a berm or a tabletop. A number
 * from this file quoted against a trail network that no longer exists is not evidence, and
 * that is now the third time this project has been fooled by exactly that.
 *
 * The same thing poisons the open-loop scenarios and it is worth naming because three of
 * them are quoted as ride-feel results. `braking`, `air` and `coast` share one spawn, and
 * on the terrain as of this round that spawn sits about eight metres above a two-and-a-half
 * metre step. All three record their worst `ridefeel.peakG` at the identical station
 * (-547.3, 686.5, 6.8) and the identical value to four decimals, before their own scripts
 * have done anything — `coast` never gets past it and stops dead at 11.5 m with the front
 * wheel 357 mm past full travel. Wave 4's "coast: 0.20 s of air and 4.6 g" is not comparable
 * to today's "0.70 s and 11.3 g" because the two runs are not on the same hillside.
 *
 * **What the round added: `ridefeel.sinkUnmeasured`.** `wheelSinkDepth` is `fatal` and reads
 * zero over budget, and it says that while deliberately not looking at part of the run —
 * `measureSink` stands down for `SINK_BLIND_S` around a crash, because the renderer lifts
 * the drawn bike out of the hillside there. That was always the design and the other half
 * was always meant to be `rider.crashBikeSinkM`, which is declared and *never observed*. So
 * on `pump` this round the front wheel reached **1877 mm past full travel** and nothing in
 * the project measured how deep the drawn bike was, because the crash flag had already
 * latched. The new invariant publishes the share of a run that went unmeasured, so nobody
 * can read a clean `wheelSinkDepth` as a clean run. It is 0 of 36674 on the gate scenario
 * (no crashes) and non-zero on every scenario that crashes, and the fix it routes belongs
 * to `rider.ts`, not here.
 *
 * One negative result, recorded because it would otherwise be repeated. `launch.mjs` was
 * written to split "the trail threw the bike" from "the bike threw itself", by comparing the
 * CoM's vertical velocity at take-off with the surface's own rate of climb. Its flat bench
 * behaves (zero launches on flat ground) but its drop bench does not: `airborne` latches on
 * the step both wheels top out, which on a drop is while the chassis is still falling, so
 * the discriminator reads -0.88 m/s on a case that is by construction all bike and no
 * terrain. The split it prints is therefore not evidence and is not quoted anywhere. Its
 * peak-g census is sound and is: the worst events of a gate run are grounded, at 28-30 km/h,
 * leaned 30 degrees, with the ground *falling away* ahead and the front wheel asked for
 * 363 mm past full travel — which is a lateral step under a leaned wheel, i.e.
 * `trails.shoulderStep`, at 6-15x its own budget on the same run.
 *
 * Round 9 is the round the file stopped being graded on burial and started being graded on
 * riding, and the carried gap it was handed was "the bike cannot leave the ground": the
 * gate run's end card read BIGGEST AIR 0.0 over a 1.76 km flow line, `hucks` — a scripted
 * full-charge hop every 2.8 s at 45-64 km/h — yielded maxAirTime 0.04 s, and `pump` bottomed
 * both ends at 11.29 g. Two changes, both of them the same mistake in two places: **the
 * model let the rider's own body cancel the rider's own movement.**
 *
 * **The pump coordinate ran at one rate in both directions.** Pushing into the bike is a
 * muscle contraction lifting most of a rider's weight and takes about 150 ms; *un*weighting
 * is releasing one and is several times quicker. With a single rate, a full-charge release
 * put its impulse on `s.vy` in one step while `pumpPos` was still at +0.96 of the preload —
 * so `rider.pumpLegDatum` held the leg datum 135 mm below the leg coordinate and the legs
 * were still transmitting 1185 N into a 30 kg chassis. The frame was pressed back into the
 * hill at 4.8 g for the whole flight. 3.6 m/s of impulse, worth 0.66 m and 0.73 s in vacuum,
 * delivered 120 mm and 0.14 s. See `control.pumpRetractStiffness`.
 *
 * **The leg stop deleted momentum instead of resolving it.** `bodySquashVel` is the
 * *relative* velocity of two masses, so zeroing it at a stop does not stop them — it
 * teleports 62 of the system's 92 kg onto the chassis's velocity with no force anywhere.
 * At the EXTENSION stop the rider is always the one moving up faster, which is what put the
 * legs there, so what was deleted was exactly the upward momentum of the larger mass at the
 * instant its legs went taut. On a bike that is *how the bike comes off the ground*. It is
 * an inelastic internal collision now (`legStopHit`), momentum-conserving by construction.
 *
 * Round 10 measured what those two bought, and then measured the thing every round before it
 * had been arguing about without an instrument: **whether the mountain has anything to jump
 * off.** The answer changes who owns the gap.
 *
 * What round 9 bought, on the deterministic bench (`captures/ride-feel-tools/bench2.mjs`,
 * the real `BikePhysics` against an analytic surface that nine other agents cannot edit):
 * a full-charge hop from a coast is **650 mm of rise and 0.704 s aloft** where the same
 * release used to be 120 mm and 0.133 s; a 0.15 s stroke is 22 mm and no air at all; and
 * working rollers in phase is **+51.0 m / +12.5 m** over 26 s while working the same
 * rollers out of phase is **-43.2 m / -32.8 m**. That last pair is the bar's "reward
 * timing" as a number: same button, same duration, opposite sign, decided only by where on
 * the terrain the player pressed it. On the mountain, measured inside one session at 14:05:
 * `hucks` maxAirTime **0.04 s -> 0.75 s**, `pump` maxG **11.29 -> 6.87**, and `pump` 234.6 m
 * against `coast`'s 139.6 m on the identical script with the button never pressed.
 *
 * The mountain numbers in that last sentence have an expiry date and it was ninety minutes.
 * Re-measured at 16:10 the same day with `physics.ts` byte-identical and the deterministic
 * bench bit-identical to the digit, `coast` — the control run, the one that read 1.73 g and
 * zero air at 14:05 — reads **14.41 g, 1.60 s of air and crashes for 16.6% of its steps**,
 * and `descend`, `braking` and `air` read 17.6-39.6 g at the same station. The shared
 * open-loop spawn now sits on a benched trail section and the fall line those scenarios
 * plunge goes over the outboard fill. `spot.mjs` down that fall line: 30 m of sustained
 * **-58% to -67%**, then **-46% / -124% / -152% in three 25 cm steps** — a 379 mm vertical
 * step in a quarter of a metre — then a compression and a **+77%** up-ramp within 3 m of it.
 * Forty g is this model's honest answer to a 0.38 m step at 40 km/h and there is no spring
 * curve that makes it anything else. That is `terrain`, off-tread, and the only reason it is
 * written down here is that it will be read as a ride-feel regression by anyone who compares
 * two SUMMARY.md files taken two hours apart. **The bench is the arm of record for this
 * file; the mountain is a spot check with a timestamp on it.**
 *
 * **Now the re-routing, and it is the useful half of this round.** `BIGGEST AIR 0.0` has two
 * possible causes with different owners — the bike will not leave a lip, or the trail has no
 * lips — and no instrument in the project could tell them apart. Both are measurable and
 * neither needs a judgement call:
 *
 *   - THE MODEL. `bench2.mjs`'s launch bench builds a jump whose answer is arithmetic: a
 *     constant-grade run-in, a circular transition rotating the surface up to a known angle,
 *     and past the lip a flat deck at the lip's own height. A body leaving at v and theta is
 *     ballistic at v*sin(theta) and returns to the deck at 2*v*sin(theta)/g. Graded against
 *     that closed form at the speed the bike actually arrives with, the model delivers
 *     **0.75/0.90 of the ideal launch off a 20 degree lip at 40/55 km/h and 0.94/1.01 off a
 *     28 degree one**, and 1.71-1.80x with a bunny hop timed to the lip — 1.09-1.65 s of air
 *     and 1.54-3.46 m of apex, inside the bar's 1.2-2.5 s band. The zero-degree arm reads
 *     0.000 s of air, so it can fail. What it does NOT do is pop off a shallow lip: at 8
 *     degrees it is 0.00-0.28 of the ideal and lets go 1.9 m late, because a 170 mm fork
 *     with 120 mm of stroke in it absorbs the whole of a 2 m/s launch before it tops out.
 *     That is what a long-travel bike does on a roller, and it is why the hop exists.
 *   - THE MOUNTAIN. `captures/ride-feel-tools/features.mjs` walks the gate route's own
 *     centreline at 5 cm, differentiates it twice over the 0.35 m chord a 29in wheel bridges,
 *     and reports the convex curvature of every one of the 32 features on Sunday Best. It
 *     contains no bike at all. The result: **25 of 32 features have no upward ramp anywhere
 *     in them** — every berm and every roller train descends monotonically — and the steepest
 *     upward ramp on the whole 1764.6 m is **8.5 degrees**, on a feature labelled "Gap jump",
 *     rising 0.30 m. The four tabletops top out at 4.5 degrees and 0.08 m.
 *
 * Those two numbers meet: the launch bench says an 8-degree lip at 55 km/h is worth 0.14 s,
 * and the gate run measures maxAirTime 0.25 s. **The bike is delivering what the trail
 * offers.** BIGGEST AIR 0.0 is `trails`' feature amplitude, not this file's launch, and the
 * fix is a lip with a real transition on the jump-kind features — not a change here.
 *
 * Two invariants went in so that neither half can go quiet again, and both were shown to
 * fail before either was believed (`bench2.mjs`, section "CAN THE NEW INVARIANTS FAIL?"):
 *
 *   - `ridefeel.hopLaunchLoss` grades a fired hop against its own ballistic reference over
 *     100 ms. It reads 0.000 on the delivered build over 250 samples and **0.747, 68% of
 *     samples over budget, on the pre-round-9 constants** — i.e. it reads three quarters of
 *     the historical defect, which is what the trace above says was there. Every previous
 *     "can the bike jump" number was a consequence (`maxAirTime`, `BIGGEST AIR`) that
 *     another owner can drive to zero; this one is the mechanism.
 *   - `ridefeel.fullTravelShare` is "bottom-outs are felt and rare" written as a RATE. The
 *     form is the point: wave 4 was planned around `maxFrontTravel 170 / maxRearTravel 160`
 *     read as a state when the median is 41/43 mm, and `maxFrontTravelMm` is still printed
 *     in every SUMMARY.md. A max cannot say "rare". It reads 0.000 on a 1.0 m drop to flat
 *     and 0.044 on a 2.5 m drop that bottoms both ends.
 *
 * The gate, twice in one afternoon, on a network `trails` was rebuilding between the two:
 * 14:05 **completionPct 99.5, clean, zero respawns, zero stalls, 3 ejections, maxAirTime
 * 0.25 s**; 18:20 **89.5%, bottom reached after 2 respawns (so the gate FAILS), 4 ejections,
 * maxAirTime 0.55 s**. Every ride-feel invariant is inside its budget on both except
 * `ridefeel.peakG`, which is 11.7 g on 12 of 45823 steps (0.03%). The maxAirTime doubling is
 * the trail growing lips, and so is the failure: **all four ejections are "thrown into the
 * air and came down off the line"**, 0.26-0.58 s aloft at 47.6-56.3 km/h, three of them on a
 * named feature (rollers, tabletop, a commitment-0.80 double). `trails` has declared
 * `trails.lipOvershoot` and `trails.flightOffLine` for exactly this, which is the right
 * owner; nothing about it is a launch this file got wrong.
 *
 * A hypothesis checked and rejected on the way to that, recorded because it is the obvious
 * one and it is wrong. `applyAirControl` damps `s.yawRate` toward `input.airYaw*airYawRate`
 * at 7/s, so a neutral stick erases the bike's yaw rate in about 140 ms — which looks like a
 * straight violation of the bar's "takeoff preserves the momentum AND ROTATION you had".
 * It is not what puts the rider off the line here. At the worst of the four, 56.3 km/h on a
 * 63 m radius, preserved yaw is v/R = 0.248 rad/s and over 0.28 s of flight it is worth
 * 0.15 m of lateral position against a 2.5 m corridor; the other three are on 9999 m radius,
 * i.e. straight, where it is worth nothing at all. The bike arrives at those lips already
 * carrying lateral velocity (slip 0.56-1.00). Fix the yaw damping because the bar asks for
 * it, not because it would move the gate.
 *
 * One open defect this round found in its own model and did not fix, recorded so it is not
 * discovered again as a surprise. **The pump does not net exactly zero on flat ground, at
 * walking pace.** The claim below (`updatePump`, and `control.pumpRange`'s note in
 * tuning.ts) is that it nets zero by construction, everywhere. Measured on the analytic flat
 * bench — no gradient anywhere, so the ground reaction has no forward component at all —
 * 16 s of pumping covers 49.35 m against 47.19 m of coasting, +2.16 m, and +1.76 m on the
 * pre-round-9 constants, so it is older than this round.
 *
 * Where it comes from is the useful part, and it is not spread over the run. The same pair
 * cut at four durations, on a bench that decays from 30 km/h to walking pace:
 *
 *      first  4 s   coast 13.98 m @ 18.2 km/h   pump 13.98 m @ 18.2 km/h   +0.00 m
 *      first  8 s   coast 30.92 m @ 12.4 km/h   pump 31.08 m @ 12.9 km/h   +0.16 m
 *      first 12 s   coast 41.84 m @  7.3 km/h   pump 42.70 m @  8.1 km/h   +0.86 m
 *      first 16 s   coast 47.19 m @  2.5 km/h   pump 49.35 m @  3.9 km/h   +2.16 m
 *
 * **It is exactly zero above 18 km/h and all of it is in the walking-pace tail**, which is
 * where `solveTire` hands over from the slip model to the stiction bristle (`stBlend`). The
 * suspect is therefore the contact patch: `w.cx/cz` moves fore/aft with pitch and travel
 * every step, the bristle anchor does not, and a pump cycle that displaces the patch
 * backwards while it is loaded and forwards while it is not is an inchworm. It is not a
 * slope term and it is not `rollingResistance`, which is linear in load and therefore has
 * the same mean whatever the rider does. None of the numbers above depend on it — every
 * roller result is an A/B against a coast on the same surface, and the trail is never ridden
 * at 3 km/h — but it is the file claiming a property it does not have, which is the exact
 * failure this wave exists to stop. Next round: anchor the bristle to the patch's own motion
 * or weaken the claim to the speeds it holds at.
 *
 * Round 11 has one finding and it is a number this file set against itself:
 * **`stopTravel` is 400 mm and `SINK_BUDGET_MM` is 50, and until this round they were the
 * same constant.**
 *
 * The gap handed to this round was "the drawn bike is still inside the hill wherever the
 * model leaves the ground, and a fifth of every such run is measured by nobody" —
 * `wheelSinkDepth` 321.8 mm on `air` and 172.1 mm on `hucks`, `sinkUnmeasured` 1.000 on
 * 16-22% of the steps of air/hucks/pump/coast/pumpsync, and `rider.crashBikeSinkM` (the
 * invariant this file's own consequence text named as the owner of that window) sitting in
 * the report's "declared but never observed" list. Every one of those numbers was taken on
 * a mountain that has since been rebuilt; re-measured on the delivered build they are all
 * inside budget, which is exactly the trap the round-8 and round-10 blocks above warn
 * about and is not evidence that anything was fixed. So this round did not argue about the
 * mountain. It built the defect out of arithmetic: `captures/ride-feel-tools/bury.mjs`,
 * the real `BikePhysics` against an analytic ledge, a formula in one file that no other
 * agent can move.
 *
 * **The mechanism, measured there.** A square-edged UP step is what buries the drawn wheel;
 * a DOWN step of any size does not (a 1.0 m drop-off reads 16 mm). At 38 km/h a 0.35 m
 * ledge put the drawn front wheel 73 mm into the hillside and a 0.50 m ledge put it
 * **270 mm** in — and the bike rode away from both, at 9.6 g. That is `trails.shoulderStep`
 * and `trails.rideBandStep`, which run 6-15x over their own budgets on the gate run,
 * arriving at the rider as burial. The reason it arrives as burial rather than as a hit is
 * that `travel` is kinematic: every millimetre past full travel is a millimetre the chassis
 * sits below where the renderer draws the wheel, and the hillside term — the one force in
 * the file that is allowed to make `maxWheelForce` and actually stop the bike — did not
 * engage until 400 mm past full travel, because it was keyed to `stopTravel`.
 *
 * `stopTravel`'s own tuning note argues for 0.40 at length and every word of that argument
 * is about something else: the CASE SEVERITY ramp, and specifically that at 0.22 "a terrain
 * step scored a full-severity case and a crash. Eighteen of twenty-five riding scenarios
 * ended there." That is correct and it is unchanged here. What the note never priced is the
 * second job the same constant was doing, which is this piece's own fatal budget. Splitting
 * them is the whole fix: `suspension.hillsideStart` is where the axle is against the
 * mountain (0.05 m — `SINK_BUDGET_MM` written as a length), `stopTravel` still spans the
 * severity ramp, and `updateImpact` is untouched.
 *
 * On the bench, A/B in one process, `hillsideStart` 0.40 (the old behaviour, reproducible
 * exactly) against 0.05:
 *
 *      0.50 m ledge @ 38 km/h   sink 270 mm -> 46 mm,  9.6 g -> 15.2 g, 38->25 km/h
 *      0.35 m ledge @ 38 km/h   sink  73 mm -> 45 mm,  8.8 g -> 15.1 g
 *      3.5 m huck to flat       sink  56 mm -> 55 mm, 14.0 g -> 15.1 g
 *      everything else          IDENTICAL TO THE DIGIT
 *
 * That last row is the important one and it is why this is not a retune. Every bench in
 * bench2.mjs, every drop up to 2 m, every sustained face and every roller result is
 * bit-identical, because ordinary riding never leaves the frame's own stroke — the deepest
 * excursion on the plunge bench is 15 mm against a 50 mm engagement point. The entire cost
 * of the change lands on events that were already past the end of the suspension, and there
 * it is honest: a 0.5 m square-edged ledge at 38 km/h is hitting a wall, and 9.6 g was the
 * price of riding through the wall instead of into it.
 *
 * **The second half is the coverage, and it is the half a reader would have been fooled by.**
 * `measureSink` used to stand down for 1.2 s around a crash, because `bikeModel.ts` lifts
 * the drawn root out of the hillside there and this file's mirror of the pose did not
 * include the lift. The lift is four lines and every input to it is already here, so it is
 * mirrored now (see `CRASH_LIFT_IN` and `measureSink`'s second pass) and the stand-down is
 * gone. What that is worth, on the same deterministic bench, one run that crashes, the only
 * difference being `bike.sinkBlindS`:
 *
 *      sinkBlindS 1.2 (round 10)   30.0% of the run unmeasured   wheelSinkDepth reads  8 mm
 *      sinkBlindS 0   (round 11)    0.0% of the run unmeasured   wheelSinkDepth reads 49 mm
 *
 * A third of the run invisible and the fatal invariant reading six times too low. That is
 * the carried gap's "a fifth of every such run is measured by nobody", reproduced and
 * removed on ground nothing else can move — and `sinkBlindS` stays in `tuning.ts` precisely
 * so the coverage invariant can still fail, because one that can only read zero is a
 * decoration.
 *
 * **The instrument was validated by injection before any of this was believed.** The bike is
 * placed a KNOWN distance below the surface with its pose parked and `measureSink` is
 * called: 0 / 50 / 100 / 250 / 500 / 888 / 1400 mm of burial read back to within 2 mm, a
 * bike held 1 m clear reads -1000 mm, and a bike pitched 28.6 degrees nose-down with its CoM
 * 250 mm low reads 680 mm against a hand-computed 680. Inside the crash window the same
 * injections read 0 (the lift removes them) and a 2000 mm burial reads 400 — the lift's own
 * 1.6 m cap, arithmetic. Any of those failing would have invalidated every other number
 * above, which is the point of running them first.
 */

import { Terrain, GroundSample, makeGroundSample, SurfaceProps } from '../world/terrain';
import { Weather } from '../world/weather';
import { Tuning } from '../core/tuning';
import { clamp, clamp01, damp, lerp, sign, wrapPi, finiteOr } from '../core/math';
import { declareInvariant } from '../core/audit';
import type { ControlInput } from '../input/controls';

const G = 9.81;

/**
 * How far the DRAWN wheel may be under the DRAWN ground, mm.
 *
 * A tyre pressing a couple of centimetres into dirt is contact and is wanted. Half a
 * wheel is the defect. 50 mm is a tyre's own carcass deflection plus the width of the
 * contact patch, i.e. the deepest a wheel can be without the player seeing rim in the
 * soil.
 */
const SINK_BUDGET_MM = 50;

/**
 * tan(head angle) — how far BACK the front axle moves per metre of fork compression.
 *
 * `bikeModel.ts` slides the fork lower along its own axis (`position.y = (travel-sag)/cosH`
 * on a bone rotated by the head angle), so a compressed fork carries the front wheel back
 * as well as up. It is 61 mm at full travel, which is nothing on the flat and is 46 mm of
 * HEIGHT once the chassis is pitched 49 degrees — measured as the entire residual error of
 * this file's mirror of the renderer's pose, so it is in.
 */
const FORK_RAKE = Math.tan(Tuning.bike.headAngle);

/**
 * The self-audit invariants this file owns.
 *
 * `wheelSink` is the one this wave exists for, and the reason it is declared here rather
 * than derived from `overTravel` is the whole lesson: `overTravel` is measured in the
 * SIM's frame, against the piece of ground the SIM decided the wheel was standing on.
 * The defect is that those are not the ground the wheel is DRAWN on. A metric that shares
 * the model's own error cannot see the model's own error — five waves, five instruments
 * that did exactly that.
 *
 * So this one is measured from the renderer's pose equations (`src/render/bikeModel.ts`
 * `update()`, rules 2 and 4 of its header) against `terrain.height` at the drawn wheel's
 * own footprint, and it is cross-checked every round against the posed scene graph by
 * `captures/ride-feel-tools/sink.mjs`, which reads the `frontAxle`/`rearAxle` bone world
 * matrices and knows nothing about this file. If the two ever disagree, believe the bone.
 */
const SINK = declareInvariant({
  id: 'ridefeel.wheelSinkDepth',
  owner: 'ride-feel',
  what: 'lowest point of a drawn wheel below the terrain surface under it',
  unit: 'mm',
  budget: SINK_BUDGET_MM,
  compare: 'max',
  severity: 'fatal',
  phase: 'run',
  consequence: 'the bike rides inside the hill — screenshots show dirt to the rider\'s waist',
  source: 'src/bike/physics.ts SINK_BUDGET_MM',
});

/**
 * The mechanism behind the one above, published separately so a regression says *why*.
 *
 * The horizontal distance between where `solveWheel` samples the ground for a wheel and
 * where this file models that wheel as being drawn. It was up to 1.40 m on the gate
 * scenario and 1.25 m on `corner`, because the two files pivot the bike about different
 * points. Anything above a tyre's own footprint means the suspension is being solved
 * against hillside the player cannot see.
 *
 * Read it for what it is: both halves are computed here, so it catches `drawnPoseBlend`
 * being turned back down and it CANNOT catch `bikeModel.ts` changing its pivot underneath
 * this file. That check is not expressible from inside the sim and it is not pretended to
 * be — it lives in `captures/ride-feel-tools/sink.mjs`, which differences this file's
 * model of the pose against the posed bones and prints the error in mm.
 */
const POSE_GAP = declareInvariant({
  id: 'ridefeel.simDrawPoseGap',
  owner: 'ride-feel',
  what: 'distance between the ground the wheel solve samples and where the wheel is modelled as drawn',
  unit: 'm',
  budget: 0.10,
  compare: 'max',
  severity: 'error',
  phase: 'run',
  consequence: 'the suspension holds the bike at a height set by ground that is not under the wheel',
  source: 'src/core/tuning.ts bike.drawnPoseBlend',
});

/**
 * COVERAGE for the one above. How much of a run `wheelSinkDepth` was not looking at.
 *
 * Round 10 declared this because `SINK` is `fatal`, read 0% over budget on the gate
 * scenario, and was doing that while deliberately not looking at part of every run that
 * crashes: `measureSink` used to stand down while the bike was down and for a beat after,
 * on the argument that `bikeModel.ts` lifts the drawn root out of the hillside there and
 * the other half belonged to `rider.crashBikeSinkM`. Measured across the delivered set
 * that window was **16-22% of air/hucks/pump/coast/pumpsync** — one sim step in five on
 * which nobody in the project was measuring whether the bike was drawn underground — and
 * `rider.crashBikeSinkM` sat in the report's "declared but never observed" list on any
 * capture whose scenarios happened not to crash. A fatal invariant that quietly stops
 * watching for a fifth of a run is the same failure this wave exists to stop, with better
 * manners.
 *
 * Round 11 removed the window rather than annotating it. `measureSink` now mirrors the
 * renderer's crash lift the same way it already mirrors the renderer's pose, so it can
 * measure the delivered frame while the bike is down, and this reads 0 on every step of
 * every run. Two consequences worth stating because both are load-bearing:
 *
 *  - the two invariants now measure the SAME quantity over overlapping windows and are a
 *    cross-check on each other, instead of two halves of a partition with a seam nobody
 *    owned. `rider.crashBikeSinkM` measures it off the posed scene graph, this file
 *    measures it off the pose equations; if they disagree, believe the scene graph.
 *  - an invariant that can only ever read zero is not an instrument. This one can still
 *    fail: `bike.sinkBlindS` is the length of the stand-down, it is 0 now, and setting it
 *    back to 1.2 reproduces the historical blindness exactly — which is how the numbers
 *    above were re-measured rather than quoted (`captures/ride-feel-tools/bury.mjs`).
 *
 * 1 on a step the sink was suppressed, 0 on a step it ran, so the bus's SAMPLES-OVER
 * column reads directly as the share of the run that went unmeasured.
 */
const SINK_COVER = declareInvariant({
  id: 'ridefeel.sinkUnmeasured',
  owner: 'ride-feel',
  what: 'sim steps on which drawn-wheel sink was not measured',
  unit: 'count',
  budget: 0,
  compare: 'max',
  severity: 'warn',
  phase: 'run',
  consequence: 'ridefeel.wheelSinkDepth reads clean over a window nobody is watching',
  source: 'src/core/tuning.ts bike.sinkBlindS',
});

/**
 * Mirror of `bikeModel.ts` CRASH_LIFT — the renderer raises the drawn root out of the
 * hillside while the bike is down, and `measureSink` has to know it to measure the frame
 * the player is actually shown. Copied constants rather than an import because
 * `bikeModel.ts` belongs to another owner and this file must not reach into it; the
 * agreement between the two is checked every round by the probe that reads the posed
 * bones (`captures/ride-feel-tools/sink.mjs`), which is the only place it *can* be
 * checked. If they ever disagree, the bone is right and this is stale.
 */
const CRASH_LIFT_IN = 13;
const CRASH_LIFT_OUT = 3.4;
const CRASH_LIFT_MAX = 1.6;

/** Scratch for `measureSink`'s two passes over the wheels. Module scope: zero allocation. */
const sinkLowY = [0, 0];
const sinkBX = [0, 0];
const sinkBZ = [0, 0];

/**
 * Peak felt g at the CoM. Wave 4 got `coast` down to 4.6 g and 0.20 s of air from 8.08 g
 * and 2.52 s, and nothing in the harness guarded it — this is that guard. A landing off a
 * real drop is allowed to be violent; a bike rolling along the ground is not, and every
 * time this file has regressed it has done so by making an ordinary metre of trail read
 * like a wall.
 */
const PEAK_G = declareInvariant({
  id: 'ridefeel.peakG',
  owner: 'ride-feel',
  what: 'felt acceleration at the combined centre of mass',
  unit: 'g',
  budget: 8,
  compare: 'max',
  severity: 'warn',
  phase: 'run',
  consequence: 'a hit the rider cannot read as terrain — the bike stops dead or leaves the mountain',
  source: 'src/bike/physics.ts PEAK_G',
});

/**
 * DOES THE BIKE LEAVE THE GROUND WHEN THE RIDER ASKS IT TO?
 *
 * This is the invariant the carried gap existed for four rounds without, and the reason it
 * is worth stating carefully is that the gap was invisible for exactly the reason the wave
 * is about: every number the piece published was a *consequence* of the hop working —
 * `maxAirTime` on a scenario, `BIGGEST AIR` on the end card — and every one of them has
 * another owner who can drive it to zero. `hucks` read maxAirTime 0.04 s and the gate run
 * read BIGGEST AIR 0.0, and neither number could tell you whether the bike was refusing to
 * leave the ground or whether the mountain simply had nothing to leave from. (It was both,
 * and this round separated them: see the header.)
 *
 * So this measures the mechanism instead, against the only reference that cannot be argued
 * with. `updateHop` puts a known impulse on `s.vy`. From that instant the bike is a body in
 * a gravity field, and one step later it should be moving at `vy0 - g*dt`, and 100 ms later
 * at `vy0 - g*0.1`. Anything MISSING from that is a force that cancelled the rider's own
 * hop — and the only forces that can are the ones between the rider and the frame, which
 * this file owns end to end.
 *
 * Ground reaction pushes the other way (a loaded spring extending helps the hop leave), so
 * a healthy hop reads at or below zero here and is clamped to zero. It is a one-sided
 * measure on purpose: this is a defect detector, not a score.
 *
 * The number it read before round 9, on the flat hop bench: a full-charge release put
 * 3.6 m/s on `s.vy` and 0.14 s later the bike had 0.7 — the rider's legs were still driving
 * the preload down into a 30 kg chassis at 4.8 g while the bike was trying to leave. That
 * is 0.8 of the hop cancelled, against the 0.25 budget here. It reads the historical defect
 * at full size, which is the check that says it can fail.
 *
 * ONE READING NOTE, because the bus will phrase it misleadingly. A scenario in which the
 * player never presses the button has no hop to grade, so this records no samples and the
 * report lists it under "declared but never observed (owner has not wired it up)". That is
 * the bus's wording for an unwired invariant and it is wrong here — the correct reading on
 * `corner`, `manual` or `parked` is "nobody hopped". It is exercised wherever a hop is
 * released with the wheels down: `pump`, `hucks`, `air` and `pumpsync` all record it.
 */
const HOP_LOSS_BUDGET = 0.25;
const HOP_LOSS = declareInvariant({
  id: 'ridefeel.hopLaunchLoss',
  owner: 'ride-feel',
  what: 'share of a bunny hop\'s own impulse cancelled by the rider\'s own load path within 100 ms',
  unit: 'frac',
  budget: HOP_LOSS_BUDGET,
  compare: 'max',
  severity: 'error',
  phase: 'run',
  consequence: 'the hop button does nothing — the bike cannot leave the ground and no feature on the mountain reads as a jump',
  source: 'src/bike/physics.ts HOP_LOSS_BUDGET',
});

/**
 * "Bottom-outs are felt and rare" (docs/BAR.md), published as a RATE and never as a max.
 *
 * The form matters more than the number, and it is a direct answer to the most expensive
 * mistake this project has made. Wave 4 was planned around "the suspension is on its bump
 * stop in EVERY scenario — maxFrontTravel 170 / maxRearTravel 160, full travel". Both
 * figures were real and the conclusion was wrong: they are maxima over a run, and the
 * median travel is 41/43 mm, which is sag. A whole wave was built on a max read as a state.
 *
 * `maxFrontTravelMm` is still in every SUMMARY.md and it will be misread again. This is the
 * number that cannot be: the running share of GROUNDED sim steps on which either wheel is
 * at the end of its stroke. A max cannot say "rare"; only a rate can, and "rare" is half of
 * what the bar actually asks for. `peakG` above is the other half — "felt".
 *
 * Held back until a second of grounded riding has accumulated, because a share computed
 * over three steps is not a share, and re-published twice a second thereafter so the worst
 * value is the worst the run ever got to rather than whatever the last step happened to be.
 */
const FULL_TRAVEL_BUDGET = 0.02;
const FULL_TRAVEL = declareInvariant({
  id: 'ridefeel.fullTravelShare',
  owner: 'ride-feel',
  what: 'share of grounded sim steps with either wheel at the end of its stroke',
  unit: 'frac',
  budget: FULL_TRAVEL_BUDGET,
  compare: 'max',
  severity: 'warn',
  phase: 'run',
  consequence: 'the suspension is out of travel during ordinary riding, so every bump arrives at the rider through the frame',
  source: 'src/bike/physics.ts FULL_TRAVEL_BUDGET',
});

export interface WheelState {
  /** Suspension compression, m (0 = topped out). Clamped to travelMax. */
  travel: number;
  /**
   * Stroke demanded past full travel, m. Zero whenever the damper is inside its range.
   *
   * `travel` alone cannot tell a reviewer whether a wheel sitting at travelMax is a
   * loaded rider-stage absorption (correct) or an unloaded stall against the bump stop
   * (the bug). Both read exactly travelMax. This is the difference, and it is exported
   * through telemetry so that distinction is measurable rather than inferred.
   */
  overTravel: number;
  travelVel: number;
  /** Wheel spin rate, rad/s. */
  spin: number;
  contact: boolean;
  /** Normal load, N. */
  load: number;
  slipRatio: number;
  slipAngle: number;
  /** 0..1 how saturated the friction circle is — drives dust, sound, and feel. */
  slipUsage: number;
  ground: GroundSample;
  /** World position of the contact patch. */
  cx: number;
  cy: number;
  cz: number;
  /** Bristle anchor for the stiction model. */
  anchorX: number;
  anchorZ: number;
  anchored: boolean;
}

export interface BikeState {
  // Position of the CoM, m.
  x: number;
  y: number;
  z: number;
  // Velocity, m/s.
  vx: number;
  vy: number;
  vz: number;
  /** Heading (yaw), rad. 0 = +X. */
  yaw: number;
  yawRate: number;
  /** Lean (roll about the velocity axis), rad. Positive = right. */
  lean: number;
  leanRate: number;
  /** Pitch, rad. Positive = nose up. */
  pitch: number;
  pitchRate: number;
  /** Steering angle at the bars, rad. */
  steer: number;
  steerRate: number;

  front: WheelState;
  rear: WheelState;

  airborne: boolean;
  airTime: number;
  /** Seconds since last leaving the ground — used for landing detection. */
  lastGroundTime: number;

  /** Rider weight shift, m: +fore, and lateral. */
  riderFore: number;
  riderLat: number;
  /** Rider body compression (legs/arms absorbing), m. */
  bodySquash: number;
  bodySquashVel: number;

  /** Commanded lean from the player — the setpoint must be the player's command.
   * Any setpoint derived from the bike's own state is self-referential and wobbles. */
  leanCommand: number;

  speed: number;
  crashed: boolean;
  crashTimer: number;

  /** Rider leg/arm extension driving the pump, m. Negative = crouched and unweighting,
   * positive = extended and pressing the bike into the ground. Published because it is
   * the pose the rider model and the camera both want, and because it is the only
   * external evidence that a pump happened. */
  pump: number;
  /** Load the rider is currently putting through the contact patches, N. Signed. */
  pumpLoad: number;
  /** Commanded manual, 0..1. */
  manual: number;

  /**
   * Severity of the impact being absorbed right now, 0..1. See updateImpact.
   *
   * Zero for every ordinary landing. It only leaves zero once the wheels are demanding
   * stroke the frame does not have *and* the rider's own legs are well into theirs,
   * which is what casing a landing physically is. 1 means the chassis reached the
   * hillside, and that is a crash.
   */
  impact: number;
  /**
   * How shaken the rider still is by the last case, 0..1, decaying over caseRecover.
   * Every rider authority term in this file is scaled down by it, so a case costs
   * control for a beat instead of only costing speed. Published because the rider model
   * and the camera both want to see it.
   */
  caseShock: number;

  /** Diagnostics for the capture harness. */
  gForce: number;
  hopCharge: number;
}

function makeWheel(): WheelState {
  return {
    travel: 0,
    overTravel: 0,
    travelVel: 0,
    spin: 0,
    contact: false,
    load: 0,
    slipRatio: 0,
    slipAngle: 0,
    slipUsage: 0,
    ground: makeGroundSample(),
    cx: 0,
    cy: 0,
    cz: 0,
    anchorX: 0,
    anchorZ: 0,
    anchored: false,
  };
}

export function makeBikeState(): BikeState {
  return {
    x: 0, y: 0, z: 0,
    vx: 0, vy: 0, vz: 0,
    yaw: 0, yawRate: 0,
    lean: 0, leanRate: 0,
    pitch: 0, pitchRate: 0,
    steer: 0, steerRate: 0,
    front: makeWheel(),
    rear: makeWheel(),
    airborne: false,
    airTime: 0,
    lastGroundTime: 0,
    riderFore: 0,
    riderLat: 0,
    bodySquash: 0,
    bodySquashVel: 0,
    leanCommand: 0,
    speed: 0,
    crashed: false,
    crashTimer: 0,
    pump: 0,
    pumpLoad: 0,
    manual: 0,
    impact: 0,
    caseShock: 0,
    gForce: 1,
    hopCharge: 0,
  };
}

/** Events the sim emits for VFX/audio to consume; cleared each step by the reader. */
export interface SimEvents {
  landed: number;      // impact severity 0..1, -1 if none this step
  bottomOut: number;   // 0..1
  hopped: boolean;
  crashed: boolean;
  scrubImpulse: number;
  /**
   * Severity of the case in progress, 0..1, 0 when there is none.
   *
   * Deliberately a *level* and not an edge, unlike its five siblings. Those are cleared
   * by the render loop after the frame that produced them; this one is written every sim
   * step, so a consumer that samples at 60 Hz cannot miss a case that began and ended
   * between two of its frames — and a case lasts long enough (tens of ms) that a level is
   * the more useful thing to read anyway. `state.impact` is the same number; this is here
   * so everything a system needs about one hit is in one place.
   */
  cased: number;
}

/** Per-wheel force/torque accumulator. Preallocated — this runs 240 times a second. */
interface WheelForces {
  fx: number; fy: number; fz: number;
  yawT: number; rollT: number; pitchT: number;
  /** The tire's own horizontal force, kept apart from the normal force because the
   * anti-squat/anti-rise linkage only routes what the tire makes. */
  tireFx: number; tireFz: number;
}
function makeForces(): WheelForces {
  return { fx: 0, fy: 0, fz: 0, yawT: 0, rollT: 0, pitchT: 0, tireFx: 0, tireFz: 0 };
}

/** Scratch for the wheel-envelope samples. solveWheel is not re-entrant. */
const envS = [makeGroundSample(), makeGroundSample(), makeGroundSample(), makeGroundSample()];
/** Envelope probe offsets and the wheel-curvature drop at each, filled once per step. */
const envOff = [0, 0, 0, 0];
const envDrop = [0, 0, 0, 0];

/** All-neutral input — what reset() rides for half a second to settle onto the springs. */
const SETTLE_INPUT: ControlInput = {
  lean: 0, weightShift: 0, brakeFront: 0, brakeRear: 0,
  pedal: 0, hop: false, airYaw: 0, manual: 0, resetPressed: false,
};

export class BikePhysics {
  readonly state = makeBikeState();
  readonly events: SimEvents = { landed: -1, bottomOut: 0, hopped: false, crashed: false, scrubImpulse: 0, cased: 0 };

  private hopHeld = 0;
  private overAngle = 0;
  /**
   * Impact accumulator. One hit is a window, not a step: the peak of a landing arrives
   * 20-40 ms after touchdown and the stroke that measures it keeps growing for as long
   * again, so severity has to be the peak over a window or it reads whatever the first
   * step happened to see. `impHold` is the seconds left before the window closes.
   */
  private impPeakOv = 0;
  private impGImpulse = 0;
  private impHold = 0;
  private impSev = 0;
  /** Rider thrown fore/aft by a case, m. Decays; added on top of the two ordinary halves. */
  private caseFore = 0;
  /** Seconds after a remount during which a crash cannot re-trigger. */
  private remountGrace = 0;
  /** Rider leg coordinate for the pump: position (m) and velocity (m/s). It moves the
   * datum the legs hold about; the load that produces is the leg element's own, not a
   * separate force. See updatePump and legForce. */
  private pumpPos = 0;
  private pumpVel = 0;
  /** Seconds left of the extension that follows a released hop button. */
  private popTimer = 0;
  /** Brake torque the pads have actually taken up, N*m. A lever is not a switch: pad
   * clearance, hose swell and tire carcass all lag the lever, and without that lag a
   * step input locks the wheel before any load has transferred. */
  private brakeAppliedFront = 0;
  private brakeAppliedRear = 0;
  /** Player-commanded and automatic halves of the rider's fore/aft position, m. */
  private playerFore = 0;
  private autoFore = 0;
  /** Total tire longitudinal force this step and last, N — the only thing that actually
   * transfers load, and therefore the only thing the automatic bias reacts to. */
  private tireLongStep = 0;
  private tireLongPrev = 0;
  /** Attitude a chassis lying parallel to the ground under the bike would hold, rad. */
  private groundPitch = 0;
  /**
   * Last step's wheel-envelope contact height under each wheel, m, and whether there is
   * one to difference against yet.
   *
   * This is the ground half of the shaft-speed identity and it is a *measurement*, not a
   * cache: see the long note at `vGround` in solveWheel. It is sampled every step whether
   * the wheel is touching or not, so it is continuous through a landing.
   */
  private prevGndF = 0;
  private prevGndR = 0;
  private prevGndValid = false;
  /** Scratch for the fall-line lookup a remount does. Preallocated — no per-step garbage. */
  private readonly descentDir = { x: 0, z: 0 };
  /**
   * The ground under the CoM, and how hard the frame is currently pressing on it (N).
   *
   * This is the frame-vs-hillside contact that replaced the positional floor clamp — see
   * the long note in `step`. It is read by `updateImpact` so that a chassis on the dirt is
   * something the severity model can see; before this it was invisible to every consumer
   * in the file, which is how the bike could be buried and read `airborne`.
   */
  private readonly bellyG = makeGroundSample();
  private bellyLoad = 0;
  private readonly frontF = makeForces();
  private readonly rearF = makeForces();
  /** This step's rider fore/aft CoM shift, m. See `drawX` in solveWheel. */
  private riderShift = 0;
  /**
   * True while `reset()` is driving its 700-step settle. The self-audit bus is reset by
   * the harness at the scenario boundary and `reset()` runs immediately after it, so
   * without this every scenario's worst sink would be a spawn transient measured before
   * the bike has found its springs — the same class of error as reading a max over a run
   * as a state.
   */
  private settling = false;
  /** Seconds left of the crash window `ridefeel.wheelSinkDepth` does not measure in.
   *  `bike.sinkBlindS` is 0, so this stays 0 — it exists so the historical blindness can
   *  be reproduced and the coverage invariant can still fail. See SINK_COVER. */
  private sinkBlind = 0;
  /** This file's copy of `bikeModel.ts`'s crash-lift mix, stepped at sim rate. The drawn
   *  bike rides out of the hillside while it is down and `measureSink` measures the drawn
   *  bike, so it has to carry the same number. See CRASH_LIFT_IN. */
  private crashMix = 0;
  /**
   * The ballistic reference a fired hop is graded against — see `HOP_LOSS`.
   * `hopWatch` is the seconds left of the window, `hopVyRef` the velocity a body given
   * that impulse would have by now, and `hopVy0` the impulse itself (the denominator).
   */
  private hopWatch = 0;
  private hopVyRef = 0;
  private hopVy0 = 0;
  /** Grounded steps, steps with a wheel at the end of its stroke, and seconds since the
   *  share was last published. See `FULL_TRAVEL`. */
  private btGrounded = 0;
  private btFull = 0;
  private btAcc = 0;
  /** Vertical arm from the CoM to the patch solveWheel is currently working on, m
   * (negative — the patch is below). solveTire needs it to build the patch velocity and
   * solveWheel has already resolved it; recomputing it there would be the same trig twice. */
  private armUp = 0;

  constructor(private readonly terrain: Terrain) {}

  /**
   * Drop the bike in already sitting on its suspension, pitched to the ground under it.
   * Spawning level on a 20% slope buries the rear wheel 100 mm into the stroke on frame
   * one; the spring answers with ten g and the first thing the player sees is the bike
   * leaving the mountain.
   */
  reset(x: number, z: number, yaw: number): void {
    const s = this.state;
    const B = Tuning.bike;
    // Nothing between here and the end of this function is riding, so nothing between
    // here and the end of this function is evidence. See `settling`.
    this.settling = true;
    Object.assign(s, makeBikeState());
    s.x = x;
    s.z = z;
    s.yaw = yaw;
    // Start at the sag the springs actually produce, not at the nominal `sagFraction`.
    // The two only agree when the rates happen to be sized for that fraction, and when
    // they disagree the settle loop below has to absorb the difference as a real
    // transient — which it cannot always do. Measured while re-rating the springs: an
    // initial travel 17 mm past equilibrium at each wheel put ~280 N of unbalanced spring
    // into the first step, the chassis pitched into it, and reset() came to rest belly
    // down at 28 degrees nose-low with the front wheel carrying the whole bike, on flat
    // ground. Deriving the pose from load/rate makes the analytic guess exact for the
    // heave mode, so the settle only has to find pitch.
    const S0 = Tuning.suspension;
    const loadF = (1 - B.rearWeightBias) * B.mass * G;
    const loadR = B.rearWeightBias * B.mass * G;
    const sagF = clamp(loadF / S0.frontRate, 0, S0.frontTravel);
    const sagR = clamp(loadR / S0.rearRate, 0, S0.rearTravel);
    s.front.travel = sagF;
    s.rear.travel = sagR;
    s.front.load = loadF;
    s.rear.load = loadR;
    // Ride height that puts each wheel at that travel: solveWheel reads
    // travel = comHeight + travelMax*sagFraction - drop - tireDeflection, so drop is
    // comHeight offset by however far the real sag sits from the nominal one, plus the
    // few millimetres the casings give under static load. The casing term is small — 3-4
    // mm a side — but leaving it out means the first step reads the bike sitting that far
    // *into* the stroke and answers with a couple of hundred newtons of transient, which
    // is the same class of error the paragraph above is about.
    const tireF = clamp(loadF / S0.frontTireRate, 0, S0.tireMaxDeflect);
    const tireR = clamp(loadR / S0.rearTireRate, 0, S0.tireMaxDeflect);
    const dropRef = B.comHeight
      + 0.5 * ((S0.frontTravel * S0.sagFraction - sagF - tireF)
             + (S0.rearTravel * S0.sagFraction - sagR - tireR));

    // The same pose in the drawn frame, because the solver now measures travel there (see
    // `dropDrawn`). Two differences: `comHeight` is not projected onto the pitched axis,
    // and the wheel's own dip under pitch — `wheelRadius*(1-cos(pitch))` — has to be
    // carried, since the renderer swings the axle about the contact line. The casing terms
    // drop out because the renderer poses the wheel from `travel` and has no tire
    // compliance at all. Getting this wrong costs a transient the settle loop then has to
    // absorb, which is the failure the paragraph above is about.
    const mixR = B.drawnPoseBlend;
    const dropRefDrawn = 0.5 * ((S0.frontTravel * S0.sagFraction - sagF)
      + (S0.rearTravel * S0.sagFraction - sagR));

    const a = B.wheelbase * B.rearWeightBias;
    const b = B.wheelbase * (1 - B.rearWeightBias);
    // The renderer's axle offsets: its chassis origin is the bottom bracket, not the CoM.
    const aD = B.wheelbase - B.drawnChainstay;
    const bD = B.drawnChainstay;
    const fwdX = Math.cos(yaw), fwdZ = Math.sin(yaw);
    let pitch = 0;
    for (let i = 0; i < 10; i++) {
      const cp = Math.cos(pitch);
      const sp = Math.sin(pitch);
      // Same arms the solver uses: the wheels hang off the body-down axis, so on a
      // pitched chassis both patches sit armH behind where a level bike would put them.
      // Spawning against the level-bike assumption on a 20% descent buries the rear
      // 110 mm into the stroke on frame one and the first thing the harness sees is
      // 6 kN of spring and the bike leaving the mountain.
      const armH = B.comHeight * sp * (1 - mixR);
      const af = lerp(a, aD, mixR);
      const br = lerp(b, bD, mixR);
      const hf = this.terrain.height(x + fwdX * (af * cp + armH), z + fwdZ * (af * cp + armH));
      const hr = this.terrain.height(x + fwdX * (armH - br * cp), z + fwdZ * (armH - br * cp));
      const rideH = lerp(dropRef * cp,
        B.comHeight + B.wheelRadius * (1 - cp) + dropRefDrawn * cp, mixR);
      s.y = 0.5 * ((hf - af * sp) + (hr + br * sp)) + rideH;
      pitch = Math.atan2(clamp(hf - hr, -1.2, 1.2), B.wheelbase * cp);
    }
    s.pitch = pitch;
    this.hopHeld = 0;
    this.overAngle = 0;
    this.playerFore = 0;
    this.autoFore = 0;
    this.tireLongStep = 0;
    this.tireLongPrev = 0;
    this.groundPitch = s.pitch;
    this.prevGndValid = false;
    this.prevGndF = 0;
    this.prevGndR = 0;
    this.pumpPos = 0;
    this.pumpVel = 0;
    this.popTimer = 0;
    this.brakeAppliedFront = 0;
    this.brakeAppliedRear = 0;

    // Then settle it for real. The analytic pose above puts the frame parallel to two
    // sampled ground points at nominal sag, but the solver reads the ground through a
    // wheel envelope and balances pitch against two springs of different rates, so the
    // analytic pose is close and never exact. Measured, it was 45 mm of fork out of
    // place on the first step: the damper answers that with most of a g, the harness
    // reads 1.4 g and a dive to 18 degrees nose-down before the rider has touched
    // anything, and the front tire spends the first half-second at full slip. Half a
    // second with the clock hidden costs nothing and puts the bike genuinely at rest on
    // its suspension.
    //
    // Pinned in the horizontal plane rather than held on the brakes. Brakes would settle
    // it, but they settle it *nose-heavy*: holding a bike on a 20% pitch runs the whole
    // gravity component through the contact patches, which is a real load transfer, and
    // the player would then start every run with 70% of the weight on the front. Pinning
    // vx/vz leaves the tires making no longitudinal force at all, which is the pose a
    // bike freewheeling onto that piece of ground actually holds.
    // Heavily overdamped so it relaxes monotonically onto the fixed point instead of
    // ringing: without the bleed the settle stops wherever the bounce happened to be on
    // the last frame, and the fork was topped out on frame one about a third of the time.
    // Mild enough (about 7/s against a 1.5 Hz heave mode) that it still reaches the true
    // equilibrium rather than stalling short of it.
    //
    // The anchors are dropped every step, and that is not tidiness — it is the whole
    // reason this loop converges. Pinning vx/vz stops the bike *translating*, but the
    // contact patches still swing fore/aft as the chassis pitches (solveWheel resolves
    // them through the pitched arms), so a 45 kN/m bristle at each patch is a torsional
    // spring of tens of kN*m/rad fighting exactly the rotation this loop is trying to
    // relax. Measured with the anchors live: reset put the bike down 15 degrees nose-low
    // with 935 N on the front wheel and *nothing* on the rear — 68% of the fork's stroke
    // gone before the player has touched anything, and the rear tire with no load to make
    // grip with — and it took a full second of riding to shed. Since x/z are pinned there
    // is nothing for the bristle to prevent here anyway.
    for (let i = 0; i < 700; i++) {
      this.step(1 / 240, SETTLE_INPUT);
      s.vx = 0; s.vz = 0; s.yawRate = 0; s.leanRate = 0;
      s.lean = 0; s.front.spin = 0; s.rear.spin = 0;
      s.x = x; s.z = z; s.yaw = yaw;
      s.front.anchored = false; s.rear.anchored = false;
      s.vy *= 0.97;
      s.pitchRate *= 0.97;
    }

    s.vx = 0; s.vy = 0; s.vz = 0;
    s.yawRate = 0; s.leanRate = 0; s.pitchRate = 0;
    s.lean = 0; s.leanCommand = 0; s.steer = 0; s.steerRate = 0;
    s.yaw = yaw;
    s.speed = 0;
    s.front.spin = 0; s.rear.spin = 0;
    s.riderFore = 0; s.riderLat = 0;
    s.bodySquash = 0; s.bodySquashVel = 0;
    s.airborne = false; s.airTime = 0; s.lastGroundTime = 0;
    // Drop the bristle anchors with everything else. The settle loop leaves them stretched
    // wherever it finished, and since the velocities are being zeroed underneath them that
    // stretch is a force with nothing to balance: the harness read the rear tire at 0.98 of
    // its friction circle on frame one, which is a puff of dust and a squeal of tire on
    // spawn, every time.
    s.front.anchored = false; s.rear.anchored = false;
    s.front.slipUsage = 0; s.rear.slipUsage = 0;
    s.front.slipRatio = 0; s.rear.slipRatio = 0;
    s.front.slipAngle = 0; s.rear.slipAngle = 0;
    s.crashed = false; s.crashTimer = 0;
    s.impact = 0; s.caseShock = 0;
    s.gForce = 1;
    this.impPeakOv = 0; this.impGImpulse = 0; this.impHold = 0; this.impSev = 0;
    this.caseFore = 0; this.remountGrace = 0;
    this.playerFore = 0;
    this.autoFore = 0;
    this.tireLongStep = 0;
    this.tireLongPrev = 0;
    this.overAngle = 0;
    this.hopHeld = 0;
    this.pumpPos = 0;
    this.pumpVel = 0;
    this.popTimer = 0;
    this.brakeAppliedFront = 0;
    this.brakeAppliedRear = 0;
    s.pump = 0; s.pumpLoad = 0; s.manual = 0;
    this.events.landed = -1;
    this.events.bottomOut = 0;
    this.events.hopped = false;
    this.events.crashed = false;
    this.events.scrubImpulse = 0;
    this.events.cased = 0;
    // Drop the ground-rate history LAST, after every pose fixup above.
    //
    // It has to be here and not before the settle loop, because the loop re-validates it
    // and then the lines above move the bike underneath it. `s.riderFore = 0` is the one
    // that bites: the automatic steepness bias has the rider ~40 mm behind neutral by the
    // end of a settle on a real gradient, zeroing it moves `frontOff`/`rearOff` and
    // therefore slides both contact patches 40 mm along the hill in no time at all. A
    // measured ground rate reports that honestly as several m/s of ground movement, the
    // damper answers it, and the harness reads 6.0 g on the first step of a run — on the
    // `parked` scenario, which is a bike standing still. A teleport has no previous
    // ground to difference against, by definition; the first step after one falls back to
    // the analytic gradient and the second differences normally.
    this.prevGndValid = false;
    this.prevGndF = 0;
    this.prevGndR = 0;
    this.settling = false;
    this.sinkBlind = 0;
    this.crashMix = 0;
    this.hopWatch = 0;
    this.hopVyRef = 0;
    this.hopVy0 = 0;
    this.btGrounded = 0;
    this.btFull = 0;
    this.btAcc = 0;
  }

  step(dt: number, input: ControlInput): void {
    const s = this.state;
    const B = Tuning.bike;

    s.speed = Math.hypot(s.vx, s.vz);

    this.updateRider(dt, input);
    this.updateSteering(dt, input);
    this.updateBrakes(dt, input);
    this.updatePump(dt, input);

    // Accumulated forces at the CoM, world frame.
    let fx = 0;
    let fy = -B.mass * G;
    let fz = 0;
    let yawTorque = 0;
    let rollTorque = 0;
    let pitchTorque = 0;

    // Body axes.
    const cy = Math.cos(s.yaw);
    const sy = Math.sin(s.yaw);
    // Forward (heading) and right vectors on the horizontal plane.
    const fwdX = cy, fwdZ = sy;
    const rgtX = -sy, rgtZ = cy;

    // Longitudinal axle offsets from the CoM. The static load split is geometry, not a
    // magic number: the rear carries rearWeightBias of the weight exactly when the front
    // axle sits that fraction of the wheelbase ahead of the CoM. Rider weight shift then
    // moves the CoM inside that span, which is what actually changes the split when you
    // get behind the saddle — no separate "weight transfer" fudge is needed anywhere.
    const shift = s.riderFore * Tuning.rider.comShiftFraction;
    // Published to solveWheel so the drawn-pose mirror can take it back out again: the
    // renderer's axles do not move when the rider does, so mirroring what it draws means
    // undoing this term. See `drawX`.
    this.riderShift = shift;
    const frontOff = B.wheelbase * B.rearWeightBias - shift;
    const rearOff = -B.wheelbase * (1 - B.rearWeightBias) - shift;

    const rearRes = this.rearF;
    const frontRes = this.frontF;
    this.tireLongStep = 0;
    this.solveWheel(dt, s.rear, rearRes, rearOff, input, false, fwdX, fwdZ, rgtX, rgtZ);
    this.solveWheel(dt, s.front, frontRes, frontOff, input, true, fwdX, fwdZ, rgtX, rgtZ);
    this.tireLongPrev = finiteOr(this.tireLongStep, 0);

    fx += rearRes.fx + frontRes.fx;
    fy += rearRes.fy + frontRes.fy;
    fz += rearRes.fz + frontRes.fz;
    yawTorque += rearRes.yawT + frontRes.yawT;
    rollTorque += rearRes.rollT + frontRes.rollT;
    pitchTorque += rearRes.pitchT + frontRes.pitchT;

    const grounded = s.front.contact || s.rear.contact;

    // --- The frame against the hillside ------------------------------------------
    //
    // This replaces the positional clamp that used to sit at the bottom of `step`, and the
    // replacement is the point. A clamp is not a contact: it teleported the CoM, threw away
    // whatever downward velocity the bike had, and did both *outside* the force sum — so
    // `gForce` could not see it, `updateImpact` could not score it, and `airborne` stayed
    // true while the guard alone held the bike up. The critique measured exactly that state
    // and named it: "the clamp needs to stop being the thing that carries the bike."
    //
    // What is here instead is an ordinary contact, solved with everything else:
    //
    //  - It is a **force**, added to the same accumulator the wheels use, before
    //    integration. It therefore appears in `s.gForce` (which is fy/m by construction),
    //    it can only ever remove the energy its damper removes, and it cannot inject the
    //    step change in velocity a clamp does.
    //  - It acts **along the ground normal**, not straight up. On a 40-degree face a
    //    vertical clamp deletes the component of the bike's motion that was carrying it
    //    along the hill; a normal contact lets it keep sliding, which is what a frame
    //    scraping a bank actually does.
    //  - Its damper only ever resists **closing** motion, so it is dissipative and cannot
    //    push the bike back out faster than it came in.
    //  - And while it is touching, the bike is **not airborne** (see below). A chassis
    //    resting on the dirt is the single least airborne thing in the model, and the
    //    telemetry used to call it flight.
    //
    // The reference height is unchanged (`chassisFloorFraction`), so where the frame meets
    // the ground is the same place it was; only what happens there is different. The hard
    // positional backstop still exists at the very bottom of `step`, much deeper, and it is
    // now genuinely a NaN/one-bad-step guard rather than a load path.
    this.terrain.sample(s.x, s.z, this.bellyG);
    const bellyPen = B.comHeight * B.chassisFloorFraction - (s.y - this.bellyG.height);
    const bellyContact = bellyPen > 0;
    if (bellyContact) {
      const bg = this.bellyG;
      // The penetration is measured VERTICALLY — it is a heightfield, so `y - height()` is
      // all there is — and the contact acts along the NORMAL, so the two have to be
      // reconciled or the force is wrong by 1/ny on exactly the ground where it fires most.
      // The perpendicular depth of a point `pen` below the surface in y is `pen*ny`; on a
      // 45-degree face that is 71% of the vertical figure, and on the 60-70% faces this
      // mountain is made of it is half. Skipping the correction was measured and it is not
      // cosmetic: it made a lateral shove of `k*pen*nx` on every steep traverse, which is a
      // hand pushing the bike off the line at the one moment it has least margin.
      const penPerp = bellyPen * Math.max(bg.ny, 0.15);
      // Closing speed along the surface normal, positive driving in. Resisted one way only,
      // so this element can dissipate and never return energy.
      const closing = -(s.vx * bg.nx + s.vy * bg.ny + s.vz * bg.nz);
      this.bellyLoad = clamp(
        B.bellySpring * penPerp + (closing > 0 ? B.bellyDamping * closing : 0),
        0, B.bellyMaxForce,
      );
      fx += bg.nx * this.bellyLoad;
      fy += bg.ny * this.bellyLoad;
      fz += bg.nz * this.bellyLoad;
    } else {
      this.bellyLoad = 0;
    }

    // Attitude of the ground under the bike. Every "is the rider about to go over the
    // bars" test has to be measured against this, not against the world horizon: on a
    // 30 degree chute an ordinary, comfortable attitude *is* 30 degrees nose down, and
    // an absolute threshold fires the endo save continuously and fights the terrain the
    // rider is deliberately following. Damped, because a single wheel dropping into a
    // hole swings the sampled normal hard for one step.
    if (grounded) {
      let gnx = 0, gny = 0, gnz = 0;
      if (s.front.contact) { gnx += s.front.ground.nx; gny += s.front.ground.ny; gnz += s.front.ground.nz; }
      if (s.rear.contact) { gnx += s.rear.ground.nx; gny += s.rear.ground.ny; gnz += s.rear.ground.nz; }
      if (gny > 1e-3) {
        const along = gnx * fwdX + gnz * fwdZ;
        this.groundPitch = damp(this.groundPitch, -Math.atan2(along, gny), 10, dt);
      }
    } else if (Tuning.control.airGroundTrack > 0) {
      // Airborne, the same quantity has to keep being measured, because it is what the
      // rider is looking at: the slope they are about to land on.
      //
      // Gated on the same constant that consumes it so that setting `airGroundTrack` to 0
      // reproduces the behaviour this replaced exactly — freezing `groundPitch` at the lip
      // and levelling to the horizon — rather than half of it. An A/B knob that only
      // disables the visible half of a change prices the wrong thing.
      //
      // It used to freeze at whatever the lip read, and applyAirControl did not use it at
      // all — a neutral stick levelled the bike to the *world* horizon. On a descent that
      // is a deliberate mis-pitch by the whole gradient: the bike arrives nose-up, the rear
      // hits first, and it pitches straight back off the ground. Measured by A/B on this
      // constant alone at 240 Hz, `corner` is airborne 8.8% of the run without it and 1.8%
      // with it, and its front wheel is off the ground 10.1% of grounded frames against
      // 6.0%. A front wheel with no load steers nothing, and "left the corridor" is the
      // most common ejection on the gate scenario.
      //
      // The preview is ballistic and cheap: solve for when the chassis comes back down to
      // the height of the ground beneath it, carry that far forward at the current ground
      // speed, and read the grade there over one wheelbase. Exact on flat ground, short
      // (i.e. conservative) into a landing that falls away, and it collapses to the ground
      // underneath the bike as the flight ends, so there is no step at touchdown. Three
      // height() calls, only while airborne.
      // Solve h + vy*t - g*t^2/2 = 0 for the positive root, with vy UPWARD-positive:
      //     t = (vy + sqrt(vy^2 + 2*g*h)) / g
      // Writing it with a downward-positive velocity instead flips the sign of the first
      // term, and that is not a cosmetic error — it is the difference between "you are
      // already at the ground, look 0 m ahead" and "you will go up and come back down,
      // look 2*v/g seconds ahead". Measured with the sign wrong: a wheel 0 m above the
      // ground falling at 3.3 m/s previewed 0.67 s and 6 m of hillside instead of 0, so
      // the bike matched a slope three corners further down and landed nose-first with the
      // REAR wheel in the air and the front tire at 0.99 of its friction circle. The sign
      // is checked by the two degenerate cases: falling and touching gives 0, rising off a
      // lip at v with nothing below gives 2*v/g.
      const hHere = this.terrain.height(s.x, s.z);
      const fall = Math.max(0, s.y - B.comHeight - hHere);
      const tFall = Math.min(
        Tuning.control.airPreviewMaxS,
        Math.max(0, (s.vy + Math.sqrt(Math.max(s.vy * s.vy + 2 * G * fall, 0))) / G),
      );
      const ahead = s.speed * tFall;
      const half = B.wheelbase * 0.5;
      const hF = this.terrain.height(s.x + fwdX * (ahead + half), s.z + fwdZ * (ahead + half));
      const hR = this.terrain.height(s.x + fwdX * (ahead - half), s.z + fwdZ * (ahead - half));
      this.groundPitch = damp(this.groundPitch, Math.atan2(hF - hR, B.wheelbase), 10, dt);
    }

    // A bike whose frame is on the dirt is not flying, whatever the wheel solve says about
    // it. `bellyContact` is in here rather than folded into `grounded` on purpose: every
    // other consumer of `grounded` (the rider balance PD, the saves, the manual, yaw and
    // pitch damping) is scaled by tire *load*, and a frame dragging on a bank supplies
    // none — so letting it stand in for a contact patch would hand the rider authority
    // they do not have. What it does own is the flight state, and only that.
    if (grounded || bellyContact) {
      if (s.airborne) {
        const impact = clamp01((-s.vy) / 12);
        this.events.landed = impact;
        s.airborne = false;
      }
      s.airTime = 0;
      s.lastGroundTime = 0;
    } else {
      if (!s.airborne && s.lastGroundTime > 0.06) s.airborne = true;
      s.lastGroundTime += dt;
      s.airTime += dt;
      this.applyAirControl(dt, input);
    }

    // --- Rider balance ---------------------------------------------------------
    // The tires do the leaning (see updateSteering) but they can only do it while the
    // contact patches are moving; below walking pace a two-wheeler is a bare inverted
    // pendulum and falls over in about a second. A real rider balances there with body
    // english and a dab. This term is that rider: strong at a standstill, a light
    // centring hand at speed. Making it strong at speed instead is the tempting shortcut
    // and it kills countersteer — the bike then rights itself without turning.
    // A rider on the floor is not balancing anything. Until this round `crashed` was a
    // flag with no dynamics behind it at all: the bike kept the full balance assist, the
    // endo save and the manual, and rode away from a crash at fifty km/h. See updateCrash.
    if (grounded && !s.crashed) {
      const R = Tuning.rider;
      const C = Tuning.control;
      const I = Tuning.impact;
      const load = s.front.load + s.rear.load;
      const loadFrac = clamp01(load / (B.mass * G));
      // What a case costs: authority, for as long as the rider is still gathering
      // themselves up. This is the second half of giving impact severity a state — the
      // first half (speed, and the crash itself) is in updateImpact/updateCrash, and on
      // its own it would only be a scalar penalty. Taking the rider's hands off the bike
      // for a beat is what makes a hard landing something you ride *out of* rather than
      // something the telemetry logs. Never all of it: a rider who has just cased is
      // fighting, not absent, and zero authority here is an unrecoverable lowside from
      // every big hit.
      const authority = 1 - I.caseAuthorityLoss * s.caseShock;
      const slow = 1 - clamp01(s.speed / R.balanceSpeed);
      // At a crawl the rider stands the bike up rather than holding the commanded lean.
      const target = s.leanCommand * clamp01(s.speed / R.balanceSpeed);
      const raw = -(s.lean - target) * R.balanceTorque - s.leanRate * R.balanceDamping;
      // Ceiling on it, because at speed all the rider has is their own mass on a lever
      // about a metre long — roughly 200 N*m. At a crawl they can also put a foot down,
      // which is worth a great deal more. Leaving it uncapped is the shortcut that makes
      // the bike self-right without turning, and countersteer stops meaning anything.
      // Past the lean the rider actually asked for, they are no longer riding, they are
      // fighting a crash — and a rider fighting a crash uses everything they have: knee
      // out, bar in, foot down. It has to be an *additive* torque that grows with how far
      // past the command the bike is, not a larger ceiling on the body-english term:
      // raising the ceiling alone changes nothing, because the proportional term never
      // reaches it — measured, the bike sat at 60 degrees against a 24 degree command in
      // a perfectly stable equilibrium, one bump away from an unrecoverable lowside from
      // an input the player thought meant "hold this turn". The deadband is what keeps
      // this out of ordinary cornering: inside it the tires do all the work, so
      // countersteer still has to earn the lean.
      //
      // The measure is `|lean|` past whatever the rider asked for *on the side the bike is
      // actually on*, and the distinction is not pedantry. `|lean| - |leanCommand|` was
      // here, and it reads a bike lying at 29 degrees left against a 29 degree *right*
      // command as perfectly obedient — the rider is upside down relative to what they
      // asked for and the term that exists to notice exactly that says nothing. The
      // harness caught the consequence on holdline: a landing rolled the bike from +22 to
      // -58 degrees against a steady +29 command and the save did not engage until -34,
      // by which point it was catching a crash instead of preventing one. Taking the
      // command as zero whenever the bike is on the other side of vertical costs nothing
      // in ordinary cornering (the command and the lean share a sign there) and makes a
      // roll to the wrong side something the rider fights from a few degrees out.
      const cmdOnThisSide = Math.max(0, sign(s.lean) * s.leanCommand);
      const over = Math.max(0, Math.abs(s.lean) - cmdOnThisSide - R.saveDeadband);
      const save = over > 0
        ? -sign(s.lean) * over * R.saveTorque - s.leanRate * R.saveDamping * clamp01(over * 8)
        : 0;
      const cap = lerp(R.balanceMaxTorque, R.balanceMaxTorqueLow, slow * slow);
      // Two things the save does not share with the balance PD above it, and both are
      // asymmetries the model could not survive. See `rider.saveLoadCap` and
      // `impact.caseSaveAuthorityLoss` for the measurements.
      //
      // (1) It works against the load that is doing the toppling, not against one g. The
      //     term that lays a leaned bike down is the normal force through
      //     comHeight*sin(lean), and on a landing that force is many times static — while
      //     `loadFrac` is clamped at 1, so the rider's answer was not. A rider standing on
      //     the pedals through a compression can push in proportion to what is pushing back
      //     at them; that is the same physics on both sides of the equation, and it is why
      //     weighting the outside pedal works in a berm and does nothing in the air.
      // (2) A case does not take it away. Being slammed costs finesse — holding a line, a
      //     manual — and those still pay the full `caseAuthorityLoss` through `authority`.
      //     It does not cost the reflex that stops you going down, and charging the save
      //     for it is what made every hard landing cost the bike twice.
      const saveLoad = Math.min(Math.max(load, 0) / (B.mass * G), R.saveLoadCap);
      const saveAuth = 1 - I.caseAuthorityLoss * I.caseSaveAuthorityLoss * s.caseShock;
      const assist = clamp(raw, -cap, cap) * loadFrac * authority + save * saveLoad * saveAuth;
      rollTorque += assist;

      // --- Roll authority while the tires carry nothing --------------------------
      // Every rider term above is multiplied by loadFrac, which is right — knee out, bar
      // in and a foot down all need a contact patch. But it leaves a hole: between a
      // bottom-out and the tires taking load again the wheels can be touching and carrying
      // almost nothing for a couple of hundred milliseconds, and in that window the
      // grounded path has no authority *and* the airborne path is not running, because the
      // bike is technically still in contact. That is the one moment a landing is most
      // likely to throw the bike onto its side, and the model had the rider frozen for it.
      //
      // Airborne, applyAirControl already says what a rider can do with no tire load at
      // all: move the bike about their own mass, tracking a lean attitude at a bounded
      // rate. None of that stops being true because a tire is brushing the dirt. So this
      // is that same authority, faded in by how unweighted the bike is and squared so the
      // onset is gentle. At normal load it is identically zero, and it can never exceed
      // what the rider could do a centimetre higher in the air.
      const light = 1 - clamp01(loadFrac / R.unweightedLoadFrac);
      if (light > 0.001) {
        const wantRate = clamp((s.leanCommand - s.lean) * C.airLeanTrack, -C.airRollRate, C.airRollRate);
        rollTorque += clamp(
          (wantRate - s.leanRate) * R.unweightedRollGain,
          -R.unweightedRollMax, R.unweightedRollMax,
        ) * light * light * authority;
      }
      // Publish it as body english. Standing the bike up out of a right-hand lean means
      // the rider's weight goes left, which is exactly the counter-lean you want to see.
      s.riderLat = damp(s.riderLat, clamp(assist / Math.max(load, 1), -R.lateralShiftRange, R.lateralShiftRange), 9, dt);

      // The same save in pitch. Unlike lean, a rider has real pitch authority all the
      // time — pulling the bars against the pedals is what a manual is — so this is not
      // a cheat, it is the missing degree of freedom. Front brake at mu 1.25 decelerates
      // harder than the geometry can hold the rear down, so without it every hard stop
      // on a steep pitch ends over the bars, which is a stoppie the player cannot ride
      // out. Below pitchSaveAngle it contributes nothing, so ordinary nose-dive, manual
      // and terrain-following attitude are all still pure suspension geometry.
      //
      // It has to be strong enough to beat the geometry it is fighting, and measured,
      // it was not. Past atan(frontOffset/comHeight) = 33 degrees nose down the front
      // contact patch is *behind* the CoM, so the front wheel's own normal force pitches
      // the bike further over — that is the balance point of a stoppie, and there is a
      // stable equilibrium on the far side of it. The harness found it: the bike parked
      // itself on its front wheel at 42 degrees for eighteen seconds and never came
      // back. The save now starts inside 33 degrees and outweighs that term by an order
      // of magnitude, so past the balance point is somewhere the rider visits and rides
      // back out of.
      // Asymmetric, because the two balance points are not in the same place. Nose-down
      // the front patch passes behind the CoM at atan(frontOffset/comHeight) — 40 degrees
      // with the rider back. Nose-up it is the *rear* patch and the short arm, which the
      // steepness bias shortens further exactly when a descent is trying to loop the bike
      // out: 22 degrees. A single 0.5 rad threshold is inside one and well outside the
      // other, and the harness caught it — cresting a roller the bike went to 26 degrees
      // nose-up with the front wheel in the air and came down on the fork.
      const relPitch = s.pitch - this.groundPitch;
      // --- Manual ---------------------------------------------------------------
      // The rider pulling on the bars against the pedals. It is a held *attitude*, not
      // an impulse — the whole mechanic is sitting on the balance point — so it is a
      // capped PD on pitch relative to the ground under the bike, exactly like every
      // other attitude term in this file. Capped at roughly what a rider's own weight on
      // the bar-to-pedal lever is worth: past the balance point the torque cannot save
      // them, and having to catch it by moving is what makes holding one an act of
      // balance rather than a button that is either pressed or not.
      const manual = clamp01(finiteOr(input.manual, 0));
      s.manual = manual;
      if (manual > 0.01) {
        const want = manual * C.manualAngle;
        pitchTorque += clamp(
          (want - relPitch) * C.manualTorque - s.pitchRate * C.manualDamping,
          -C.manualTorqueMax, C.manualTorqueMax,
        ) * loadFrac * authority;
      }
      // The loop-out save stands down while a manual is commanded. It exists so terrain
      // cannot loop the bike out from under a rider who did not ask for it; a rider
      // deliberately manualling is asking for precisely that attitude, and a save that
      // fights them means the mechanic does not exist. It comes back past the relief, so
      // going too far is still catchable rather than fatal.
      const loopAngle = R.loopSaveAngle + manual * C.manualSaveRelief;
      const overP = Math.abs(relPitch) - (relPitch > 0 ? loopAngle : R.pitchSaveAngle);
      if (overP > 0) {
        const into = clamp01(overP * 4);
        // Round 6: the pitch save gets the same load treatment the roll save got, and for
        // the identical reason — this was the one half of that finding that was never
        // applied. `saveLoadCap`'s note says the term that topples a leaned bike is the
        // normal force through comHeight*sin(lean) and scales with the whole load, so a
        // rider answering it at `loadFrac` (clamped at 1) has a fraction of the authority
        // of the thing rolling them. Pitch is the same equation with the other arm: past
        // atan(frontOffset/comHeight) the front contact patch is BEHIND the CoM, so the
        // front wheel's own normal force pitches the bike further nose-down, and that
        // moment is `rLong * load` — again the whole load.
        //
        // Measured at 240 Hz on the gate scenario, this is the mechanism behind the last
        // burial left in the file. The deepest excursion of the whole run (404 mm past
        // full travel, t=81.7 s) happens at **11.9 km/h** — far too slow to be a wall
        // strike — with the chassis at **-52 degrees of pitch**. It is not the suspension
        // and it is not speed: at that attitude the rigid geometry puts the front axle
        // mount `frontOffset*sin(pitch)` = 546 mm below the CoM's own plane, the kinematic
        // travel formula divides the rest by cos(pitch), and the wheel is asked for 574 mm
        // of a 170 mm stroke. `travel` clamps, the renderer draws the wheel from the
        // clamped value, and the difference is exactly how far the drawn wheel is inside
        // the hill. At that instant the front patch is 346 mm behind the CoM and its own
        // ~8 kN of normal force is making 2.8 kN*m of nose-down moment against a save
        // worth 750 N*m — a quarter of what was toppling it.
        //
        // Same cap (3 g) and the same reasoning about what a rider can physically do: they
        // push against the bars in proportion to what is pushing back at them. Both
        // directions get it, because the nose-up half is the rear patch passing in FRONT
        // of the CoM and is the same equation mirrored.
        pitchTorque += (-sign(relPitch) * overP * R.pitchSaveTorque - s.pitchRate * R.pitchSaveDamping * into)
          * saveLoad * saveAuth;
      }
    } else {
      s.manual = 0;
      s.riderLat = damp(s.riderLat, 0, 4, dt);
    }

    // Aerodynamic drag — matters above ~10 m/s and sets the terminal speed on a fire road.
    const v = s.speed;
    if (v > 0.01) {
      const drag = 0.5 * 1.2 * B.dragArea * v * v;
      fx -= (s.vx / v) * drag;
      fz -= (s.vz / v) * drag;
    }
    fy -= 0.5 * 1.2 * B.dragArea * s.vy * Math.abs(s.vy) * 0.4;

    // --- Integrate translation, and the vertical is two masses ---------------------
    //
    // Horizontally the bike and the rider are one rigid 92 kg: a rider is stiffly coupled
    // fore/aft and laterally, and the slow part of that coupling is already `riderFore`
    // and `riderLat`. Vertically they are not, and pretending they were is what three
    // rounds of spring shaping were working around.
    //
    // `riderMass` hangs on the legs and arms; the rest is chassis, and only the chassis
    // is on the springs. The springs therefore carry whatever the legs *transmit*, not
    // the rider's own inertia — which is the entire reason a 170 mm bike absorbs a drop
    // that 170 mm of spring cannot. See the note in tuning.ts.
    //
    // `fy` already contains -(chassis + rider) * g, so the rider's share of gravity is
    // added back and the leg force subtracted; at rest legF is exactly riderMass * g and
    // both accelerations are zero, which is why sag and a parked bike are unchanged.
    const invM = 1 / B.mass;
    const R2 = Tuning.rider;
    const mR = R2.riderMass;
    const mC = Math.max(B.mass - mR, 1);
    const legF = this.legForce();
    // What the rider is actively putting through the bike, N, signed — the leg force
    // above or below the static hold. Still the same published quantity it always was;
    // it is now measured off the real load path rather than being the stand-in that
    // used to generate it.
    s.pumpLoad = finiteOr(legF - mR * G, 0);
    const aChassisY = (fy + mR * G - legF) / mC;
    const aRiderY = legF / mR - G;
    s.vx += fx * invM * dt;
    s.vy += aChassisY * dt;
    s.vz += fz * invM * dt;
    this.updateLegs(dt, aChassisY - aRiderY);

    // Guard rails: the gyroscopic and tire terms are quadratic and can run away.
    s.vx = clamp(finiteOr(s.vx, 0), -80, 80);
    s.vy = clamp(finiteOr(s.vy, 0), -90, 90);
    s.vz = clamp(finiteOr(s.vz, 0), -80, 80);

    s.x = finiteOr(s.x + s.vx * dt, s.x);
    s.y = finiteOr(s.y + s.vy * dt, s.y);
    s.z = finiteOr(s.z + s.vz * dt, s.z);

    // Positional backstop. What used to be here was the *only* thing stopping the chassis
    // posting through the heightfield, sitting at `chassisFloorFraction` and doing it by
    // teleport. The frame-vs-hillside contact near the top of `step` now owns that job as a
    // force, so this is what it always claimed to be and never was: a guard against one bad
    // step, not a load path.
    //
    // It sits `bellyBackstopSlack` below where the contact engages, so in ordinary use the
    // spring has a real stroke to work over and this line never fires — if a capture ever
    // shows the CoM sitting exactly on it again, the contact above is too soft, not this
    // number too shallow. There is no restitution: bouncing off a numerical guard is
    // energy from nowhere, which the old `-0.15` was.
    const groundHere = this.terrain.height(s.x, s.z);
    const minY = groundHere + B.comHeight * B.chassisFloorFraction - B.bellyBackstopSlack;
    if (s.y < minY) {
      s.y = minY;
      if (s.vy < 0) s.vy = 0;
    }

    // Integrate rotation.
    s.yawRate += (yawTorque / B.yawInertia) * dt;
    s.yawRate = clamp(finiteOr(s.yawRate, 0), -9, 9);
    s.yawRate = damp(s.yawRate, 0, grounded ? Tuning.bike.yawDamping : 0.3, dt);
    s.yaw = wrapPi(s.yaw + s.yawRate * dt);

    s.leanRate += (rollTorque / B.rollInertia) * dt;
    s.leanRate = clamp(finiteOr(s.leanRate, 0), -12, 12);
    s.lean = clamp(s.lean + s.leanRate * dt, -1.5, 1.5);

    s.pitchRate += (pitchTorque / B.pitchInertia) * dt;
    s.pitchRate = clamp(finiteOr(s.pitchRate, 0), -12, 12);
    if (grounded) s.pitchRate = damp(s.pitchRate, 0, Tuning.bike.pitchDamping, dt);
    s.pitch = clamp(s.pitch + s.pitchRate * dt, -1.4, 1.4);

    // Felt g: proper acceleration of the *system* centre of mass, i.e. everything except
    // gravity. Still exactly fy/m even with the vertical split, because the leg force is
    // internal: mC*aChassis + mR*aRider = fy + mR*g - legF + legF - mR*g = fy. So this
    // number means the same thing it always did, and it falls when the legs limit what
    // the springs have to make — which is the whole point.
    s.gForce = finiteOr(Math.hypot(fx, fy + B.mass * G, fz) / (B.mass * G), 1);

    this.updateImpact(dt);
    this.updateCrash(dt);
    this.updateHop(dt, input);
    if (!this.settling) {
      PEAK_G.observeAt(s.gForce, s.x, s.y, s.z);
      // Track the renderer's crash lift at sim rate. `damp` is an exact exponential, so
      // the continuous trajectory is the same one `bikeModel.ts` gets stepping it at
      // frame rate — the two only differ in where they sample it, which is what the
      // posed-bone probe checks. See CRASH_LIFT_IN.
      this.crashMix = damp(this.crashMix, s.crashed ? 1 : 0,
        s.crashed ? CRASH_LIFT_IN : CRASH_LIFT_OUT, dt);
      // The stand-down that used to live here is gone; `sinkBlindS` is 0 and this whole
      // branch exists so the historical blindness stays reproducible (see SINK_COVER).
      if (s.crashed) this.sinkBlind = Tuning.bike.sinkBlindS;
      else if (this.sinkBlind > 0) this.sinkBlind -= dt;
      // Publish the coverage before taking the measurement, so the two can never
      // disagree about which steps were counted. See SINK_COVER.
      const blind = this.sinkBlind > 0;
      SINK_COVER.observeAt(blind ? 1 : 0, s.x, s.y, s.z);
      if (!blind) this.measureSink();

      // Did the hop the rider just asked for survive its first 100 ms? `updateHop` above
      // has already fired this step's impulse if there was one, so the reference is
      // current. Only losses count — see HOP_LOSS.
      if (this.hopWatch > 0) {
        this.hopWatch -= dt;
        this.hopVyRef -= G * dt;
        const lost = (this.hopVyRef - s.vy) / this.hopVy0;
        HOP_LOSS.observeAt(lost > 0 ? lost : 0, s.x, s.y, s.z);
      }

      // Bottom-outs, as a rate over grounded riding and never as a max. See FULL_TRAVEL.
      if (grounded) {
        const S = Tuning.suspension;
        this.btGrounded++;
        if (s.front.travel >= S.frontTravel - 1e-4 || s.rear.travel >= S.rearTravel - 1e-4) this.btFull++;
        this.btAcc += dt;
        if (this.btGrounded > 240 && this.btAcc >= 0.5) {
          this.btAcc = 0;
          FULL_TRAVEL.observeAt(this.btFull / this.btGrounded, s.x, s.y, s.z);
        }
      }
    }
    // Both wheels have now sampled the ground once, so the next step has something to
    // difference the envelope height against. Set here rather than in solveWheel so the
    // front wheel cannot difference against a height the rear wrote this same step.
    this.prevGndValid = true;
  }

  // ---------------------------------------------------------------------------

  /**
   * How far the DRAWN wheels are inside the DRAWN ground, this step.
   *
   * This is the gate metric for this piece and it is deliberately NOT derived from
   * anything the solver believes. It re-implements `bikeModel.ts`'s pose maths — root at
   * `(s.x, s.y - comHeight, s.z)`, chassis rotated about that root, wheel bottom at
   * chassis `(drawX, travel - sagRef, 0)`, plus the disc's own dip under pitch — and
   * compares the result with `terrain.height` under it. `overTravel` cannot do this job:
   * it is the excess stroke measured against the ground the SOLVER sampled, so on every
   * frame where the two files disagree about where the bike is, it reads zero while the
   * wheel is half a metre under.
   *
   * Cost: one `terrain.height` per wheel per step (480 Hz total), and four more per wheel
   * only on a step that is already over budget. Measured against the ten `terrain.sample`
   * calls the wheel envelope already makes, that is noise.
   *
   * THE CRASH LIFT IS PART OF THE POSE, and round 11 is where that was admitted. While
   * `crashed` is held, `bikeModel.ts` raises the drawn root by `min(sink, 1.6 m)` blended
   * in at 13/s and out at 3.4/s, where its `sink` is the deepest of the two wheels
   * measured against the ground the SIM sampled. Round 10 answered that by standing this
   * function down for 1.2 s around a crash and publishing the hole as `sinkUnmeasured`;
   * measured across the delivered set the hole was a fifth of every run that crashes. The
   * lift is four lines of arithmetic and every input to it is already in this file, so
   * mirroring it is strictly better than not looking: the function now measures the frame
   * the player is shown for every step of every run, and where the lift genuinely removes
   * the burial it reads zero *because the burial is not on the screen*, which is a claim
   * the old stand-down could not make.
   *
   * That is why it is two passes over the wheels now. The lift is one number for the whole
   * bike — the deepest wheel decides it and both wheels ride up by it — so nothing can be
   * observed until both `lowY` are known.
   *
   * If this and `captures/ride-feel-tools/sink.mjs` (which reads the posed bones straight
   * out of the scene graph) ever disagree, the probe is right and this is stale.
   */
  private measureSink(): void {
    const s = this.state;
    const B = Tuning.bike;
    const S = Tuning.suspension;
    const cy = Math.cos(s.yaw);
    const sy = Math.sin(s.yaw);
    const cosP = Math.cos(s.pitch);
    const sinP = Math.sin(s.pitch);
    const cosLd = Math.cos(s.lean);
    const sinLd = Math.sin(s.lean);
    const dip = B.wheelRadius
      * (cosLd * cosP - Math.sqrt(Math.max(0, 1 - sinLd * sinLd * cosP * cosP)));
    const rootY = s.y - B.comHeight;
    const frontX = B.wheelbase - B.drawnChainstay;
    // Pass 1: where each wheel is drawn, before the crash lift.
    let simSink = 0;
    for (let i = 0; i < 2; i++) {
      const isFront = i === 0;
      const w = isFront ? s.front : s.rear;
      const t = w.travel - (isFront ? S.frontTravel : S.rearTravel) * S.sagFraction;
      // The fork carries the front wheel back as it compresses. See FORK_RAKE.
      const drawX = (isFront ? frontX - t * FORK_RAKE : frontX - B.wheelbase);
      const lowY = rootY + drawX * sinP + t * cosLd * cosP + dip;
      const fwd = drawX * cosP - t * cosLd * sinP;
      const lat = t * sinLd;
      sinkLowY[i] = lowY;
      sinkBX[i] = s.x + cy * fwd - sy * lat;
      sinkBZ[i] = s.z + sy * fwd + cy * lat;
      // The renderer's lift is measured against `w.ground.height` — the ground the SOLVER
      // sampled — and not against a fresh lookup. Mirrored exactly, or the two disagree by
      // the envelope's own soft-max on precisely the frames this is here to cover.
      const d = w.ground.height - lowY;
      if (d > simSink) simSink = d;
    }
    // The crash lift. `cm` is `bikeModel.ts`'s smoothstep of the same damped mix.
    const cm = this.crashMix < 1e-3
      ? 0 : this.crashMix * this.crashMix * (3 - 2 * this.crashMix);
    const lift = cm > 0 && simSink > 0 ? Math.min(simSink, CRASH_LIFT_MAX) * cm : 0;
    // Pass 2: what the ground under each drawn wheel says about it.
    for (let i = 0; i < 2; i++) {
      const lowY = sinkLowY[i] + lift;
      const bx = sinkBX[i];
      const bz = sinkBZ[i];
      let sink = this.terrain.height(bx, bz) - lowY;
      if (sink > SINK_BUDGET_MM * 0.001) {
        // Confirm before crying. A single analytic sample can land on the top of a cut
        // bank the wheel is merely beside; the SHALLOWEST reading over the wheel's own
        // footprint cannot. Only paid on a step that is already over budget, which after
        // this round is a fraction of a percent of them.
        const d = 0.30;
        const hx = cy * d;
        const hz = sy * d;
        const lx = -sy * d;
        const lz = cy * d;
        let h = this.terrain.height(bx + hx, bz + hz) - lowY;
        if (h < sink) sink = h;
        h = this.terrain.height(bx - hx, bz - hz) - lowY;
        if (h < sink) sink = h;
        h = this.terrain.height(bx + lx, bz + lz) - lowY;
        if (h < sink) sink = h;
        h = this.terrain.height(bx - lx, bz - lz) - lowY;
        if (h < sink) sink = h;
      }
      SINK.observeAt(sink * 1000, bx, lowY, bz);
    }
  }

  private updateRider(dt: number, input: ControlInput): void {
    const s = this.state;
    const R = Tuning.rider;
    const B = Tuning.bike;
    // A manual is the rider's whole body going behind the saddle as well as a pull on the
    // bars, so it commands weight shift too — otherwise the pitch torque is fighting a
    // CoM that never moved and the front wheel comes up only as long as the button is
    // held. Clamped together with the stick so the two cannot sum past the rider's reach.
    const manualCmd = clamp01(finiteOr(input.manual, 0));
    const foreCmd = clamp(clamp(input.weightShift, -1, 1) - manualCmd, -1, 1);
    this.playerFore = damp(this.playerFore, foreCmd * R.weightShiftRange, R.weightShiftRate, dt);

    // Automatic fore/aft bias — the rider doing the riding. A real rider never sits
    // still: they drive weight over the bars when they get on the gas and get behind
    // the saddle under the brakes, and that is most of why a real bike keeps both
    // wheels working through a transition that this model would otherwise ride on one.
    //
    // Driven from the tire longitudinal force alone, never from the slope. Gravity on a
    // descent transfers no load — it acts at the CoM — so a rider free-wheeling down a
    // fire road has no reason to move, and biasing off pitch attitude instead would have
    // them shuffling backwards on every roller for no reason. Last step's force, so the
    // loop is not closed inside a single step; heavily rate-limited and clamped, so the
    // negative feedback (moving back cuts front load cuts braking force) settles rather
    // than hunting.
    //
    // Plus the steepness term, which is the other half of the same rider and is not
    // optional. The front contact patch sits comHeight*sin(pitch) behind where a level
    // bike would put it, so on a pitch steeper than atan(frontOffset/comHeight) = 33
    // degrees the patch is *behind* the CoM and the front wheel's own normal force
    // pitches the bike further over — the bike is past the endo balance point from
    // terrain alone, before the rider has touched a brake. That is exactly why getting
    // behind the saddle on steeps is the first thing anyone is taught, and with the arms
    // resolved properly the model needs it for the same reason a rider does. On a climb
    // it runs the other way and keeps the front wheel down.
    const grounded = s.front.contact || s.rear.contact;
    const aLongG = this.tireLongPrev / (B.mass * G);
    const accelBias = clamp(aLongG / R.autoBiasSpan, -1, 1) * R.autoBiasRange;
    const steepBias = clamp(this.groundPitch / R.autoBiasSteepSpan, -1, 1) * R.autoBiasSteepRange;
    const autoTarget = grounded
      ? clamp(accelBias + steepBias, -R.weightShiftRange, R.weightShiftRange)
      : 0;
    this.autoFore = damp(this.autoFore, autoTarget, R.autoBiasRate, dt);
    // Third term: where a case threw them. Outside the two ordinary halves and outside
    // their clamp on purpose — being pitched at the bars by a hit the suspension could
    // not take is not a weight shift the rider chose, and its own decay in updateImpact
    // is what puts them back over the cranks.
    s.riderFore = clamp(
      this.playerFore + this.autoFore + this.caseFore,
      -R.weightShiftRange * 1.4, R.weightShiftRange * 1.4 + Tuning.impact.caseThrow,
    );

    // A lean is only holdable if the bike can turn tight enough to hold it, and the turn
    // radius is bounded below by steering lock: tan(lean) <= v^2 * steer / (g * wheelbase).
    // Below roughly 8 m/s that bound bites long before maxLean. Accepting a lean command
    // the bike physically cannot hold is what turns a corner that scrubs speed into a
    // lowside — the bike keeps falling in, the front runs out of slip angle trying to
    // catch it, and it goes down. Clamping the *command* costs nothing a rider would
    // notice and removes the whole failure mode.
    const holdable = Math.atan((s.speed * s.speed * Tuning.control.maxUsefulSteer) / (G * B.wheelbase));
    const maxLean = Math.min(R.maxLean, holdable);
    s.leanCommand = damp(s.leanCommand, clamp(input.lean, -1, 1) * maxLean, R.leanCommandRate, dt);
    s.leanCommand = clamp(s.leanCommand, -maxLean - 0.05, maxLean + 0.05);
  }

  /**
   * Brake levers to pad torque.
   *
   * Two things happen here and the bar asks for both. The curve makes the lever
   * modulatable: on a linear map the entire useful band between "slowing hard" and
   * "locked" is crammed into the top few percent of travel, so the brake is functionally
   * on/off however finely the player squeezes it. Above unity the map is progressive —
   * measured against brakeFrontMax, the front now locks at 96% of lever instead of about
   * 60%, and the whole first half is fine control.
   *
   * The lag is the pads actually taking up the lever. A step at the lever is a ramp at the
   * contact patch on any real bike (pad clearance, hose swell, tire carcass), and without
   * it a scripted or keyboard step arrives as the full 470 N*m in one 4 ms sim step,
   * which locks the wheel before an ounce of load has transferred forward. That is not a
   * hard stop, it is a skid, and it is why the front brake read as a switch.
   */
  private updateBrakes(dt: number, input: ControlInput): void {
    const C = Tuning.control;
    // The exponent is read from the table rather than being inlined. It was written as
    // `l * Math.sqrt(l)` — exactly l**1.5 — which meant `brakeCurve` documented a knob
    // that did nothing, and the one number that decides how much of the lever is useful
    // could not be swept. Two pow() calls at 240 Hz is 480 a second; the sweep it buys is
    // worth more than that.
    const shape = (lever: number): number => Math.pow(clamp01(finiteOr(lever, 0)), C.brakeCurve);
    const rate = 1 / Math.max(C.brakeRampTime, 1e-3);
    this.brakeAppliedFront = damp(this.brakeAppliedFront, shape(input.brakeFront) * C.brakeFrontMax, rate, dt);
    this.brakeAppliedRear = damp(this.brakeAppliedRear, shape(input.brakeRear) * C.brakeRearMax, rate, dt);
  }

  /**
   * The pump: the rider driving their own mass against the bike.
   *
   * This is the mechanic the bar calls the most satisfying one in the genre, and it was
   * simply absent — there was a `pumpGain` in the tuning table that nothing read. What
   * makes it work is not a speed bonus, it is the geometry of the ground reaction, so it
   * is worth writing down exactly, because a version that adds speed any other way reads
   * as a cheat the first time a player notices it.
   *
   * The ground pushes back along its *own normal*. For a surface descending in +x that
   * normal is (sin@, cos@) — it tilts *forward*. So any extra load the rider puts through
   * the contact patch on a backside has a forward component; on the face of the next
   * roller the normal tilts backward, so unloading there removes a retarding component.
   * Push down the backside, absorb over the face, and both halves of one leg stroke drive
   * you along the trail. That falls straight out of the model as long as the rider can
   * modulate contact load — which is the only thing added here — and it correctly nets
   * exactly zero on flat ground.
   *
   * It is modelled as a mass on the end of a finite leg stroke, not as a force the player
   * holds on, and that distinction is the whole reason it cannot be exploited. The load is
   * pumpMass * (acceleration of the leg coordinate), so extending and then stopping nets
   * zero impulse by construction: any speed gained has to come from the terrain's slope
   * changing sign between the push and the recovery. Holding the button does nothing at
   * all once the legs reach the end of their travel, and no amount of button-mashing on a
   * fire road produces a metre per second.
   *
   * One button, because on a real bike it is one movement. Held: press down, which is
   * both the pump and the preload before a lip. Released: extend, which is the pop — and
   * the hop impulse in updateHop rides on the same release. Whether that movement is a
   * pump, a preload or a bunny hop is decided entirely by where on the terrain the player
   * lets go, which is what "rewards timing" has to mean.
   */
  private updatePump(dt: number, input: ControlInput): void {
    const s = this.state;
    const C = Tuning.control;
    // Where the rider wants their body. Crouching into the bike while the button is held,
    // extending for a beat after it is released, neutral otherwise.
    this.popTimer = Math.max(0, this.popTimer - dt);
    const target = input.hop ? C.pumpRange : (this.popTimer > 0 ? -C.pumpRange : 0);

    // Pushing and unweighting are not the same movement and do not happen at the same
    // rate, and until round 9 this coordinate ran at one rate in both directions. That
    // symmetry is what stopped the bike leaving the ground.
    //
    // Extending into the bike is a muscle contraction against most of the rider's weight;
    // 110 1/s^2 puts a full stroke at ~0.15 s, which is right and is what sets the pump's
    // timing window. *Un*weighting is not a contraction at all — the rider stops pushing
    // and the leg unloads under its own return — and it is several times quicker. The
    // measurement that says this matters: released at full charge, the hop's impulse lands
    // on `s.vy` in one step while `pumpPos` is still at +0.96 of its range, so
    // `legForce`'s datum is still 135 mm below the leg coordinate and the legs are still
    // transmitting 1185 N. The chassis is 30 kg. That is 4.8 g pressing the frame back
    // into the hill for the whole of a flight that lasted 0.14 s, out of an impulse worth
    // 3.6 m/s and 0.66 m of air. Same rate in both directions = the rider is still doing
    // the preload while the bike is meant to be leaving.
    const unweighting = target < this.pumpPos;
    const kP = unweighting ? C.pumpRetractStiffness : C.pumpStiffness;
    const cP = unweighting ? C.pumpRetractDamping : C.pumpDamping;

    // Second-order, so the coordinate has a real acceleration to read. Solved
    // semi-implicitly for the same reason everything else here is: an explicit spring at
    // 110 rad^2/s^2 is fine at 240 Hz but there is no reason to leave it marginal.
    const accel = (target - this.pumpPos) * kP - this.pumpVel * cP;
    this.pumpVel = clamp(finiteOr(this.pumpVel + accel * dt, 0), -6, 6);
    this.pumpPos = clamp(finiteOr(this.pumpPos + this.pumpVel * dt, 0), -C.pumpRange * 1.2, C.pumpRange * 1.2);

    // Where the load comes from, and this changed in round 3.
    //
    // There used to be a `pumpMass * accel` force injected straight at the contact
    // patches. It was a stand-in for a rider the model did not have: nothing pushed *down*
    // on the chassis to balance it, so the ground reaction was a free upward force on the
    // frame. At 92 rigid kilograms that was tolerable. With the vertical split it is not —
    // the chassis alone is 30 kg, so the same stand-in shoved the bike three times as hard
    // and the pump inverted: measured on the roller bench, the round-2 +100 N*s became
    // -3 N*s and the mountain run lost 300 N*s of impulse against coasting.
    //
    // It is gone. The rider is a real mass on a real load path now, so a pump is what a
    // pump is: `pumpPos` moves the datum the legs hold about (see `legForce`), the legs
    // transmit that to the chassis, the chassis presses into the ground and the ground
    // reacts along its own normal. Every property the stand-in was built to fake now falls
    // out instead of being asserted — it nets zero on flat ground because the normal is
    // vertical there, and holding the button cannot be exploited because pushing the datum
    // down simply accelerates the rider up until the legs are back on it.
    //
    // Measured against round 2 on the three deterministic roller benches: distance gained
    // by pumping goes +9.0/-0.2/+17.1 m to +1.3/+13.9/+18.8 m, and on the mountain, where
    // the two runs diverge and the number is only a coarse signal, -4.0 m to +76.9 m.
    s.pump = this.pumpPos / C.pumpRange;
  }

  /**
   * The compressive force in the rider's legs and arms, N — the load path between
   * `riderMass` and the chassis, and therefore between the rider and the springs.
   *
   * This is the round-3 change. `bodySquash` used to be a decorative 28 kg mass driven
   * one way by frame acceleration: it moved for the animation and carried nothing, so the
   * springs had to stop the whole 92 kg inside 165 mm of frame stroke. Peak g on a
   * landing is bounded below by v^2/(2*d*g) and d was the frame's stroke and nothing
   * else, which is why round 2's twenty-four-point sweep could only trade time on the
   * bump stop against peak g: both are set by the same d, and no spring shape changes d.
   *
   * Three properties matter and each is a separate choice, not a fitted constant:
   *
   * - **It carries the static load at zero deflection.** The preload is exactly
   *   riderMass*g, so a parked bike, sag, brake dive and cornering are bit-for-bit what
   *   they were. The element only does anything once the load leaves the ordinary band.
   * - **It is soft.** 7800 N/m on 62 kg is a 1.8 Hz mode, an order below the springs, so
   *   above about 2 g the legs are what gives and the frame stroke follows the *leg*
   *   force rather than the rider's inertia. That is what stops an ordinary landing
   *   reaching the end of the stroke at all.
   * - **It is heavily damped and that damping is dissipative.** A rider absorbing a hit
   *   is working eccentrically; essentially none of it comes back. This is where the
   *   energy that used to be stored in the end of the fork stroke and handed straight
   *   back to the bike now goes.
   *
   * The legs cannot pull down — flat pedals — so it is clamped at zero, and in the air it
   * sits there with both masses simply falling together.
   */
  private legForce(): number {
    const s = this.state;
    const R = Tuning.rider;
    // The pump moves the datum the legs hold about, so a crouch and an extension are a
    // real preload as well as a visible pose. Sign: pumpPos positive is the rider
    // extended (pressing the bike down), which is the body *un*-squashed, so pushing the
    // datum negative raises the force the legs are transmitting.
    const datum = -this.pumpPos * R.pumpLegDatum;
    let f = R.riderMass * G
      + R.legStiffness * (s.bodySquash - datum)
      + R.legDamping * s.bodySquashVel;
    // Bone on bone. Quadratic so the rate is zero where it joins the linear part and
    // largest at the end of the crouch — no shelf for the coordinate to park on, and a
    // real bound on how deep a hit can drive the rider before the frame takes it back.
    const endFrom = R.bodyTravel - R.legEndSpan;
    if (s.bodySquash > endFrom) {
      const u = (s.bodySquash - endFrom) / Math.max(R.legEndSpan, 1e-3);
      f += R.legEndForce * u * u;
    }
    return clamp(finiteOr(f, R.riderMass * G), 0, R.legMaxForce);
  }

  /**
   * Integrate the leg coordinate. `relAccel` is the chassis's vertical acceleration minus
   * the rider's, which is by definition the second derivative of the compression.
   *
   * Hitting either end of the range zeroes the velocity into it rather than letting the
   * coordinate keep integrating against a clamp — a clamped position with a live velocity
   * is a spring that fires the moment the load comes off, and this one is between the
   * rider and the bike.
   */
  private updateLegs(dt: number, relAccel: number): void {
    const s = this.state;
    const R = Tuning.rider;
    s.bodySquashVel = clamp(finiteOr(s.bodySquashVel + relAccel * dt, 0), -8, 8);
    let d = s.bodySquash + s.bodySquashVel * dt;
    if (d > R.bodyTravel) { d = R.bodyTravel; if (s.bodySquashVel > 0) this.legStopHit(); }
    else if (d < -R.legExtend) { d = -R.legExtend; if (s.bodySquashVel < 0) this.legStopHit(); }
    s.bodySquash = finiteOr(d, 0);
  }

  /**
   * Reaching the end of the leg travel is a collision between the two masses, and until
   * round 9 the model deleted its momentum instead of resolving it.
   *
   * `bodySquashVel` is the *relative* velocity, vChassis - vRider; only `s.vy` and this
   * are states, so the rider's own velocity is `s.vy - bodySquashVel` and nothing else.
   * Zeroing the relative velocity on its own therefore does not stop the two masses, it
   * teleports the rider onto the chassis's velocity — 62 of the system's 92 kg changing
   * speed with no force anywhere and no equal and opposite anything. It is a momentum
   * leak, and it is worth naming which way it leaks: at the EXTENSION stop the rider is
   * always the one moving up faster (that is what put the legs there), so what is deleted
   * is exactly the upward momentum of the larger mass at the moment its legs go taut —
   * i.e. the bunny hop. On a bike, straight legs and arms with your body still travelling
   * up is *how the bike comes off the ground*: the link goes tight and the frame is
   * yanked up after you. The model was throwing that away every time.
   *
   * Resolved as what it is — a perfectly inelastic internal collision, both masses ending
   * at their common velocity `vC - (mR/M)*bodySquashVel`. Total vertical momentum is
   * conserved by construction, kinetic energy falls (bone on bone does not bounce), and
   * the sign works out on both ends: taut legs lift the frame, a bottomed crouch on a
   * landing slows it. Nothing else in the file changes, because below the stops the
   * coordinate never reaches either one.
   */
  private legStopHit(): void {
    const s = this.state;
    const share = (Tuning.rider.riderMass / Tuning.bike.mass) * Tuning.rider.legStopMomentum;
    s.vy = clamp(finiteOr(s.vy - share * s.bodySquashVel, s.vy), -90, 90);
    s.bodySquashVel = 0;
  }

  private updateSteering(dt: number, input: ControlInput): void {
    const s = this.state;
    const B = Tuning.bike;
    const C = Tuning.control;
    const R = Tuning.rider;

    // Self-steer. A leaned bike holds its line because trail turns the bars into the
    // lean until the path curvature matches: v^2 = g*R*tan(lean) with R = wheelbase/steer
    // gives steer = wheelbase*g*tan(lean)/v^2. Deriving it any other way (a constant
    // gain on lean, say) makes the steer angle speed-independent, which means a 50 km/h
    // corner gets the same 25 degrees of bar as a parking-lot turn, the tire saturates
    // instantly, and the bike snaps upright and oscillates until it is on its side.
    const vRef = Math.max(s.speed, C.steerSpeedFloor);
    // More trail means the front end finds that angle harder — which is the only reason
    // head angle is allowed to be a number in tuning at all.
    const casterAuth = R.selfSteerGain * (this.trail() / B.refTrail);
    // Capped at roughly twice peak slip angle: past that the front tire is already
    // sliding, so more caster angle only removes the margin the countersteer loop needs
    // to catch the bike, and the loop goes unstable exactly when it is needed most.
    const steerEq = clamp(
      (B.wheelbase * G * Math.tan(clamp(s.lean, -1.25, 1.25))) / (vRef * vRef) * casterAuth,
      -C.maxCasterSteer, C.maxCasterSteer,
    );

    // Countersteer: to lean right you first steer left. A direct roll torque from the
    // rider stalls at about half the commanded lean because the rider has nothing to
    // push against; the tires have to do it.
    //
    // Structured as a rate loop, not a raw PD on lean angle: lean error asks for a roll
    // *rate*, that rate is capped, and the bars close on it. Two failures this avoids.
    // A -leanRate damping term looks symmetric with the P term and is positive feedback
    // — rolling right it steers further left, which rolls you right harder. And an
    // uncapped P term saturates the bar against its stop for the whole roll-in, so the
    // bike blows through the commanded lean while still yawing the *other* way, never
    // develops centripetal force, and lies down. Damping a roll means steering into it,
    // and a rider rolls in at a rate, not as fast as physically possible.
    const leanError = s.leanCommand - s.lean;
    const csAuthority = 1 / (1 + s.speed * C.steerSpeedFalloff);
    const wantRollRate = clamp(leanError * R.leanRateGain, -R.maxRollRate, R.maxRollRate);
    const counter = clamp(
      -(wantRollRate - s.leanRate) * R.countersteerGain,
      -C.maxCountersteer, C.maxCountersteer,
    );
    let steerTarget = steerEq + counter * csAuthority;

    // Direct steering at walking pace, where leaning does nothing useful.
    const lowSpeedBlend = 1 - clamp01(s.speed / C.lowSpeedSteerSpeed);
    steerTarget = lerp(steerTarget, clamp(input.lean, -1, 1) * C.maxSteerAngle * 0.8, lowSpeedBlend);
    steerTarget = clamp(steerTarget, -C.maxSteerAngle, C.maxSteerAngle);

    const prev = s.steer;
    s.steer = damp(s.steer, steerTarget, C.steerRate, dt);
    s.steerRate = (s.steer - prev) / dt;
  }

  /** Mechanical trail, m — the reason head angle changes handling at all. */
  private trail(): number {
    const B = Tuning.bike;
    return (B.wheelRadius * Math.sin(B.headAngle) - B.forkOffset) / Math.cos(B.headAngle);
  }

  private solveWheel(
    dt: number,
    w: WheelState,
    res: WheelForces,
    offset: number,
    input: ControlInput,
    isFront: boolean,
    fwdX: number, fwdZ: number,
    rgtX: number, rgtZ: number,
  ): void {
    const s = this.state;
    const B = Tuning.bike;
    const S = Tuning.suspension;
    res.fx = 0; res.fy = 0; res.fz = 0;
    res.yawT = 0; res.rollT = 0; res.pitchT = 0;
    res.tireFx = 0; res.tireFz = 0;

    const travelMax = isFront ? S.frontTravel : S.rearTravel;
    const sagRef = travelMax * S.sagFraction;

    // Effective vertical arm from the CoM down to the patch. Pure rigid geometry would
    // drop the CoM by comHeight*(1-cos(lean)) — 310 mm at 45 degrees — and the frame
    // cannot fall that far that fast, so the suspension tops out mid-flick and the bike
    // corners on half its weight. A real rider does not drop that far either: the bike
    // leans under a body that stays much closer to upright. leanDropFraction is how much
    // of the geometric drop the combined CoM actually takes.
    const cosL = 1 - B.leanDropFraction * (1 - Math.cos(s.lean));
    const cosP = Math.cos(s.pitch);
    const sinP = Math.sin(s.pitch);

    // Contact patch position relative to the CoM, resolved once and used for everything:
    // where to sample the ground, and — crucially — the moment arms further down.
    //
    // The naive arm is "offset ahead, comHeight below", and it is wrong the moment the
    // bike is not level. The wheel hangs off the frame along the *body*-down axis, so
    // pitching the chassis swings the whole contact line fore/aft by armDown*sin(pitch)
    // and lengthens the vertical arm by offset*sin(pitch). On a 20 degree descent that
    // is 363 mm of fore/aft arm — half the wheelbase — and dropping it is not a small
    // error: it multiplied the front wheel's nose-up moment by 1.5 while cutting the
    // rear's nose-down moment to a fifth, so the chassis pitched nose-up until the fork
    // topped out. Measured on the corner run: the front tire carried 0 N for 46% of the
    // ride and the bike cornered on the rear wheel alone.
    //
    // With the full arms in, a free-rolling bike on *any* slope reproduces its static
    // load split exactly (the moment of each normal force about the CoM collapses to
    // load * offset, independent of slope). That is the correct answer and it is worth
    // stating plainly, because the intuition that gravity pulls weight onto the front
    // wheel of a descending bike is wrong: gravity acts at the CoM and makes no moment
    // about it. Load only transfers when a tire pushes.
    const leanLat = Math.sin(s.lean) * B.comHeight;
    const armDown = B.comHeight * cosL;
    const rLong = offset * cosP + armDown * sinP;
    const rUp = offset * sinP - armDown * cosP;
    const rLat = -leanLat;
    this.armUp = rUp;

    // --- ...and where the renderer DRAWS that wheel ------------------------------
    //
    // The arms above are the rigid-body geometry about the CoM and they are right — every
    // moment in this function is taken about the CoM and they are what the moments need.
    // They are NOT where the wheel appears on the screen, and until this round the
    // difference was nobody's number.
    //
    // `bikeModel.ts` (its header, rules 2 and 4) poses the bike about the *contact line*:
    //     root      = (s.x, s.y - comHeight, s.z), yawed
    //     chassis   = Rz(pitch) * Rx(lean), applied ABOUT THE ROOT
    //     wheel     = chassis-space (drawX, wheelRadius + travel - sagRef, 0)
    // so a pitched or leaned bike swings its wheels about a point on the ground, while
    // this function swings its contact patches about a point comHeight above it. The two
    // separate by up to `comHeight*sin(pitch)` fore/aft and `comHeight*sin(lean)`
    // laterally — 0.71 m and 0.47 m at the worst frame of the gate scenario. The wheel
    // solve was reading the height of a piece of hillside up to three quarters of a metre
    // from the wheel and holding the chassis at the height that ground asked for; the
    // wheel is drawn on the ground under it, and on a 40-70% face the gap between the two
    // is half a metre of dirt over the axle.
    //
    // `drawX` is the axle's fore/aft position in the renderer's chassis space, whose
    // origin is the bottom bracket rather than the CoM (see `bike.drawnChainstay`), and
    // it deliberately does NOT carry the rider's weight-shift term: `offset` moves the
    // wheels relative to the CoM when the rider shifts, and the renderer does not, so
    // mirroring the drawing means adding the shift back.
    const cosLd = Math.cos(s.lean);
    const sinLd = Math.sin(s.lean);
    // The wheel's own position in its stroke, as the renderer draws it (from sag, and
    // clamped — the renderer poses from `w.travel`, which is the clamped value).
    const tDraw = w.travel - sagRef;
    const drawX = offset + this.riderShift
      + (B.wheelbase - B.drawnChainstay) - B.wheelbase * B.rearWeightBias
      - (isFront ? tDraw * FORK_RAKE : 0);
    // A disc rotated about a point on its rim dips. Pitch is a rotation about the wheel's
    // own spin axis, so it does not tilt the disc — but it does carry the axle round the
    // contact line, and the lowest point of the wheel ends up `wheelRadius*(1-cos(pitch))`
    // below the rotated contact line: 35 mm at 25 degrees, 91 mm at 41. Lean is exact and
    // contributes nothing here. This term is an artefact of the renderer pivoting about
    // the contact line rather than about the CoM, and it is included because it is what
    // the player sees, not because it is good geometry. See the note in the file header.
    const dip = B.wheelRadius
      * (cosLd * cosP - Math.sqrt(Math.max(0, 1 - sinLd * sinLd * cosP * cosP)));
    const dLong = drawX * cosP - tDraw * cosLd * sinP;
    const dLat = tDraw * sinLd;

    const mix = B.drawnPoseBlend;
    const sLong = lerp(rLong, dLong, mix);
    const sLat = lerp(rLat, dLat, mix);
    const px = fwdX * sLong + rgtX * sLat;
    const pz = fwdZ * sLong + rgtZ * sLat;
    const ax = s.x + px;
    const az = s.z + pz;
    // How far the ground we are about to sample is from the wheel we are about to draw.
    // Zero by construction once `drawnPoseBlend` is 1; it is published anyway, because an
    // invariant that can only ever read zero is not an instrument — this one reads the
    // full defect at blend 0, which is how it was checked (see instrument_validation).
    POSE_GAP.observeAt(Math.hypot(sLong - dLong, sLat - dLat), ax, s.y, az);

    // Wheel envelope. A 29" wheel does not fall into a hole narrower than itself and
    // starts climbing an edge a hand's width before its axle reaches it. Sampling the
    // heightfield at a single point instead makes every cut batter and jump lip a step
    // change in travel: the damper answers with thousands of newtons in one 4 ms step
    // and the bike gets fired off the mountain. Three samples along the direction of
    // travel, each dropped by the wheel's own curvature, is the cheap version of the
    // right answer and it is the difference between a trail and a cheese grater.
    const g = this.terrain.sample(ax, az, w.ground);
    const R2 = B.wheelRadius * B.wheelRadius;
    envOff[0] = B.wheelRadius * B.envelopeSpan;
    envOff[1] = -envOff[0];
    envOff[2] = B.wheelRadius * B.envelopeSpanOuter;
    envOff[3] = -envOff[2];
    for (let i = 0; i < 4; i++) {
      envDrop[i] = B.wheelRadius - Math.sqrt(Math.max(0, R2 - envOff[i] * envOff[i]));
    }
    // Soft max, not max. A hard max hands the contact the winning probe's raw normal, and
    // which probe wins flips as the bike moves a millimetre — so the normal, and with it
    // the whole horizontal component of the ground reaction, steps by hundreds of newtons
    // between one 4 ms step and the next. Measured, a bike standing perfectly still on a
    // 7% slope buzzed at +-100 N with the rear reading half a friction circle of slip,
    // which is a bike that hums. A soft max over the same probes is the same envelope
    // wherever one probe genuinely dominates, and a blend wherever two are close.
    let wsum = 0;
    let hAcc = 0, nxAcc = 0, nyAcc = 0, nzAcc = 0;
    let best = g.height;
    for (let i = 0; i < 4; i++) {
      const e = this.terrain.sample(ax + fwdX * envOff[i], az + fwdZ * envOff[i], envS[i]);
      const h = e.height - envDrop[i];
      if (h > best) best = h;
    }
    if (best > g.height) {
      const inv = 1 / B.envelopeBlend;
      let wt = Math.exp((g.height - best) * inv);
      wsum = wt; hAcc = wt * g.height; nxAcc = wt * g.nx; nyAcc = wt * g.ny; nzAcc = wt * g.nz;
      for (let i = 0; i < 4; i++) {
        const h = envS[i].height - envDrop[i];
        wt = Math.exp((h - best) * inv);
        wsum += wt;
        hAcc += wt * h;
        nxAcc += wt * envS[i].nx; nyAcc += wt * envS[i].ny; nzAcc += wt * envS[i].nz;
      }
      const iw = 1 / wsum;
      g.height = hAcc * iw;
      const nl = Math.hypot(nxAcc, nyAcc, nzAcc) || 1;
      g.nx = nxAcc / nl; g.ny = nyAcc / nl; g.nz = nzAcc / nl;
    }
    const surf = SurfaceProps[g.surface];
    // The rate the ground under this wheel is actually rising or falling at, measured on
    // the surface the wheel actually stands on — i.e. *after* the envelope soft-max
    // above, which is the same surface `travel` is measured against ten lines below.
    //
    // See the long note at `vGround` for why this is a finite difference and not the
    // analytic gradient it replaces. Differencing the ground height is safe in the way
    // differencing `w.travel` would not be: `travel` is clamped to the stroke and is run
    // down by `topOutRate` while the wheel is airborne, so its delta is meaningless on the
    // step of first contact — the ground's own height has neither problem and is sampled
    // every step whether the wheel is touching or not.
    const prevH = isFront ? this.prevGndF : this.prevGndR;
    const gndRate = this.prevGndValid ? clamp((g.height - prevH) / dt, -14, 14) : 0;
    if (isFront) this.prevGndF = g.height; else this.prevGndR = g.height;
    // Publish the patch before solving the tire — the stiction anchor measures against
    // it, and feeding it last frame's position makes the bristle stretch by a whole
    // step of travel every step.
    w.cx = ax;
    w.cy = g.height;
    w.cz = az;

    // Suspension travel is kinematic, not integrated: at compression T the CoM rides
    // (comHeight + sag - T) above the patch, so T follows straight out of where the frame
    // is relative to the ground under each wheel. Integrating travel from a penetration
    // that itself already contains travel is a feedback loop with gain two — it slams to
    // fully bottomed or fully topped in a single step, which is why nothing had sag.
    const drop = (s.y + offset * sinP - g.height) / Math.max(cosL * cosP, 0.25);
    // The same statement written against the pose the renderer actually draws, which is
    // the second half of this round's fix and the half that removes the burial that has
    // nothing to do with the suspension at all.
    //
    // Inverting bikeModel's own equation for the wheel's lowest point,
    //     lowY = (s.y - comHeight) + drawX*sin(pitch) + T*cos(lean)*cos(pitch) + dip
    // and asking what T puts that point exactly on the ground gives the line below. It is
    // the same shape as `drop` above with two differences that matter:
    //
    //  - `comHeight` is INSIDE the projection there and outside it here. That single
    //    placement is worth `comHeight*(1 - cosL*cosP)` of ride height — 100 mm at 25
    //    degrees of pitch, 175 mm at 25 pitch and 30 lean, 314 mm at 41. Under the old
    //    form a bike whose CoM was exactly `comHeight` above the ground read the fork as
    //    TOPPED OUT the moment it leaned, so the spring stopped holding it up and it sank
    //    until something else did.
    //  - `cosL` is `leanDropFraction`'s half-drop, which belongs in the moment arms and
    //    does not belong here: the renderer leans the bike rigidly about the contact line,
    //    where a leaned wheel's contact point does not move down at all.
    const dropDrawn = (B.comHeight - dip - (s.y - g.height) - drawX * sinP)
      / Math.max(cosLd * cosP, 0.25);
    // Total closure of the whole stack between the chassis and the dirt. The frame's own
    // stroke is this minus whatever the tire gives, because the casing is a spring in
    // series with the fork and until round 3 this model said it was a rigid link.
    //
    // Solved with the previous step's load rather than iterated. The loop gain is
    // (frame rate)/(tire rate) — at most 50/118 kN/m even deep in the end-stroke ramp —
    // so a one-step lag at 240 Hz converges monotonically, and the alternative is a root
    // find inside the hottest function in the file for a 4 ms difference.
    //
    // `dropDrawn` is the FRAME's stroke on its own (the renderer has no tire compliance —
    // it poses the wheel from `travel`), where the rigid form is the whole stack, so the
    // casing has to be added back in rather than subtracted. `closureProv` exists only to
    // feed the tire's own sanity clamp, which needs a closure before there is one.
    const closureRigid = B.comHeight + sagRef - drop;
    const closureDrawn = sagRef + dropDrawn;
    const closureProv = lerp(closureRigid, closureDrawn, mix);
    // Never more than the closure itself: the casing cannot give more than the ground has
    // moved. Without that clamp a fast unload drives `travel` negative while the tire is
    // still on the dirt, the wheel is declared airborne, `load` goes to zero, the casing
    // un-deflects and it is back in contact one step later — a 240 Hz contact chatter.
    // Contact is therefore decided by the closure, and the frame's stroke by what is left.
    // ...and never more than half of it, which is insurance rather than physics. The
    // casing is modelled as compliance, not as a force source, so a step where a stale
    // load pushed the deflection all the way to the closure would leave the frame topped
    // out at zero force with the tire silently carrying everything. Half the closure is
    // orders away from anything ordinary riding reaches — at static load the casing gives
    // 3.4 mm against 41 mm of sag — and it bounds that case to nothing.
    const tireDef = Math.min(
      clamp(w.load / (isFront ? S.frontTireRate : S.rearTireRate), 0, S.tireMaxDeflect),
      Math.max(closureProv, 0) * 0.5,
    );
    const closure = lerp(closureRigid, closureDrawn + tireDef, mix);
    let travel = closure - tireDef;
    // Anything past full travel is not suspension any more, it is the mountain.
    const overTravel = Math.max(0, travel - travelMax);

    if (closure > 0) {
      w.contact = true;
      travel = Math.min(travel, travelMax);
      w.overTravel = overTravel;
      // Shaft speed is the closing rate between the chassis and the ground, and both
      // halves of that have to be measured against the *same* ground. Until this round
      // they were not, and that mismatch was the largest phantom force in the file.
      //
      // The chassis half is analytic and exact: `s.vy + offset*cosP*pitchRate` is how fast
      // the wheel's mounting point is moving down. The ground half used to be the analytic
      // gradient of the heightfield read off the contact normal,
      //     vGround = -(nx*vx + nz*vz) / max(ny, 0.2),
      // which is the rate of the **raw** surface — while `travel` twenty lines above is
      // measured against the **wheel envelope**, the soft-max of five probes dropped by
      // the wheel's own curvature. Those are different surfaces, and they are different
      // by construction exactly where the envelope earns its keep: at a step or a steep
      // facet the raw gradient is what the envelope is there to stop the wheel from
      // following.
      //
      // Two things made the gap enormous rather than academic. The gradient goes as 1/ny,
      // so a 70-degree facet under one probe multiplies it by three; and it is
      // proportional to forward speed, so it grows with the thing it is supposed to
      // damp. Measured over the scenario set, `travelVel` sat pinned at the +-8 m/s guard
      // for 20-26 consecutive steps — a tenth of a second — while the wheel's real stroke
      // rate over the same steps was between -4 and +2 m/s, and on many of them the wheel
      // was *extending* while the compression circuit was being asked for 2.37 kN. p90 of
      // the excess was 5.4 m/s on braking/coast/holdline and 5.3 on lowspeed; the worst
      // was the whole 8 m/s clamp. That phantom kilonewton drives the wheel further into
      // the stroke, which is self-reinforcing, and it is most of why a bike rolling at
      // 15-20 km/h with no air at all read 8.5-10 g with the front wheel 100-330 mm past
      // full travel in five different scenarios.
      //
      // `gndRate` above is the measured rate of the envelope, so `travelVel` is now the
      // exact derivative of the kinematic `travel` the spring reads:
      //     d(travel)/dt = (gndRate - vy - offset*cosP*pitchRate) / (cosL*cosP)
      // which is this expression. Position and velocity now come off one surface.
      //
      // The old note here argued the analytic form was needed because differencing gives
      // "the whole stroke divided by 4 ms" on the step of first contact. That is true of
      // differencing `travel` — which is clamped and is decayed by `topOutRate` in the
      // air — and it is not true of differencing the ground, which is continuous through
      // touchdown. The landing spike it was guarding against comes from the chassis term,
      // which is still analytic and still exact.
      const vGround = this.prevGndValid
        ? gndRate
        : -(g.nx * s.vx + g.nz * s.vz) / Math.max(g.ny, 0.2);
      // Same arm and same projection the position uses, or the damper is once again being
      // driven by a velocity the wheel never had — round 4's finding, and the largest
      // phantom force this file has ever carried. `drawX` and `cosLd` are what `travel` is
      // now measured with, so they are what its derivative has to be measured with.
      const closing = s.vy + lerp(offset, drawX, mix) * cosP * s.pitchRate - vGround;
      w.travelVel = clamp(finiteOr(
        -closing / Math.max(lerp(cosL, cosLd, mix) * cosP, 0.25), 0), -8, 8);
      w.travel = travel;

      // Spring-like and damping terms are now accumulated apart from each other, and that
      // bookkeeping is the whole of this round's fix. `rebLoadFloor` below is a statement
      // about their *ratio* — a damper may not cancel more of the spring than the tire can
      // afford to lose — and it cannot be written at all while the two are one running
      // total. See the long note at the floor itself.
      // Main spring — nearly linear, because this is the part small bumps ride on.
      const x = travel / travelMax;
      const rate = (isFront ? S.frontRate : S.rearRate) * lerp(1, isFront ? S.frontProgression : S.rearProgression, x * x);
      let spring = rate * travel;

      // Mid-stroke support — the hump an air spring's negative chamber and a volume spacer
      // put between sag and the end of the stroke.
      //
      // The capture is what asks for this. A histogram of grounded frames by travel decile
      // was bimodal: a quarter of the time in the first tenth of the stroke, a quarter in
      // the third and fourth, then almost nothing through the whole upper half — and then
      // 14% of frames *bottomed*. More frames at full travel than in the entire top half
      // of the stroke is the signature of a spring that is too soft in the middle, where a
      // hit blows straight through the region that should have caught it and arrives at
      // the stop instead.
      //
      // It shipped at a third of its current size and the trace says why that was not
      // enough, in a way the histogram alone does not: on this mountain the *rear wheel
      // carries the whole bike on its own* for seconds at a time — the ground pitches down
      // faster than the chassis does, the front hangs, and the rear is asked for 3.5-4.5 kN
      // quasi-statically, at one g. The old curve topped out at 2.8 kN across the entire
      // stroke, so the only answer available to "carry 4 kN" was to sit on the stop until
      // the ground let go. It was never mainly a landing problem, and no amount of
      // end-stroke ramp addresses it, because a ramp is only there at the end.
      //
      // It is smoothstepped in and out, so rate is continuous at both ends: no shelf where
      // it starts and, more importantly, none where it stops — the point of a plateau is
      // that it adds force in the middle *without* adding it at full travel, where more
      // force is only more peak g. And it carries hysteresis for the same reason the ramp
      // does: a plain spring here would store the energy and hand it back, which showed up
      // on the drop bench as restitution climbing from 0.29 to 0.49 the moment a
      // conservative version of this term was added.
      const supF = isFront ? S.frontSupport : S.rearSupport;
      if (supF > 0 && x > S.supportStart) {
        const u = clamp01((x - S.supportStart) / Math.max(S.supportEnd - S.supportStart, 1e-3));
        const sHys = lerp(S.supportHysteresis, 1, clamp01(w.travelVel * 10 + 0.5));
        spring += supF * u * u * (3 - 2 * u) * sHys;
      }

      // Split damping: low-speed controls body motion, high-speed swallows square edges.
      const sv = w.travelVel;
      const comp = sv > 0;
      const lo = comp ? (isFront ? S.frontCompLow : S.rearCompLow) : (isFront ? S.frontRebLow : S.rearRebLow);
      const hi = comp ? (isFront ? S.frontCompHigh : S.rearCompHigh) : (isFront ? S.frontRebHigh : S.rearRebHigh);
      const knee = S.kneeVelocity;
      // Blow-off. Past this shaft speed the shim stack is open and damping force stops
      // growing, exactly as it does on real hardware — and the reason to model it is not
      // fidelity, it is that without it the damper is the largest single force in the
      // file on precisely the hits it is least able to help. Measured before it existed:
      // a wheel arriving at the 8 m/s velocity clamp made 1.6 kN of high-speed linear
      // damping and 3.7 kN of quadratic on top of 3.8 kN of spring, pinned the wheel
      // guard, and read 27 g at the CoM. A damper that keeps growing with velocity turns a
      // fast hit into a wall, which is the thing this whole round is about.
      // Compression and rebound blow off at different shaft speeds, and they have to,
      // because they are being asked for opposite things. Compression must stop growing
      // early or the damper becomes the peak force on a fast hit; rebound must keep
      // authority to much higher speeds, because holding the wheel down after a deep
      // stroke is the entire anti-pogo mechanism and a rebound circuit that blows off at
      // the same 2 m/s hands the landing straight back. Measured: sharing one blow-off
      // speed took rebound damping from 1.3 kN to 0.73 kN and put the kick-back rate back
      // up by 18 points in a single capture.
      const absv = Math.min(Math.abs(sv), sv > 0 ? S.blowoffSpeed : S.blowoffSpeedReb);
      // Position-sensitive compression bleed. The compression circuit fades toward
      // `compDeepFade` over the last part of the stroke; rebound is untouched.
      //
      // This is the flattening term. The whole of this round is the observation that peak
      // load is set by how flat the force profile is, and by the time the wheel is at the
      // end of its stroke the spring is at its largest — so a damper that is still making
      // its full blow-off force *there* is adding its kilonewton to the one place the
      // profile is already highest, having spent itself where the spring was small. Real
      // hardware does this too, and for the same reason: a tapered bleed or a
      // position-sensitive compression circuit that opens deep in the stroke.
      //
      // Measured on the drop bench: the front's peak at a 5.3 m/s touchdown was 1666 N of
      // spring, 300 of ramp, ~180 of rider stage and 1084 of damper — the damper was a
      // third of it, at 170 mm of travel where it could no longer prevent anything. Fading
      // it there and leaving it whole at the top of the stroke, where it is the only force
      // the wheel has, is worth about a g and costs nothing at first contact.
      const compFade = comp
        ? lerp(1, S.compDeepFade, clamp01((x - S.compFadeStart) / Math.max(1 - S.compFadeStart, 1e-3)))
        : 1;
      let damping = (absv <= knee ? lo * sv : sign(sv) * (lo * knee + hi * (absv - knee))) * compFade;

      // High-speed compression, quadratic past the knee and applied across the *whole*
      // stroke rather than only deep in it.
      //
      // This is the term that lowers peak g, and where it acts matters as much as how big
      // it is. Peak load on a landing is set by how flat the force profile is across the
      // stroke: a spring puts all of its force at the end, so the peak is the whole hit,
      // and no amount of end-stroke ramp fixes that — an end-stroke ramp makes the peak
      // *worse*, it only moves the bottom-out further away. A damper's force does not
      // depend on position at all, so it is flat by construction, and being quadratic it
      // is worth 0.2 N at the 0.3 m/s shaft speeds ordinary chatter runs at and a
      // kilonewton at the 4.5 m/s a chute at 65 km/h produces. That is exactly the "small
      // bumps disappear, big hits use travel" split the bar asks for, and no spring shape
      // can produce it.
      if (sv > knee) {
        const ex = absv - knee;
        damping += (isFront ? S.frontCompQuad : S.rearCompQuad) * ex * ex * compFade;
      }

      // --- End-stroke ramp, then the rider ---------------------------------------
      // What used to be here was a bumper, a second spring stage, and a hard 4200 N
      // ceiling that all of them ran into; tuning.ts has the long version of why that was
      // the wrong tool. The short version: a clamp leaves the last fifth of the stroke
      // completely unresisted, so 78% of wave-1's landings reached full travel — and
      // because a clamp truncates the compression branch far harder than the rebound
      // branch (compression is where the damper adds, rebound is where it subtracts) it
      // silently deleted most of the hysteresis loop, which is the energy the suspension
      // is supposed to eat. A 5.7 m/s touchdown left the ground again at 3.4 m/s.
      //
      // Two segments, because there really are two things: the fork/shock running out of
      // its own stroke, and then the rider's legs and arms. They are separate springs at
      // very different rates and pretending they are one curve gets the knee wrong in
      // both directions at once.
      const rampFrom = S.rampStart * travelMax;
      const rampSpan = travelMax - rampFrom;
      let hard = 0;
      // How deep into the whole ramp-plus-bump-stop region this wheel is, 0..1. The damping
      // reads this rather than either segment so it stays continuous across the knee.
      const depth = clamp01((travel + overTravel - rampFrom) / (rampSpan + S.stopTravel));
      if (depth > 0) {
        // Hysteresis, blended smoothly across zero shaft speed rather than switched on
        // sign(sv): a force that steps when the damper changes direction is a 240 Hz
        // square wave in the one place the model can least afford one.
        const hys = lerp(S.rampHysteresis, 1, clamp01(sv * 10 + 0.5));

        // Segment 1: the stroke running out. Quadratic in how far into the last third the
        // wheel is, so force *and* rate are continuous where the ramp starts — the player
        // feels the stroke getting firmer, not a shelf — while the rate reaches ~50 kN/m
        // by full travel, which is what makes bottoming rare instead of routine.
        if (travel > rampFrom) {
          const u = (travel - rampFrom) / rampSpan;
          spring += (isFront ? S.frontRampForce : S.rearRampForce) * u * u * hys;
        }

        // Segment 2: the bump stop. This was "the rider" for two rounds and it is not the
        // rider — the rider is 62 kg on their own load path now (`legForce`), above the
        // bike rather than below the wheel. What is left here is the elastomer bumper and
        // then whatever else in the frame, wheel and casing deforms before the axle is
        // against the hillside, which is a genuinely different thing and is sized like one.
        //
        // That reclassification is what let it be re-sized honestly, and the size is the
        // point. As "the rider" it had to be soft and 580 mm long, because it was standing
        // in for a body absorbing a landing. Travel here is kinematic, so every one of
        // those millimetres was a millimetre the chassis sat below where the renderer draws
        // the wheel: the capture set had the bike 300-450 mm inside the hillside on its
        // worst hits. As a bumper it is 220 mm, firm, and the deepest excursion on the
        // plunge bench is 15 mm.
        //
        // (a) The plateau. A bumper takes load almost at once and then squashes at roughly
        //     constant force until it is solid — `1-(1-v)^2` over `stopSatTravel`, which at
        //     60 mm puts 2.0 kN under the front wheel where the frame stroke runs out. The
        //     scale it saturates over is what makes it a bumper rather than a mattress: at
        //     the 180 mm it had when this was the rider, the same term made 98 N there, and
        //     that single number is why wheels used to sink half a metre past full travel
        //     while `travel` read travelMax for every millisecond of it.
        //
        // (b) Going solid. Quadratic in how far past the frame stroke the wheel is, so the
        //     rate is zero where it joins the plateau and largest where it hands over to
        //     the hillside — no shelf at either end, and the whole profile from sag to the
        //     mountain is monotone with nowhere for a wheel to park.
        //
        // It starts *before* the frame runs out, at 0.88 of the stroke, because that is
        // where a bumper starts to touch.
        const stopFrom = S.stopStart * travelMax;
        const stopSpan = S.stopTravel + (travelMax - stopFrom);
        const stopInto = travel + overTravel - stopFrom;
        if (stopInto > 0) {
          const sat = Math.max(S.stopSatTravel, 1e-3);
          const inv = 1 - Math.min(stopInto, sat) / sat;
          const q = Math.min(stopInto, stopSpan) / stopSpan;
          spring += ((isFront ? S.frontStopForce : S.rearStopForce) * (1 - inv * inv)
            + (isFront ? S.frontStopEnd : S.rearStopEnd) * q * q) * hys;
        }
        // Published bottom-out stays what it always meant — how far past *full frame
        // travel* the wheel is — because VFX and audio read it as "the fork clanked", and
        // the rider stage now starting earlier must not make that fire on every roller.
        if (overTravel > 0) {
          this.events.bottomOut = Math.max(this.events.bottomOut, Math.min(overTravel, S.stopTravel) / S.stopTravel);
        }

        // Deep-stroke *rebound* damping, and only rebound. This is the anti-pogo term:
        // coming back out of a deep stroke the spring force is large and all of it is
        // stored energy, so without a rebound circuit sized against it the suspension
        // hands the whole landing back and fires the bike off the ground again. A real
        // fork does exactly this — a hard bottom-out comes back slowly and deliberately.
        //
        // There is deliberately no compression partner to it. A deep quadratic
        // compression damper was here for one round and it was the single largest force
        // in the file: at the 8 m/s velocity clamp it made 9.6 kN on its own, on top of a
        // whole-stroke quadratic damper doing the same job better. Two dampers cannot both
        // own the fast-hit case.
        //
        // It is much smaller than it was (4300/4700) and the floor below is why. At those
        // numbers this term was not damping the rebound, it was *deleting* it: 1.2 kN*s/m
        // at a 2 m/s extension is 2.4 kN against a 2 kN spring, so the whole wheel force
        // clamped to zero and stayed there for the length of the recovery. That reads as
        // anti-pogo — a wheel making no force cannot launch anything — and it is the same
        // mechanism as having no suspension at all.
        //
        // And it fades out once the wheel is past the rider's own stroke, because out
        // there it is not a suspension any more. `overTravel > stopTravel` means the axle
        // is inside the hillside, and a rebound damper's job — holding the wheel down so
        // the chassis does not get launched — is meaningless when the wheel is buried: all
        // it does is hold it buried. Measured on the capture: a bike stopped nose-up at
        // 1 km/h with the rear axle a metre inside the hill extracted at 0.3 m/s and read
        // full travel for 0.67 s, with the wheel pinned at the rebound floor the whole
        // time. The spring stack out there is 6 kN and `rampHysteresis` already eats over
        // half of it on the way back, so nothing is being handed back by letting it go.
        if (sv < 0) {
          const buried = clamp01(1 - (overTravel - S.stopTravel) / S.stopTravel);
          damping += (isFront ? S.frontRampRebDamping : S.rearRampRebDamping) * depth * depth * buried * sv;
        }

        // Past the bumper's own slack it is a cut bank: the axle is where the hillside is.
        // That contact has essentially no restitution — mountains do not bounce — so it
        // gets enough force to stop the closing motion and, crucially, a rebound term big
        // enough to cancel the spring on the way back out. Leaving it to the progressive
        // spring alone stores the whole impact and returns it: the bike hits the bank at
        // 7 m/s, stops dead, and is fired four metres straight up from a standstill.
        //
        // ROUND 11: this used to engage at `stopTravel` — 400 mm past full travel — and
        // that single number was the whole of the carried burial gap. Travel is kinematic,
        // so 400 mm of bumper is 400 mm the chassis may sit below the ground the renderer
        // draws the wheel on, against a 50 mm sink budget declared in this same file. The
        // deterministic bench measured the consequence exactly: a 0.5 m ledge at 38 km/h
        // put the drawn front wheel 270 mm inside the hillside and the bike rode away.
        // `hillsideStart` is that engagement point, and the reason it can be moved now
        // when the tuning note for `stopTravel` says it could not is that the two things
        // that number was doing have been separated — the CASE SEVERITY still spans
        // `stopTravel` (see `updateImpact`), so a terrain step is scored exactly as it was
        // and does not become an instant crash, while the GEOMETRY is bounded here.
        const wall = overTravel - S.hillsideStart;
        if (wall > 0) {
          hard = S.hardContactSpring * Math.min(wall, S.hardContactSpan)
            + (sv > 0 ? S.hardContactDamping : S.hardContactRebound) * sv;
          this.events.bottomOut = 1;
        }
      }

      // --- The rebound floor -----------------------------------------------------
      // A damper cannot pull a tire off the ground, and until this line the model let it.
      //
      // Travel here is kinematic — it is wherever the chassis happens to be relative to
      // the ground — so after a hard landing throws the chassis back up, the solver reads
      // a 2 m/s extension and asks the rebound circuit for the force that goes with it.
      // On a real fork that question is the wrong way round: the fork extends *because*
      // its own spring drives the wheel down, so the fastest it can extend is the speed at
      // which the damping force equals the spring force, and past that the wheel simply
      // falls behind the chassis. It never ends up being pulled *up* into the frame with
      // the tire still on the dirt.
      //
      // Letting it was the single mechanism behind everything this round was sent back
      // for. Measured on the descend line: a landing at 12.1 s put both wheels on the stop
      // and then read **zero load at both contact patches for 100 ms while both wheels sat
      // pinned at full travel** — the damper was cancelling the entire spring, so nothing
      // pushed the chassis back out of the stroke and nothing held the tires down. On
      // holdline the same rebound off one landing rolled the bike from +22 to -58 degrees
      // against a steady +29 degree lean command, because for a third of a second there
      // was no normal force for a tire to make a cornering force from and every rider term
      // in this file is scaled by load.
      //
      // The floor is one number: the damping may never cancel more than (1 - rebLoadFloor)
      // of the spring-like force at that position. The wheel therefore always carries a
      // real, position-proportional load while it is touching, and the chassis always has
      // something pushing it back out of the stroke.
      if (damping < 0) damping = Math.max(damping, -spring * (1 - S.rebLoadFloor));

      // There used to be a third term here: `pumpMass * accel` injected at the patch as a
      // stand-in for a rider pressing the bike into the ground. The rider is a real mass on
      // a real load path now (see `legForce` and the vertical split in `step`), so a pump
      // arrives through the springs like every other load the rider makes, and the
      // stand-in — which pushed the frame up with nothing pushing it down — is gone. See
      // `updatePump` for the measurement that made keeping it impossible.

      // Guards, not shaping. These sit far outside the ramp on purpose (see tuning.ts):
      // a wheel pinned at either of them means the ramp is wrong, not that the guard is
      // doing its job.
      let force = clamp(finiteOr(spring + damping, 0), 0, S.maxNormalForce);
      // One ceiling for the wheel, not one per term. Clamping the spring and the hillside
      // separately let a single wheel make both at once — 9600 N through one contact
      // patch, 31 g at the CoM with both wheels down, which the harness read as a hit
      // that took 13 km/h off the bike and the player would read as hitting a wall.
      hard = clamp(finiteOr(hard, 0), 0, Math.max(0, S.maxWheelForce - force));
      w.load = force;

      // Normal force along the ground normal. The hard-contact part shoves the CoM but
      // deliberately generates no moment: a wall strike normal is almost horizontal, and
      // running 13 kN of it through a metre of moment arm flips the bike every time the
      // front wheel touches a cut bank. What actually happens is that you stop.
      const nfx = g.nx * force;
      const nfy = g.ny * force;
      const nfz = g.nz * force;
      res.fx += nfx + g.nx * hard;
      res.fy += nfy + g.ny * hard;
      res.fz += nfz + g.nz * hard;

      // Weather owns the global wet-grip loss and says so in its own contract; the
      // surface table is the per-material part on top of it. Without this the rain looks
      // wet and rides dry, which is the one thing about weather a rider would notice.
      this.solveTire(dt, w, isFront, input, g, surf.grip * Weather.gripMultiplier, surf.roll, fwdX, fwdZ, rgtX, rgtZ, res);
      this.tireLongStep += res.tireFx * fwdX + res.tireFz * fwdZ;

      // Moments about the CoM from what this contact patch applies, hard contact excluded
      // (see above), using the full arms resolved at the top of this function.
      const mfx = res.fx - g.nx * hard;
      const mfy = res.fy - g.ny * hard;
      const mfz = res.fz - g.nz * hard;
      const fLatW = mfx * rgtX + mfz * rgtZ;
      const fLongW = mfx * fwdX + mfz * fwdZ;

      // Anti-squat / anti-rise / anti-dive. On a real bike the drive and brake reactions
      // do not run through the CoM: chain tension pulls on the swingarm and the brake
      // torque is taken by the caliper mount, so their line of action passes well above
      // the contact patch and only part of the couple ever reaches the springs. Without
      // it the geometry is brutally literal — full pedal is exactly the wheelie limit
      // (a = g*b/h = 0.51 g), so every time the rider gets on the gas the fork tops out
      // and the front tire stops existing, which is most of what the harness was seeing
      // during the run-up phase of every scenario.
      //
      // Only the *tire's* longitudinal force is routed this way. The horizontal
      // component of the normal force is not a linkage load and must keep its full arm,
      // or the slope-independence established above is thrown away again.
      //
      // Front anti-dive is deliberately small. It is a telescopic fork: it dives, and
      // that dive is how the player reads how close the front brake is to a stoppie.
      const tLongW = res.tireFx * fwdX + res.tireFz * fwdZ;
      const anti = isFront
        ? (tLongW < 0 ? B.antiDive : 0)
        : (tLongW > 0 ? B.antiSquat : B.antiRise);
      const fLongPitch = fLongW - tLongW * anti;

      res.rollT += -rLat * mfy + rUp * fLatW;
      res.pitchT += rLong * mfy - rUp * fLongPitch;
      // Rigid arms, like the roll and pitch moments above — `px`/`pz` are where the wheel
      // is DRAWN, which is the right place to read the ground and the wrong place to take
      // a moment about the CoM from.
      const mxw = fwdX * rLong + rgtX * rLat;
      const mzw = fwdZ * rLong + rgtZ * rLat;
      res.yawT += mxw * mfz - mzw * mfx;
    } else {
      w.contact = false;
      w.load = 0;
      w.overTravel = 0;
      w.anchored = false;
      // Topping out: the spring extends the wheel back down.
      const extendRate = S.topOutRate;
      const next = Math.max(0, w.travel - extendRate * dt);
      w.travelVel = (next - w.travel) / dt;
      w.travel = next;
      w.slipUsage = 0;
      w.slipRatio = 0;
      w.slipAngle = 0;
      // A wheel in the air keeps its speed — bearing drag and windage only. Bleeding it
      // off in a couple of seconds instead is a landing that brakes: a second of air came
      // down 30% slow, which is slip ratio -0.3, past the peak of the braking curve, and
      // the harness measured 1680 N of rear braking force nobody asked for on a 22 km/h
      // touchdown. That, repeated every roller, is why nothing carried momentum.
      w.spin = damp(w.spin, 0, Tuning.tire.freeSpinDecay, dt);
    }
  }

  private solveTire(
    dt: number,
    w: WheelState,
    isFront: boolean,
    input: ControlInput,
    g: GroundSample,
    gripMul: number,
    rollMul: number,
    fwdX: number, fwdZ: number,
    rgtX: number, rgtZ: number,
    res: WheelForces,
  ): void {
    const s = this.state;
    const B = Tuning.bike;
    const T = Tuning.tire;
    const C = Tuning.control;

    // Wheel's own heading: front wheel is steered.
    const steerA = isFront ? s.steer : 0;
    const wc = Math.cos(s.yaw + steerA);
    const ws = Math.sin(s.yaw + steerA);
    const wFwdX = wc, wFwdZ = ws;
    const wRgtX = -ws, wRgtZ = wc;

    // Contact-patch velocity: the velocity of that material point, v_CoM + omega x r,
    // with the *whole* omega.
    //
    // Yaw alone was in here, and leaving pitch and lean out is not a rounding error, it
    // is a missing damper. The patch sits a metre below the CoM, so a pitch rate of one
    // rad/s slides it a metre a second fore/aft — the bristle's stretch sees that motion
    // (it is measured from w.cx, which is resolved through the pitched arms) but the
    // bristle's *damper* did not, because the damper reads this velocity. A spring with no
    // damper is exactly what the harness found: a bike parked on a 15% slope ringing at
    // 6 Hz forever, both slip readouts pinned at 1.0, walking 400 mm downhill every three
    // seconds with both brakes locked.
    //
    // Signs: yaw runs +X toward +Z, which is a rotation about -Y, hence the minus on the
    // yaw pair. Pitch is about the body right axis (nose up positive) and lean about the
    // body forward axis (right positive); both act on the patch through the vertical arm,
    // which is negative because the patch is underneath.
    const rx = w.cx - s.x;
    const rz = w.cz - s.z;
    // ...at the hub, not at a chassis-fixed point on the ground. Slip is defined between
    // the tread and the ground, and the tread's velocity comes from the axle plus the
    // wheel's own spin — a point of the *frame* down at the tarmac is not a thing the
    // tire knows about. The difference is one wheel radius of arm, and it matters: it is
    // 35% of this term.
    const up = (this.armUp + B.wheelRadius) * B.patchRateCoupling;
    const wx = s.pitchRate * rgtX + s.leanRate * fwdX;
    const wz = s.pitchRate * rgtZ + s.leanRate * fwdZ;
    const vpx = s.vx - s.yawRate * rz - wz * up;
    const vpz = s.vz + s.yawRate * rx + wx * up;

    const vLong = vpx * wFwdX + vpz * wFwdZ;
    const vLat = vpx * wRgtX + vpz * wRgtZ;
    const speedAbs = Math.hypot(vLong, vLat);

    // --- Load sensitivity -------------------------------------------------------
    // A tire's friction coefficient falls as its vertical load rises: doubling the load
    // buys well under double the grip, because the carcass deflects and the contact
    // pressure distribution goes with it. Every real tire model has this and this one did
    // not, which made grip exactly proportional to load — and that is not a small
    // simplification when the load is what a landing spikes.
    //
    // What it cost, measured on holdline: a landing put 2.2 kN through each patch, five
    // times static. Cornering force is the tire curve times load, so the side force went
    // up fivefold too while the term that makes a leaned bike fall over only went up by
    // the same factor times sin(lean) — so the bike was stood violently up, straight
    // through vertical, and left the far side of it at 1.3 rad/s of roll with a +29 degree
    // lean command still held. With the exponent in, the same spike buys 23% less mu than
    // proportional and the kick is a fraction of the size.
    //
    // It also makes grip legible in the direction the bar asks for: unweighting a tire
    // gains you grip per newton, loading it up spends it, which is why a rider pumps
    // *into* a berm and floats over a flat corner. Clamped both ways so a nearly unloaded
    // wheel cannot become a magnet.
    const loadSens = clamp(
      Math.pow(Math.max(w.load, 1) / (B.mass * G * 0.5), -T.loadSensitivity),
      T.loadSensMin, T.loadSensMax,
    );
    const muLong = T.muLongPeak * gripMul * loadSens;
    const muLat = T.muLatPeak * gripMul * loadSens;
    const maxForce = w.load;

    const R = B.wheelRadius;
    const I = B.wheelInertia;

    // --- Stiction anchor at low speed ------------------------------------------
    // A velocity damper permits steady creep and lets a parked bike slide down a slope.
    // A saturating bristle that drags its anchor does not.
    //
    // Two things about it are easy to get wrong and both weld the bike to the mountain.
    // First, the anchor has to roll with the tire — a turning wheel is not a sliding
    // wheel — or the tire becomes a grappling hook and you cannot pedal away from a
    // standstill. Second, its longitudinal component has to reach the wheel-spin solve
    // as a torque, because at a dead stop the bristle is the *only* thing touching the
    // wheel, and without it the wheel never spins up and the bike never rolls away.
    let stLong = 0;
    let stLat = 0;
    let stBlend = 0;
    if (speedAbs < T.stictionSpeed && Math.abs(w.spin) < T.stictionSpin) {
      if (!w.anchored) {
        w.anchorX = w.cx;
        w.anchorZ = w.cz;
        w.anchored = true;
      }
      const rolled = w.spin * R * dt;
      w.anchorX += wFwdX * rolled;
      w.anchorZ += wFwdZ * rolled;

      let dx = w.cx - w.anchorX;
      let dz = w.cz - w.anchorZ;
      const stretch = Math.hypot(dx, dz);
      const maxStretch = (maxForce * T.stictionBreakaway * gripMul) / T.stictionStiffness;
      if (stretch > maxStretch && stretch > 1e-6) {
        // Drag the anchor rather than breaking free.
        const pull = (stretch - maxStretch) / stretch;
        w.anchorX += dx * pull;
        w.anchorZ += dz * pull;
        dx -= dx * pull;
        dz -= dz * pull;
      }
      // Damp the sliding velocity only. Damping the patch velocity itself would brake a
      // perfectly rolling wheel, which is the creep-permitting damper in disguise.
      const slideX = vpx - wFwdX * w.spin * R;
      const slideZ = vpz - wFwdZ * w.spin * R;
      const cap = maxForce * T.stictionBreakaway * gripMul;
      // The spring part is already inside the cap by construction — maxStretch is exactly
      // cap/stiffness and the anchor was just dragged back to it — so when the pair does
      // not fit, it is the damper that has to give, and only the damper.
      //
      // Scaling both down together is the version that was here, and it is a
      // velocity-weakening friction law wearing a disguise: the faster the patch moves,
      // the more the *restoring* force gets cut. Velocity-weakening friction is the
      // textbook recipe for a stick-slip limit cycle and that is precisely what it built —
      // a bike parked on a 50% pitch ringing at 6 Hz forever, the lightly loaded rear wheel
      // swinging between a third and nine tenths of its friction budget while the bike
      // itself did not move a millimetre.
      //
      // Solve |spring + t*damper| = cap for the largest t in [0,1]. One quadratic, exact,
      // no iteration.
      const spx = -dx * T.stictionStiffness;
      const spz = -dz * T.stictionStiffness;
      const dpx = -slideX * T.stictionDamping;
      const dpz = -slideZ * T.stictionDamping;
      let t = 1;
      if (Math.hypot(spx + dpx, spz + dpz) > cap) {
        const a = dpx * dpx + dpz * dpz;
        const b = 2 * (spx * dpx + spz * dpz);
        const c = spx * spx + spz * spz - cap * cap;
        const disc = b * b - 4 * a * c;
        t = a > 1e-9 && disc > 0 ? clamp01((-b + Math.sqrt(disc)) / (2 * a)) : 0;
      }
      let sfx = spx + dpx * t;
      let sfz = spz + dpz * t;
      // Belt and braces: if the spring alone somehow overran the cap (a load that
      // collapsed since the stretch was clamped), bring the whole thing back inside it.
      const mag = Math.hypot(sfx, sfz);
      if (mag > cap && mag > 1e-6) {
        sfx *= cap / mag;
        sfz *= cap / mag;
      }
      stLong = sfx * wFwdX + sfz * wFwdZ;
      stLat = sfx * wRgtX + sfz * wRgtZ;
      stBlend = 1 - clamp01(speedAbs / T.stictionSpeed);
    } else {
      w.anchored = false;
    }

    // --- Longitudinal: brake and drive integrated WITH the tire ---------------
    // Applying the brake after the tire force means the tire can never out-torque it,
    // the wheel locks instantly, and every stop happens at sliding friction.
    // Curved and lagged in updateBrakes — see there. What arrives here is the torque the
    // pads have actually taken up, which is what the wheel gets to argue with.
    const brakeTorque = Math.max(0, isFront ? this.brakeAppliedFront : this.brakeAppliedRear);
    const driveTorque = isFront
      ? 0
      : clamp01(input.pedal) * Math.min(C.pedalTorqueMax, C.pedalPowerMax / Math.max(1.5, Math.abs(w.spin)));

    // Implicit solve for wheel spin. The tire feedback gain is proportional to 1/speed,
    // which makes this stiff — explicit Euler exploded the wheel to 16,500 rad/s.
    const relaxSpeed = Math.max(Math.abs(vLong), T.longRelaxSpeed);
    // dFx/dspin, linearised about the current slip. The bristle's own d/dspin is
    // stiffness*R*dt (the anchor rolls with the wheel) and belongs in the same solve.
    const k = (maxForce * muLong / T.peakSlipRatio) * (R / relaxSpeed) * (1 - stBlend);
    // Both bristle terms differentiate w.r.t. spin (the anchor rolls with the wheel and
    // the damper sees wheel speed), so both belong in the implicit denominator. Leaving
    // the damper out lets a wheel take five rad/s in a single step when the bristle is
    // at its breakaway cap; the wheel then rings against its own contact patch, and a
    // bike standing still reads 1.5 g and refuses to roll away for three seconds.
    const kSt = (T.stictionStiffness * dt + T.stictionDamping) * R * stBlend;
    const slipRatio0 = (w.spin * R - vLong) / relaxSpeed;
    const slipLong0 = this.tireCurve(slipRatio0 / T.peakSlipRatio) * maxForce * muLong;
    const fLong0 = lerp(slipLong0, stLong, stBlend);
    const denom = I / dt + (k + kSt) * R;

    // A brake is a Coulomb friction pair, so it has a stick branch and a slip branch, and
    // it has to be solved as such. Applying -sign(spin) * brakeTorque unconditionally is
    // the slip branch alone, and a signed torque of 470 N*m that flips direction whenever
    // the wheel crosses zero is a square wave straight into the wheel at 240 Hz. Tapering
    // the sign across the last few cm/s did not fix it, it only lowered the gain: the
    // harness still read a bike parked on a 15% slope rocking fore/aft at 1 Hz with 550 N
    // of longitudinal tire force, both slip readouts pinned at 1.0, and 400 mm of creep
    // every three seconds — a bike that hums and walks downhill with both levers pulled.
    //
    // The stick branch is what a locked brake actually is: the pads hold the disc, the
    // wheel does not turn, and the pad reaction takes up whatever the tire and the cranks
    // hand it, up to its own capacity. Solve for the torque that would hold the wheel
    // exactly still this step; if the brake can supply it, it does, and the wheel is
    // stopped rather than driven back and forth across zero.
    const holdT = -w.spin * denom - driveTorque + fLong0 * R;
    let dOmega: number;
    if (brakeTorque > 0 && Math.abs(holdT) <= brakeTorque) {
      dOmega = -w.spin; // stuck: the brake takes the reaction
    } else {
      // Saturated: the pads slip, supplying all they have in the direction that arrests
      // the wheel. sign(holdT), not -sign(spin) — at a standstill the wheel has no sign,
      // but the torque needed to hold it does.
      const brakeT = brakeTorque > 0 ? sign(holdT) * brakeTorque : 0;
      dOmega = (driveTorque + brakeT - fLong0 * R) / denom;
    }
    w.spin = clamp(finiteOr(w.spin + dOmega, 0), -400, 400);

    const slipRatio = (w.spin * R - vLong) / relaxSpeed;
    w.slipRatio = slipRatio;
    let fLong = this.tireCurve(slipRatio / T.peakSlipRatio) * maxForce * muLong;

    // --- Lateral: slip angle plus camber thrust -------------------------------
    // Camber thrust is mandatory. Without it leaning produces no turn, nothing balances
    // gravity, and the bike always falls over. It is expressed as an equivalent slip
    // angle so it shares the one friction circle with braking.
    //
    // Its size is not free. Neutral — camber alone exactly holding a small lean — sits at
    // 2*peakSlipAngle/(pi*muLat), about 0.08 here. Above that a 10-degree lean generates
    // more side force than the lean needs, the bike snaps upright through vertical and
    // diverges; the harness reads it as an 88-degree lean and 170 g. Sit just under
    // neutral and the remaining force comes from the slip angle the turn itself builds,
    // which is what closes the loop and gives lean an equilibrium.
    const latRelax = Math.max(Math.abs(vLong), T.latRelaxSpeed);
    const slipAngle = Math.atan2(-vLat, latRelax);
    const camberEquiv = s.lean * T.camberThrust;
    const effAngle = slipAngle + camberEquiv;
    w.slipAngle = slipAngle;
    let fLat = this.tireCurve(effAngle / T.peakSlipAngle) * maxForce * muLat;

    // --- Friction ellipse -------------------------------------------------------
    // Normalised per-axis so braking genuinely eats cornering grip and the drop past the
    // limit is gradual: that gradient is the whole difference between a slide you can
    // catch and a binary stick-then-let-go.
    const nLong = fLong / Math.max(maxForce * muLong, 1e-3);
    const nLat = fLat / Math.max(maxForce * muLat, 1e-3);
    const usage = Math.hypot(nLong, nLat);
    if (usage > 1) {
      const over = clamp01((usage - 1) * T.slideFalloff);
      const scale = (1 / usage) * lerp(1, T.slideRatio, over);
      fLong *= scale;
      fLat *= scale;
    }
    // While the bristle is holding, usage has to be measured against the bristle's own
    // breakaway cap, not against muLat. Dividing by muLat reads 0.97 for a bike standing
    // perfectly still on its brakes, so the dust and the tire loop come on at a
    // standstill and the number stops meaning "how close is this to letting go".
    // Blended, not maxed, for the same reason the forces are. The slip model is still
    // being evaluated down here but its output is not what the tire is doing: a locked
    // wheel under a bike that is standing still reads slip ratio -0.5 and 0.75 usage
    // from a curve whose force was thrown away, so the dust and the tire loop came on
    // full at a standstill. What the number has to mean is "how close is this to letting
    // go", and while the bristle holds, that is the bristle's own margin.
    const stCap = Math.max(maxForce * T.stictionBreakaway * gripMul, 1e-3);
    const stUsage = Math.hypot(stLong, stLat) / stCap;
    // Published smoothed, because this number is read by dust, sound and the HUD, and a
    // per-step value is noisier than what it is supposed to describe. The bristle's cap
    // is proportional to load, so a wheel carrying 160 N on a steep pitch has a 184 N
    // budget and a millimetre of chassis ring swings the ratio between 0.3 and 0.86 —
    // the bike is standing perfectly still and the rear tire appears to be letting go and
    // regripping six times a second. A 40 ms constant is far shorter than the 200 ms a
    // real slide takes to build, so nothing about "how close is this to letting go" is
    // lost; only the part of the signal that was never about the tire.
    const raw = clamp01(finiteOr(lerp(usage, stUsage, stBlend), 0));
    w.slipUsage = damp(finiteOr(w.slipUsage, raw), raw, T.slipUsageSmoothing, dt);

    // Hand over from the slip model to the bristle as the patch stops moving.
    fLong = lerp(fLong, stLong, stBlend);
    fLat = lerp(fLat, stLat, stBlend);

    // Rolling resistance. Same taper as the brake and for the same reason: a Coulomb
    // force that flips sign at zero velocity is a stick-slip oscillator, not a loss.
    fLong -= clamp(vLong / 0.3, -1, 1) * maxForce * T.rollingResistance / Math.max(rollMul, 0.3);

    // Project tire forces into world space.
    const tfx = wFwdX * fLong + wRgtX * fLat;
    const tfz = wFwdZ * fLong + wRgtZ * fLat;
    res.tireFx = tfx;
    res.tireFz = tfz;
    res.fx += tfx;
    res.fz += tfz;
  }

  /**
   * Normalised combined tire curve: linear to the peak at 1.0, then falls to the
   * sliding plateau. Shape matters more than exact numbers — the falloff is what
   * makes a slide catchable instead of binary.
   */
  private tireCurve(normalisedSlip: number): number {
    const x = normalisedSlip;
    const a = Math.abs(x);
    if (a < 1) return Math.sin((Math.PI / 2) * x);
    const decay = Math.exp(-(a - 1) * 0.9);
    const plateau = Tuning.tire.slideRatio;
    return sign(x) * (plateau + (1 - plateau) * decay);
  }

  private applyAirControl(dt: number, input: ControlInput): void {
    const s = this.state;
    const C = Tuning.control;
    // In the air the rider moves the bike around themselves. Rates, not torques — and
    // that has to mean a rate *target*, not an angular acceleration. Adding acceleration
    // every step integrates over the hang time into whatever rotation the number happens
    // to produce: half a second of float at full stick was enough to arrive at 86 degrees
    // of lean and land on the bars. A rate target is bounded by construction and reads as
    // deliberate rather than floaty.
    const ws = clamp(input.weightShift, -1, 1);
    const rollIn = clamp(input.lean, -1, 1);
    // Neutral stick levels out gently, so a hands-off landing arrives flat-ish without
    // ever overriding a rotation the player asked for.
    // Pitch, like roll, tracks an *attitude*. Weight-back is what a player holds through
    // a hard stop, and if that is a rate command in the air then any hang time turns it
    // into a backflip: three seconds of float looped the bike through 80 degrees and put
    // it down on its tail. Manual and nose-dive attitudes still come out of it; a full
    // rotation is a trick, and tricks are not this file's job.
    //
    // The attitude it tracks is measured from the ground the bike is about to land on, not
    // from the world horizon. See `control.airGroundTrack` for the measurement; the short
    // version is that levelling to the horizon on a descent is a request to land nose-up
    // by the whole gradient, and a bike that lands nose-up bounces off the rear wheel
    // instead of settling — `corner` is airborne 8.8% of its run with this off against
    // 1.8% with it on, a bike skipping down the hill rather than riding it. `groundPitch` is
    // kept live through the flight from a ballistic preview of the landing point (see the
    // airborne branch in `step`), so this is the slope the rider is looking at rather than
    // the lip they left.
    const wantPitch = this.groundPitch * C.airGroundTrack - ws * C.airPitchRange;
    const pitchTarget = clamp((wantPitch - s.pitch) * C.airLeanTrack, -C.airPitchRate, C.airPitchRate);
    s.pitchRate = damp(s.pitchRate, pitchTarget, C.airControlRate, dt);
    // Roll tracks a lean *attitude*, not a roll rate. Holding the stick through a corner
    // that goes light is the single most common thing a player does, and with a rate
    // command that is a request to keep rolling — the bike leaves the ground at 39
    // degrees and lands at 86, on its side, from an input the player thought was "hold
    // this turn". Tracking the commanded lean means you land pointing where you asked.
    const wantLean = rollIn * Tuning.rider.maxLean;
    const rollTarget = clamp((wantLean - s.lean) * C.airLeanTrack, -C.airRollRate, C.airRollRate);
    s.leanRate = damp(s.leanRate, rollTarget, C.airControlRate, dt);
    // Ceiling on the roll rate the bike can carry through the air. Leaving the ground
    // mid-slide with 4 rad/s of roll on it integrates into another 40 degrees of lean
    // before the rider can reverse it, and that is the whole difference between landing
    // leaned over and landing on the bars.
    s.leanRate = clamp(s.leanRate, -C.airRollRate * 1.5, C.airRollRate * 1.5);
    s.yawRate = damp(s.yawRate, clamp(input.airYaw, -1, 1) * C.airYawRate, C.airControlRate, dt);
  }

  private updateHop(dt: number, input: ControlInput): void {
    const s = this.state;
    const C = Tuning.control;
    if (input.hop) {
      this.hopHeld = Math.min(this.hopHeld + dt, C.hopChargeTime);
      s.hopCharge = this.hopHeld / C.hopChargeTime;
      // The preload itself — the rider driving down through the legs and loading the
      // springs — is updatePump's job now, and it drives the body pose through the same
      // coordinate. Forcing a crouch here as well fought it: the button both compressed
      // the rider and extended them, and the visible result was neither.
    } else if (this.hopHeld > 0) {
      const charge = this.hopHeld / C.hopChargeTime;
      // The rider extends whether or not the wheels are still down — that extension is
      // what a pump is, and it is the same movement as the pop. Long enough to be a real
      // stroke, short enough that the legs are back under the rider for the landing.
      this.popTimer = 0.22;
      if (s.front.contact || s.rear.contact) {
        // How much of a hop a release is worth, against how long the rider loaded for.
        //
        // This was `0.45 + 0.55 * charge`, and that floor is why pumping did not work.
        // The button is deliberately one button — held is the rider driving down, released
        // is the extension, and which of pump, preload and bunny hop it turns out to be is
        // supposed to be decided by where on the terrain the player lets go. With a 45%
        // floor it was decided by the code instead: a 0.15 s pump stroke, the length of one
        // leg movement over a roller, released 236 N*s and put the bike 0.35 m in the air.
        // Every pump was a hop, so the harness measured the pump run *slower* than the
        // identical coasting run — 210 m against 218 — because the rider spent the descent
        // airborne, where there is nothing to pump against and only drag.
        //
        // Squared, so it grows from nothing: the same 0.15 s stroke is now worth 13% of a
        // hop, which is the small unweight a real pump has, while a deliberate 0.42 s
        // preload is still the full 3.6 m/s bunny hop. The mechanic is the same movement at
        // two timescales, which is what it is on a bike.
        const imp = C.hopImpulse * charge * charge;
        s.vy += imp / Tuning.bike.mass;
        // Arm the launch-loss window. Only a hop worth measuring: below half charge the
        // impulse is a pump's small unweight and the denominator is small enough that the
        // ratio is noise rather than a defect. See HOP_LOSS.
        if (charge >= 0.5) {
          this.hopVy0 = imp / Tuning.bike.mass;
          this.hopVyRef = s.vy;
          this.hopWatch = 0.1;
        }
        // Rear-biased pop unless the rider is forward.
        // Modest: the pop happens once, but the rotation it starts runs for the whole
        // flight. A rear-biased kick that looks right on the lip is 50 degrees of
        // backwards rotation by the time the bike lands.
        s.pitchRate += (0.6 - s.riderFore * 2) * 1.0 * charge;
        this.events.hopped = true;
        // The tuck — legs retracting under the rider as the bike comes up after them — used
        // to be injected here as 3.2 m/s straight onto the leg coordinate. That was free
        // when the coordinate carried no load. It is not now: 3.2 m/s against
        // `rider.legDamping` is 5 kN through the contact patches, pushing the bike down at
        // the exact instant a hop is trying to leave the ground. `updatePump`'s pop already
        // moves the datum the legs hold about by 144 mm toward the crouch, which produces
        // the same tuck through the same load path and with the right sign.
        //
        // The impulse itself is deliberately still applied to `s.vy` alone and the leg
        // coordinate deliberately left untouched: `bodySquashVel` is the *relative*
        // velocity, so leaving it alone is what gives the rider the same change in velocity
        // as the chassis. A bunny hop lifts both, which is what it did before the split and
        // has to keep doing.
      }
      this.hopHeld = 0;
      s.hopCharge = 0;
    }
  }

  /**
   * How hard the bike just hit something, and what that costs.
   *
   * Why this exists at all. Three rounds were spent shaping the spring curve so that
   * bottoming out would be "felt and rare", and the third one measured, exhaustively,
   * that it cannot be both: sweeping twenty-four points across support, rate, ramp,
   * rider stage, damping, blow-off, rebound and envelope, *every* change that bought
   * time off the stop paid for it in peak g and every change that bought peak g paid
   * for it in time on the stop. The curve sits on its own Pareto front and there is no
   * more juice in it. That is a one-dimensional trade, and the reason it was
   * one-dimensional is that a bottom-out had no consequence: the harness measured a
   * 20.5 m/s touchdown — the bike had ridden off a **23 metre cliff** — that buried the
   * rear axle 723 mm past full travel, read 13 g, and *rode away at 51 km/h*. Bottoming
   * was frequent and it was free, so the only currency left for making it rare was
   * spring force, which is the same currency as harshness.
   *
   * This is the second axis. Severity is a state with a case and a crash tier, the
   * chassis and the rider both answer to it, and with hitting the stop now costing the
   * player something the curve is allowed to stay soft.
   *
   * The measure is the stroke the wheels demanded past what the frame has, not the
   * closing speed and not g on its own. Three reasons, and the first is the one that
   * matters: a touchdown speed says nothing about what the ground did with it — 6 m/s
   * onto a landing that falls away is nothing and 6 m/s onto flat is a case, and
   * `overTravel` is exactly the difference. Second, it is already the quantity the rest
   * of the file uses for "past the end of the suspension", so severity and the rider
   * stage cannot disagree about when the bike ran out. Third, its scale is physical
   * rather than tuned: 0 is the frame's own stroke ending, `stopTravel` is the rider's
   * legs fully collapsed and therefore the chassis arriving at the hillside, and the
   * span between them is the whole of what a rider can absorb. g is kept as a second
   * input purely as a backstop for the hit that makes enormous force with no stroke at
   * all — a cut bank, a wall — which the stroke measure alone would score as nothing.
   */
  private updateImpact(dt: number): void {
    const s = this.state;
    const I = Tuning.impact;
    const S = Tuning.suspension;
    const grounded = s.front.contact || s.rear.contact;
    const ov = Math.max(s.front.overTravel, s.rear.overTravel);

    // The g branch is an *impulse* above the threshold, not a peak, and it only
    // accumulates while a wheel is genuinely at the end of its stroke.
    //
    // Both halves of that are a measurement. Read as a peak it fired everywhere: at 240 Hz
    // ordinary riding contains single-step excursions past 9 g that the 30 Hz telemetry
    // never shows — a wheel dropping into a cut at 8 km/h is enough — and every one of them
    // scored a case. The A/B is unambiguous: with a peak-g branch the `corner` run cased at
    // 1.6 s and never got past 12 km/h against 44 in the same tree with the branch off, and
    // `lowspeed` topped out at 8.9 km/h against 18.3. An impulse is the honest quantity
    // anyway — it is the velocity the hit actually took out of the rider, so a 4 ms spike is
    // worth nothing and a 40 ms wall strike is worth everything, which is the distinction
    // the peak could not make. And gating it on deep travel is what keeps it a *backstop*:
    // its entire job is the hit that makes enormous force through no stroke at all, and the
    // hillside term that produces those only exists past full travel in the first place.
    // `bellyLoad > 0` is the third way to be out of suspension, and until this round it was
    // the one the model could not see at all. The two wheel clauses ask "is a wheel at the
    // end of its stroke"; the frame resting on the hillside is the case where that question
    // returns false for both — a chassis on the dirt with the wheels hanging in space is
    // exactly the state the old positional clamp produced and hid. It gates the g branch
    // only, same as the others, so it costs nothing unless the bike is genuinely making
    // large acceleration while the frame is down.
    const deep = ov > 0
      || this.bellyLoad > 0
      || s.front.travel >= S.frontTravel * I.deepFraction
      || s.rear.travel >= S.rearTravel * I.deepFraction;
    const over = deep ? Math.max(0, s.gForce - I.caseG) : 0;

    // One hit is a window, not a step. The stroke keeps growing for 20-40 ms after
    // touchdown and the peak of g arrives inside that, so a severity read on the first
    // grounded step reads the leading edge of the hit and scores a case as nothing. The
    // window is held open for as long as the bike is still into the stroke and then runs
    // out, which also merges the two or three separate wheel events one ugly landing
    // produces into the single case a rider would call it.
    const inHit = grounded && (ov > I.caseOverTravel * 0.5 || over > 0);
    if (inHit) {
      this.impHold = I.holdTime;
      if (ov > this.impPeakOv) this.impPeakOv = ov;
      this.impGImpulse += over * dt;
    } else if (this.impHold > 0) {
      this.impHold -= dt;
    }

    let sev = 0;
    if (this.impHold > 0) {
      const span = Math.max(S.stopTravel - I.caseOverTravel, 1e-3);
      const sOv = clamp01((this.impPeakOv - I.caseOverTravel) / span);
      const sG = clamp01(this.impGImpulse / Math.max(I.crashGImpulse, 1e-4));
      sev = Math.max(sOv, sG);
    } else {
      this.impPeakOv = 0;
      this.impGImpulse = 0;
      this.impSev = 0;
    }
    sev = finiteOr(sev, 0);
    s.impact = sev;
    this.events.cased = sev;

    // Everything below is driven by the *rise* in severity rather than by its level, so
    // the total each consequence delivers over one hit is set by how bad that hit turned
    // out to be and not by how many steps it took to get there. A hit that develops over
    // 30 ms and one that develops over 120 ms cost the same.
    const dSev = Math.max(0, sev - this.impSev);
    if (dSev > 0) {
      this.impSev = sev;
      // The rider is thrown at the bars. Fades out through updateRider, which is where
      // the two ordinary halves of the rider's fore/aft position already live.
      this.caseFore += I.caseThrow * dSev;
      // Legs driven through their stroke — this is the visible half, and the rider model
      // reads it. Positive is compression.
      s.bodySquashVel = clamp(s.bodySquashVel + I.caseBodySlam * dSev, -8, 8);
      // And balance goes with it, to whichever side the bike was already on. A case that
      // only cost speed would be a number; what makes one frightening is that you come
      // out of it fighting for the front wheel.
      s.leanRate = clamp(s.leanRate + (sign(s.lean) || 1) * I.caseBuckRoll * dSev, -12, 12);
      s.caseShock = Math.min(1, s.caseShock + dSev);
    }

    // Speed. A case is the rider's legs, the tires and the frame all absorbing an impact
    // the suspension could not, and none of that energy comes back as forward motion —
    // casing a jump on a real bike stops you almost dead. Applied over the window rather
    // than as an impulse so it reads as the bike bogging down into the hit.
    if (sev > 0 && grounded) {
      const k = Math.exp(-I.caseScrub * sev * dt);
      s.vx *= k;
      s.vz *= k;
    }

    s.caseShock = clamp01(damp(s.caseShock, 0, 1 / Math.max(I.caseRecover, 1e-3), dt));
    this.caseFore = damp(this.caseFore, 0, 1 / Math.max(I.caseRecover, 1e-3), dt);
    if (this.remountGrace > 0) this.remountGrace -= dt;
  }

  private updateCrash(dt: number): void {
    const s = this.state;
    const I = Tuning.impact;
    if (s.crashed) {
      s.crashTimer += dt;
      // Down means down. Until this round `crashed` was a flag that changed no dynamics
      // at all, so the 23 m huck above kept its 51 km/h and the run continued exactly as
      // if nothing had happened — which is most of why bottoming was free. A crashed bike
      // and rider slide and stop.
      const grounded = s.front.contact || s.rear.contact;
      if (grounded) {
        const k = Math.exp(-I.crashDrag * dt);
        s.vx *= k;
        s.vz *= k;
      }
      if (s.crashTimer > I.crashRecoverTime) {
        // Remount. Position is kept — the rider picks the bike up where it went down —
        // but the attitude is not, or the first step back would read as another crash.
        //
        // This whole branch is what a "get up and continue" is, and it already existed:
        // `crashed` is NOT a latch that only `resetPressed` clears, and reset-to-world-
        // spawn is not the only way out of a fall. What it did not do was leave the bike
        // in a state a rider could actually ride out of, and the four additions below are
        // that — measured, each of them, as a way a run ends when it should not.
        s.crashed = false;
        s.crashTimer = 0;
        s.lean = 0; s.leanRate = 0;
        s.pitch = clamp(this.groundPitch, -0.6, 0.6); s.pitchRate = 0;
        s.leanCommand = 0;
        s.caseShock = 0; s.impact = 0;
        this.impPeakOv = 0; this.impGImpulse = 0; this.impHold = 0; this.impSev = 0;
        this.overAngle = 0;
        this.remountGrace = I.remountGrace;

        // (a) Pick the bike up out of the hill. `overTravel` past the bump stop means the
        // axle is inside the hillside, and a crash that ends by burying a wheel leaves it
        // buried — the springs cannot lift a chassis that is below where its own wheels
        // are. Raising the CoM by exactly the worst excursion puts the wheel back at full
        // travel and never above it, so this can only undo a burial, never launch anything.
        const buried = Math.max(s.front.overTravel, s.rear.overTravel);
        if (buried > 0) s.y += buried * Math.cos(s.pitch);

        // (b) Point it back down the hill. A rider who falls does not get back on facing
        // up the slope, and a bike that does has to make a standing-start U-turn on a
        // steep face — which is exactly the shape of the multi-second stalls the autopilot
        // logs. This does NOT snap to the fall line: that would push a rider who fell on a
        // traverse straight off the side of the trail. It only clamps the heading into the
        // downhill half-plane, and only when the bike is actually facing uphill, turning
        // it the short way to 15 degrees below the contour on whichever side it was
        // already on. A heading that already has any downhill component is left alone.
        const d = this.descentDir;
        this.terrain.descent(s.x, s.z, d);
        const dl = Math.hypot(d.x, d.z);
        if (dl > 1e-4) {
          const dx = d.x / dl, dz = d.z / dl;          // unit, points downhill
          const fx = Math.cos(s.yaw), fz = Math.sin(s.yaw);
          const along = fx * dx + fz * dz;             // +1 straight down the fall line
          const TARGET = 0.26;                          // sin(15 deg) below the contour
          if (along < TARGET) {
            // Sideways component decides which way the rider was already pointing; the
            // sign is kept so a traverse stays a traverse.
            const side = fx * -dz + fz * dx;
            const sgn = side >= 0 ? 1 : -1;
            const cross = Math.sqrt(Math.max(0, 1 - TARGET * TARGET)) * sgn;
            s.yaw = Math.atan2(TARGET * dz + cross * dx, TARGET * dx - cross * dz);
          }
        }
        s.yawRate = 0;

        // (c) Stop the wheels and drop the bristle anchors. The tires spent the fall
        // sliding; leaving spin and a stretched anchor behind means the first step back on
        // the bike is a full-circle slip event, which is a puff of dust, a squeal, and a
        // yaw kick at the exact moment the rider has no speed to steer with.
        s.front.spin = 0; s.rear.spin = 0;
        s.front.anchored = false; s.rear.anchored = false;
        s.front.slipUsage = 0; s.rear.slipUsage = 0;
        s.front.slipRatio = 0; s.rear.slipRatio = 0;
        s.front.slipAngle = 0; s.rear.slipAngle = 0;

        // (d) The bike has been teleported (a and b), so there is no previous ground to
        // difference the shaft speed against. Same reasoning as the end of reset().
        this.prevGndValid = false;
      }
      return;
    }
    const grounded = s.front.contact || s.rear.contact;
    const overLean = Math.abs(s.lean) > Tuning.bike.crashLean && grounded;
    // Relative to the ground, for the same reason the endo save is: a 50 degree chute
    // is not an endo.
    const overPitch = Math.abs(s.pitch - this.groundPitch) > Tuning.bike.crashPitch && grounded;
    // A crash is being on the floor, not touching an angle for one frame. On real
    // terrain the bike flicks past 72 degrees off a camber and is already coming back
    // three frames later — calling that a crash throws away saves the rider actually
    // made, and reads as the game deciding for you.
    if (overLean || overPitch) this.overAngle += dt;
    else this.overAngle = 0;
    // The impact path. Full severity means the wheels asked for the rider's entire leg
    // stroke and kept going, i.e. the chassis is where the hillside is — there is no
    // attitude at which that is ridden out, and the angle test above sees none of it
    // because the bike arrives flat and leaves flat. No hold time either: unlike an
    // angle, which flickers, this one is a measured stroke that has already happened.
    //
    // The grace window now covers the angle branch as well as the impact branch, and it
    // has to: a remount on a steep face puts the bike back on a slope where the balance
    // assist needs a beat to gather it up, and until it does, `lean` is still swinging
    // through the crash angle from the fall it just had. Without the guard the second
    // crash fires within a frame or two of the first ending and the rider never gets up —
    // "get up and continue" is not a recovery if it re-enters the fall immediately. The
    // window is short enough (`remountGrace` 0.5 s against a 0.22 s hold) that a bike that
    // really is still down re-crashes a fifth of a second after it expires.
    if ((this.overAngle > Tuning.bike.crashHoldTime
      || s.impact >= I.crashSeverity) && this.remountGrace <= 0) {
      s.crashed = true;
      s.crashTimer = 0;
      this.events.crashed = true;
    }
  }
}
